<template>
  <div
    class="relative w-full overflow-x-hidden flex flex-col font-sans text-zinc-900 dark:text-zinc-50 bg-zinc-50 dark:bg-zinc-950"
    :style="{ minHeight: appHeight }">
    <div class="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div class="absolute -top-40 left-[-10%] h-72 w-72 rounded-full bg-sky-400/20 blur-3xl dark:bg-sky-500/25"></div>
      <div class="absolute -bottom-52 right-[-15%] h-96 w-96 rounded-full bg-cyan-400/20 blur-3xl dark:bg-sky-600/25">
      </div>
      <div
        class="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[1px] bg-gradient-to-b from-sky-500/0 via-sky-500/20 to-sky-500/0">
      </div>
    </div>
    <header
      class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4 px-4 py-3 rounded-2xl bg-white/70 dark:bg-zinc-950/70 border border-zinc-200 dark:border-zinc-800 shadow-[0_8px_30px_rgba(0,0,0,0.06)] backdrop-blur-xl">
      <div class="flex items-start gap-3">
        <div>
          <p class="text-[11px] uppercase tracking-[0.20em] text-zinc-500">
            Palette Generator
          </p>
          <h1 class="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight">
            Sky Studio • Colors
          </h1>
          <p class="mt-1 text-[13px] text-zinc-500 dark:text-zinc-400 max-w-2xl">
            Craft, organize and export palettes ready for your design system.
          </p>
        </div>
      </div>

      <div class="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">

        <router-link to="/dashboard/hub"
          class="hidden sm:flex group items-center justify-center h-10 w-10 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white/50 dark:bg-zinc-900/50 text-zinc-400 hover:text-sky-500 hover:border-sky-500/30 hover:shadow-lg hover:shadow-sky-500/10 transition-all duration-300 hover:-translate-y-0.5 active:scale-95"
          title="Tutorial Hub">
          <span
            class="material-symbols-outlined text-[20px] group-hover:rotate-12 transition-transform duration-300">school</span>
        </router-link>

        <div class="flex flex-col items-start sm:items-end gap-2">
          <span class="text-[11px] text-zinc-400 dark:text-zinc-500">
            Palette • {{ activePalette.colors.length }} colors
          </span>
          <span
            class="inline-flex items-center gap-1 rounded-full bg-zinc-100 dark:bg-zinc-900/80 border border-zinc-200 dark:border-zinc-800 px-3 py-1 text-[11px] text-zinc-500 dark:text-zinc-400">
            Sky Studio • Generator
          </span>
        </div>
      </div>
    </header>

    <div
      class="flex flex-wrap items-center gap-1.5 sm:gap-2 px-3 py-2 rounded-2xl mb-6 bg-white/80 dark:bg-zinc-950/80 border border-zinc-200 dark:border-zinc-800 backdrop-blur-xl shadow-[0_6px_24px_rgba(0,0,0,0.06)]">
      <button class="btn-ghost gap-1.5 px-3 py-1.5 text-xs sm:text-[13px]"
        @click="showGeneratorSettings = !showGeneratorSettings">
        <span class="material-symbols-outlined text-[18px]">tune</span>
        <span class="hidden md:inline">Adjust</span>
      </button>
      <button class="btn-ghost gap-1.5 px-3 py-1.5 text-xs sm:text-[13px]" @click="openImport = true">
        <span class="material-symbols-outlined text-[18px]">upload_file</span>
        <span class="hidden md:inline">Import</span>
      </button>
      <button class="btn-ghost gap-1.5 px-3 py-1.5 text-xs sm:text-[13px]" @click="openExport = true"
        :disabled="!activePalette.colors.length">
        <span class="material-symbols-outlined text-[18px]">download</span>
        <span class="hidden md:inline">Export</span>
      </button>
      <button class="btn-ghost gap-1.5 px-3 py-1.5 text-xs sm:text-[13px]" @click="viewMode = 'explore'">
        <span class="material-symbols-outlined text-[18px]">grid_view</span>
        <span class="hidden md:inline">Explore</span>
      </button>
      <div
        class="hidden sm:inline-flex h-7 w-px mx-1 bg-gradient-to-b from-transparent via-zinc-300/70 to-transparent dark:via-zinc-700/80">
      </div>
      <button class="btn-primary hidden sm:inline-flex gap-1.5 px-3 py-1.5 text-xs sm:text-[13px]"
        @click="randomizeActive()">
        <span class="material-symbols-outlined text-[18px]">auto_awesome</span>
        <span>Generate</span>
      </button>
      <button class="btn-primary inline-flex sm:hidden gap-1.5 px-3 py-1.5 text-xs" @click="openSave = true"
        :disabled="!activePalette.colors.length">
        <span class="material-symbols-outlined text-[18px]">save</span>
      </button>
      <button class="btn-primary hidden sm:inline-flex gap-1.5 px-3.5 py-1.5 text-xs sm:text-[13px]"
        @click="openSave = true" :disabled="!activePalette.colors.length">
        <span class="material-symbols-outlined text-[18px]">save</span>
        <span>Save palette</span>
      </button>
    </div>

    <main v-if="viewMode === 'generator'"
      class="flex-1 flex flex-col md:flex-row overflow-hidden px-3 sm:px-4 pt-3 pb-2 gap-3">
      <div
        class="flex-1 flex flex-col rounded-3xl border border-zinc-200/80 dark:border-zinc-800/80 bg-white/80 dark:bg-zinc-950/70 backdrop-blur-xl shadow-[0_18px_55px_rgba(15,23,42,0.16)] overflow-hidden">
        <div
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-zinc-200/70 dark:border-zinc-800/80">
          <div class="flex items-center gap-2">
            <span
              class="inline-flex h-6 w-6 items-center justify-center rounded-xl bg-sky-500/15 text-sky-600 dark:text-sky-300 text-sm">
              #
            </span>
            <div>
              <p class="text-xs font-medium uppercase tracking-[0.18em] text-zinc-500">
                Active palette
              </p>
              <p class="text-[13px] text-zinc-500 dark:text-zinc-400">
                Click hex to copy • spacebar generates new
              </p>
            </div>
          </div>
          <button class="btn-ghost hidden sm:inline-flex" type="button" @click="randomizeActive()">
            <span class="material-symbols-outlined text-base">shuffle</span>
            New palette
          </button>
        </div>

        <div class="flex-1 min-h-0 flex flex-col">
          <div class="flex-1 min-h-0 px-1 sm:px-2 py-2 overflow-y-auto">
            <transition-group name="list-colors" tag="div" class="flex flex-col gap-3">
              <div v-for="(c, i) in activePalette.colors" :key="c.id" :style="{ background: c.hex }"
                class="group relative rounded-2xl h-[86px] sm:h-[95px] w-full flex items-center justify-between px-5 shadow-sm border border-black/5 dark:border-white/5 transition-all duration-200 hover:shadow-md hover:-translate-y-[2px]">
                <div class="flex items-center gap-4">
                  <div
                    class="h-10 w-10 sm:h-11 sm:w-11 rounded-xl flex items-center justify-center text-xs sm:text-[13px] font-semibold tracking-wide"
                    :class="bestTextOn(c.hex) === '#000'
                      ? 'bg-white/80 text-zinc-900 border border-zinc-300'
                      : 'bg-black/30 text-white border border-white/20'
                      ">
                    {{ (i + 1).toString().padStart(2, '0') }}
                  </div>
                  <div class="flex flex-col">
                    <button class="text-xl sm:text-2xl font-mono font-semibold tracking-wider text-left"
                      :class="bestTextOn(c.hex) === '#000' ? 'text-zinc-900' : 'text-white'" @click="copy(c.hex)">
                      {{ c.hex.toUpperCase() }}
                    </button>
                    <span
                      class="hidden sm:inline text-[11px] sm:text-[12px] uppercase font-medium tracking-[0.18em] mt-0.5"
                      :class="bestTextOn(c.hex) === '#000'
                        ? 'text-zinc-700/80'
                        : 'text-zinc-100/85'">
                      Swatch {{ i + 1 }}
                    </span>
                  </div>
                </div>
                <div
                  class="hidden sm:flex opacity-0 group-hover:opacity-100 transition-opacity duration-200 items-center gap-2">
                  <button class="action-btn" :class="getBtnColor(c.hex)" @click="c.locked = !c.locked"
                    :title="c.locked ? 'Unlock' : 'Lock'">
                    <span class="material-symbols-outlined text-[20px]">
                      {{ c.locked ? 'lock' : 'lock_open' }}
                    </span>
                  </button>
                  <button class="action-btn" :class="getBtnColor(c.hex)" @click="editingColorIndex = i"
                    title="Adjust color">
                    <span class="material-symbols-outlined text-[20px]">tune</span>
                  </button>
                  <button class="action-btn" :class="getBtnColor(c.hex)" @click="copy(c.hex)" title="Copy hex">
                    <span class="material-symbols-outlined text-[20px]">content_copy</span>
                  </button>
                  <button class="action-btn" :class="getBtnColor(c.hex)" @click="removeColorFromActive(i)"
                    :disabled="activePalette.colors.length <= 3" title="Remove color">
                    <span class="material-symbols-outlined text-[20px]">delete</span>
                  </button>
                </div>
              </div>
            </transition-group>
          </div>
          <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-2 border-t border-zinc-200/70 dark:border-zinc-800/80 text-[11px] text-zinc-500 dark:text-zinc-400">
            <div class="flex items-center gap-2">
              <span
                class="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-300 text-xs">
                ␣
              </span>
              <span>Press spacebar to generate a new combination.</span>
            </div>
            <div class="hidden sm:flex items-center gap-1">
              <span class="material-symbols-outlined text-xs text-zinc-400">info</span>
              <span>Click hex to copy • Lock icon to freeze</span>
            </div>
          </div>
        </div>
      </div>
    </main>

    <div v-if="viewMode === 'explore'" class="flex-1 overflow-y-auto px-3 sm:px-4 pt-3 pb-3">
      <div
        class="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-zinc-200/80 dark:border-zinc-800/80 bg-white/80 dark:bg-zinc-950/70 backdrop-blur-xl px-4 py-3 shadow-[0_14px_40px_rgba(15,23,42,0.18)]">
        <div class="flex items-start gap-3">
          <button type="button" @click="viewMode = 'generator'"
            class="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-zinc-100 dark:bg-zinc-900/80 border border-zinc-200 dark:border-zinc-800 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-sky-500/70 transition">
            <span class="material-symbols-rounded text-[18px]">chevron_left</span>
          </button>
          <div>
            <p class="text-[11px] font-semibold uppercase tracking-[0.20em] text-zinc-500">
              Library
            </p>
            <h3 class="text-sm sm:text-base font-medium">
              Explore Palettes
              <span class="text-xs text-zinc-500">({{ batch.length }})</span>
            </h3>
          </div>
        </div>
      </div>

      <transition-group name="list-palettes" tag="div"
        class="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <div v-for="p in batch" :key="p.id"
          class="rounded-2xl overflow-hidden border border-zinc-200/80 dark:border-zinc-800/80 bg-white/90 dark:bg-zinc-950/80 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.25)] transition-all duration-300 hover:shadow-[0_20px_55px_rgba(15,23,42,0.35)] hover:-translate-y-0.5">
          <div class="flex flex-col w-full">
            <div v-for="c in p.colors" :key="c.id" class="h-10 sm:h-12 w-full transition-colors duration-300"
              :style="{ background: c.hex }"></div>
          </div>
          <div class="p-3 flex flex-col gap-2 border-t border-zinc-200/60 dark:border-zinc-800/70">
            <div class="text-[11px] text-zinc-600 dark:text-zinc-400 leading-relaxed">
              <span v-if="p.name" class="font-medium">{{ p.name }} • </span>
              {{p.colors.map(x => x.hex.toUpperCase()).join(' · ')}}
            </div>
            <div class="flex items-center justify-between gap-2">
              <button class="chip flex items-center gap-1.5" @click="setActive(p); viewMode = 'generator'">
                <span class="material-symbols-outlined text-sm text-sky-600 dark:text-sky-300">
                  check_circle
                </span>
                <span>Activate</span>
              </button>
              <button class="chip flex items-center gap-1.5" @click="copyHexList(p.colors)">
                <span class="material-symbols-outlined text-sm">content_copy</span>
                <span>HEX</span>
              </button>
            </div>
          </div>
        </div>
      </transition-group>
    </div>

    <footer
      class="px-3 sm:px-4 py-2.5 text-[11px] sm:text-xs text-zinc-500 dark:text-zinc-400 border-t border-zinc-200/80 dark:border-zinc-800/80 bg-white/70 dark:bg-zinc-950/80 backdrop-blur-xl flex items-center justify-between gap-2">
      <span class="hidden sm:inline">
        Press
        <kbd
          class="px-2 py-1 text-[10px] font-semibold text-zinc-800 bg-zinc-100 border border-zinc-300 rounded-lg dark:text-zinc-100 dark:bg-zinc-900 dark:border-zinc-700">Space</kbd>
        to generate new palettes. Click any hex to copy.
      </span>
      <span class="sm:hidden">
        Tap any empty area to generate a new palette. Tap a hex to copy.
      </span>
      <span class="hidden md:inline text-[10px] uppercase tracking-[0.18em] text-zinc-400">
        Palette Genius • Sky UI
      </span>
    </footer>

    <transition name="slide-fade">
      <aside v-if="showGeneratorSettings"
        class="absolute top-20 right-3 sm:right-4 w-[calc(100%-1.5rem)] max-w-sm bg-white/95 dark:bg-zinc-950/95 text-zinc-900 dark:text-zinc-50 backdrop-blur-2xl rounded-3xl border border-sky-500/30 dark:border-sky-500/40 shadow-[0_18px_50px_rgba(8,47,73,0.25)] dark:shadow-[0_18px_50px_rgba(8,47,73,0.75)] p-4 space-y-4 z-20">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="font-semibold text-sm flex items-center gap-2">
              <span
                class="inline-flex h-6 w-6 items-center justify-center rounded-xl bg-sky-500/10 text-sky-600 dark:text-sky-200 text-[15px]">
                <span class="material-symbols-outlined">tune</span>
              </span>
              Generator Settings
            </h3>
            <p class="text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5">
              Adjust the logic used to create palettes.
            </p>
          </div>
          <button
            class="btn-ghost btn-icon !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/80 !text-zinc-700 dark:!text-zinc-200 hover:!bg-zinc-200 dark:hover:!bg-zinc-800"
            @click="showGeneratorSettings = false">
            <span class="material-symbols-outlined text-sm">close</span>
          </button>
        </div>

        <div class="space-y-3 text-[13px]">
          <div class="flex items-center gap-3">
            <label class="w-20 text-xs text-zinc-500 dark:text-zinc-400 shrink-0">Harmony</label>
            <select v-model="mode"
              class="select w-full text-xs bg-white/80 dark:bg-zinc-900/80 border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-100">
              <option value="random">Random</option>
              <option value="complementary">Complementary</option>
              <option value="analogous">Analogous</option>
              <option value="triad">Triad</option>
              <option value="tetrad">Tetrad</option>
              <option value="monochrome">Monochrome</option>
            </select>
          </div>

          <div class="flex items-center gap-3">
            <label class="w-20 text-xs text-zinc-500 dark:text-zinc-400 shrink-0">Colors</label>
            <input type="range" min="3" max="8" v-model.number="colorCount" class="range" />
            <span class="w-6 text-right text-[11px] tabular-nums text-zinc-700 dark:text-zinc-200">{{ colorCount
              }}</span>
          </div>
        </div>

        <button @click="randomizeActive(); showGeneratorSettings = false;"
          class="w-full flex items-center justify-center gap-2 py-2 text-[13px] font-medium text-sky-600 dark:text-sky-300 hover:text-sky-700 dark:hover:text-white transition-colors">
          <span class="material-symbols-rounded text-sm">shuffle</span>
          Generate now
        </button>
      </aside>
    </transition>

    <transition name="modal">
      <div v-if="editingColorIndex !== null"
        class="fixed inset-0 z-50 grid place-items-center p-4 bg-black/50 backdrop-blur-sm"
        @click.self="editingColorIndex = null">
        <div v-if="activePalette.colors[editingColorIndex]"
          class="w-full max-w-sm rounded-3xl border border-sky-500/30 dark:border-sky-500/25 bg-white/95 dark:bg-zinc-950/95 shadow-[0_18px_55px_rgba(8,47,73,0.25)] dark:shadow-[0_18px_55px_rgba(8,47,73,0.8)] p-6 space-y-4">
          <div class="flex items-center justify-between">
            <h3 class="font-semibold text-sm flex items-center gap-2">
              <span
                class="inline-flex h-7 w-7 items-center justify-center rounded-xl bg-sky-500/10 text-sky-600 dark:bg-sky-500/20 dark:text-sky-200 text-[16px]">
                tune
              </span>
              Adjust Color
            </h3>
            <button
              class="btn-ghost btn-icon !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/80 !text-zinc-700 dark:!text-zinc-200 hover:!bg-zinc-200 dark:hover:!bg-zinc-800"
              @click="editingColorIndex = null">
              <span class="material-symbols-outlined text-sm">close</span>
            </button>
          </div>

          <div class="h-24 rounded-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden"
            :style="{ background: activePalette.colors[editingColorIndex].hex }"></div>

          <input v-model="activePalette.colors[editingColorIndex].hex" @change="normalizeActive(editingColorIndex)"
            class="input text-center font-mono text-sm bg-white/80 border-zinc-300 text-zinc-800 dark:bg-zinc-900/80 dark:border-zinc-700 dark:text-zinc-100" />

          <div class="grid grid-cols-3 gap-2 text-[11px]">
            <div class="flex items-center gap-2">
              <span class="tiny text-zinc-500 dark:text-zinc-400">H</span>
              <input type="range" min="0" max="360" v-model.number="activePalette.colors[editingColorIndex].h"
                @input="applyHsl(editingColorIndex)" class="range" />
            </div>
            <div class="flex items-center gap-2">
              <span class="tiny text-zinc-500 dark:text-zinc-400">S</span>
              <input type="range" min="0" max="100" v-model.number="activePalette.colors[editingColorIndex].s"
                @input="applyHsl(editingColorIndex)" class="range" />
            </div>
            <div class="flex items-center gap-2">
              <span class="tiny text-zinc-500 dark:text-zinc-400">L</span>
              <input type="range" min="0" max="100" v-model.number="activePalette.colors[editingColorIndex].l"
                @input="applyHsl(editingColorIndex)" class="range" />
            </div>
          </div>

          <button
            class="btn-ghost w-full justify-center border-zinc-300 bg-zinc-100 text-zinc-800 hover:bg-zinc-200 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
            @click="editingColorIndex = null">
            Done
          </button>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="openImport" class="fixed inset-0 z-50 grid place-items-center p-4 bg-black/50 backdrop-blur-sm">
        <div
          class="w-full max-w-3xl rounded-3xl border border-sky-500/30 dark:border-sky-500/25 bg-white/95 dark:bg-zinc-950/95 shadow-[0_18px_55px_rgba(8,47,73,0.3)] dark:shadow-[0_18px_55px_rgba(8,47,73,0.85)] overflow-hidden">
          <div
            class="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between bg-gradient-to-r from-sky-500/15 via-sky-500/10 to-sky-400/10 dark:from-sky-600/40 dark:via-sky-500/30 dark:to-sky-400/30">
            <h3 class="text-sm font-semibold text-zinc-900 dark:text-zinc-50 flex items-center gap-2">
              <span class="material-symbols-outlined text-base">cloud_upload</span>
              Import palettes
            </h3>
            <button
              class="btn-ghost flex items-center gap-2 !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/60 !text-zinc-800 dark:!text-zinc-100 hover:!bg-zinc-200 dark:hover:!bg-zinc-800"
              @click="openImport = false">
              <span class="material-symbols-outlined text-base">close</span>
              <span class="hidden sm:inline text-xs">Close</span>
            </button>
          </div>
          <div class="p-4 grid gap-4 md:grid-cols-12">
            <aside class="md:col-span-4 space-y-2 text-xs text-zinc-700 dark:text-zinc-300">
              <select v-model="importKind"
                class="select w-full text-xs bg-white/80 dark:bg-zinc-900/80 border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-100">
                <option value="hex">HEX list</option>
                <option value="json">JSON</option>
                <option value="coolors">Coolors URL</option>
              </select>
              <div class="text-[11px] text-zinc-500 dark:text-zinc-400">
                HEX separated by space/line; JSON = [[ "#aa..", "#bb.." ], ...]
              </div>
            </aside>
            <section class="md:col-span-8">
              <textarea v-model="importText"
                class="code bg-white text-zinc-800 border border-zinc-300 dark:bg-zinc-950 dark:text-zinc-100 dark:border-zinc-800"
                placeholder="#000 #fff #73ab84 ... or JSON"></textarea>
            </section>
          </div>
          <div
            class="p-4 border-t border-zinc-200 dark:border-zinc-800 flex items-center justify-between text-[11px] text-zinc-600 dark:text-zinc-400">
            <div>Current palettes: {{ batch.length }}</div>
            <div class="flex items-center gap-2">
              <button
                class="btn-ghost flex items-center gap-2 !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/60 !text-zinc-800 dark:!text-zinc-100 hover:!bg-zinc-200 dark:hover:!bg-zinc-800"
                @click="previewImport()">
                <span class="material-symbols-outlined text-base">visibility</span>
                <span class="hidden sm:inline text-xs">Preview</span>
              </button>
              <button class="btn-primary flex items-center gap-2" @click="applyImport()">
                <span class="material-symbols-outlined text-base">playlist_add</span>
                <span class="text-xs">Add to batch</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="openExport" class="fixed inset-0 z-50 grid place-items-center p-4 bg-black/50 backdrop-blur-sm">
        <div
          class="w-full max-w-3xl rounded-3xl border border-sky-500/30 dark:border-sky-500/25 bg-white/95 dark:bg-zinc-950/95 shadow-[0_18px_55px_rgba(8,47,73,0.3)] dark:shadow-[0_18px_55px_rgba(8,47,73,0.85)] overflow-hidden">
          <div
            class="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between bg-gradient-to-r from-sky-500/15 via-sky-500/10 to-sky-400/10 dark:from-sky-600/40 dark:via-sky-500/30 dark:to-sky-400/30">
            <h3 class="text-sm font-semibold text-zinc-900 dark:text-zinc-50 flex items-center gap-2">
              <span class="material-symbols-outlined text-base">file_download</span>
              Export active palette
            </h3>
            <button
              class="btn-ghost flex items-center gap-2 !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/60 !text-zinc-800 dark:!text-zinc-100 hover:!bg-zinc-200 dark:hover:!bg-zinc-800"
              @click="openExport = false">
              <span class="material-symbols-outlined text-base">close</span>
              <span class="hidden sm:inline text-xs">Close</span>
            </button>
          </div>
          <div class="p-4 grid gap-4 md:grid-cols-12">
            <aside class="md:col-span-4 space-y-2 text-xs text-zinc-700 dark:text-zinc-300">
              <select v-model="exportFormat"
                class="select w-full text-xs bg-white/80 dark:bg-zinc-900/80 border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-100">
                <option value="css">CSS Variables</option>
                <option value="scss">SCSS Map</option>
                <option value="tailwind">Tailwind Config</option>
                <option value="json">JSON</option>
                <option value="svg">SVG</option>
                <option value="png">PNG</option>
              </select>
              <input v-model="baseName"
                class="input w-full text-xs bg-white/80 border-zinc-300 text-zinc-800 dark:bg-zinc-900/80 dark:border-zinc-700 dark:text-zinc-100"
                placeholder="brand" />
            </aside>
            <section class="md:col-span-8">
              <div v-if="exportFormat !== 'png'">
                <textarea readonly
                  class="code bg-white text-zinc-800 border border-zinc-300 dark:bg-zinc-950 dark:text-zinc-100 dark:border-zinc-800"
                  :value="exportText"></textarea>
              </div>
              <div v-else class="space-y-3">
                <canvas ref="pngCanvas"
                  class="w-full rounded-2xl border border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-950"></canvas>
              </div>
            </section>
          </div>
          <div
            class="p-4 border-t border-zinc-200 dark:border-zinc-800 flex items-center justify-between text-[11px] text-zinc-600 dark:text-zinc-400">
            <div>Colors: {{ activePalette.colors.length }}</div>
            <div class="flex items-center gap-2">
              <button
                class="btn-ghost flex items-center gap-2 !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/60 !text-zinc-800 dark:!text-zinc-100 hover:!bg-zinc-200 dark:hover:!bg-zinc-800"
                @click="copyExport()" :disabled="exportFormat === 'png'">
                <span class="material-symbols-outlined text-base">content_copy</span>
                <span class="hidden sm:inline text-xs">Copy</span>
              </button>
              <button class="btn-primary flex items-center gap-2" @click="downloadExport()">
                <span class="material-symbols-outlined text-base">download</span>
                <span class="text-xs">Download</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="openSave" class="fixed inset-0 z-50 grid place-items-center p-4 bg-black/50 backdrop-blur-sm">
        <div
          class="w-full max-w-xl rounded-3xl border border-sky-500/30 dark:border-sky-500/25 bg-white/95 dark:bg-zinc-950/95 shadow-[0_18px_55px_rgba(8,47,73,0.3)] dark:shadow-[0_18px_55px_rgba(8,47,73,0.85)] overflow-hidden">
          <div
            class="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between bg-gradient-to-r from-sky-500/15 via-sky-500/10 to-sky-400/10 dark:from-sky-600/40 dark:via-sky-500/30 dark:to-sky-400/30">
            <h3 class="text-sm font-semibold text-zinc-900 dark:text-zinc-50 flex items-center gap-2">
              <span class="material-symbols-outlined text-base">folder_special</span>
              Save palette
            </h3>
            <button
              class="btn-ghost flex items-center gap-2 !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/60 !text-zinc-800 dark:!text-zinc-100 hover:!bg-zinc-200 dark:hover:!bg-zinc-800"
              @click="openSave = false">
              <span class="material-symbols-outlined text-base">close</span>
              <span class="hidden sm:inline text-xs">Close</span>
            </button>
          </div>
          <div class="p-4 space-y-4 text-xs text-zinc-700 dark:text-zinc-300">
            <div class="space-y-2">
              <label class="text-[11px] text-zinc-500 dark:text-zinc-400">Folder</label>
              <div class="flex gap-2 items-center">
                <div
                  class="flex-1 h-10 rounded-2xl border border-zinc-300 dark:border-zinc-800 bg-zinc-50/80 dark:bg-zinc-900/80 px-3 flex items-center text-xs text-zinc-800 dark:text-zinc-200">
                  {{ selectedGroup || '/ (root)' }}
                </div>
                <button
                  class="btn-ghost flex items-center gap-2 !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/60 !text-zinc-800 dark:!text-zinc-100 hover:!bg-zinc-200 dark:hover:!bg-zinc-800"
                  @click="openFolder = true">
                  <span class="material-symbols-outlined text-base">folder_open</span>
                  <span class="hidden sm:inline text-xs">Choose</span>
                </button>
                <button
                  class="btn-ghost flex items-center gap-2 !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/60 !text-zinc-800 dark:!text-zinc-100 hover:!bg-zinc-200 dark:hover:!bg-zinc-800 disabled:opacity-40"
                  @click="selectedGroup = ''" :disabled="!selectedGroup">
                  <span class="material-symbols-outlined text-base">clear</span>
                  <span class="hidden sm:inline text-xs">Clear</span>
                </button>
              </div>
            </div>
            <div class="space-y-2">
              <label class="text-[11px] text-zinc-500 dark:text-zinc-400">File name</label>
              <input v-model="saveFileName"
                class="input w-full text-xs bg-white/80 border-zinc-300 text-zinc-800 dark:bg-zinc-900/80 dark:border-zinc-700 dark:text-zinc-100"
                placeholder="brand-palette" />
            </div>
          </div>
          <div
            class="p-4 border-t border-zinc-200 dark:border-zinc-800 flex items-center justify-between text-[11px] text-zinc-600 dark:text-zinc-400">
            <div>Will save a PNG preview</div>
            <div class="flex items-center gap-2">
              <button class="btn-primary flex items-center gap-2" @click="savePngToServer()"
                :disabled="uploading || creatingGroup || !activePalette.colors.length">
                <span class="material-symbols-outlined text-base">save</span>
                <span class="text-xs">Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="openFolder" class="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/50 backdrop-blur-sm">
        <div
          class="w-full max-w-5xl rounded-3xl border border-sky-500/30 dark:border-sky-500/25 bg-white/95 dark:bg-zinc-950/95 shadow-[0_18px_55px_rgba(8,47,73,0.35)] dark:shadow-[0_18px_55px_rgba(8,47,73,0.9)] overflow-hidden">
          <div
            class="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between bg-gradient-to-r from-sky-500/15 via-sky-500/10 to-sky-400/10 dark:from-sky-600/40 dark:via-sky-500/30 dark:to-sky-400/30">
            <h3 class="text-sm font-semibold text-zinc-900 dark:text-zinc-50 flex items-center gap-2">
              <span class="material-symbols-outlined text-base">folder_open</span>
              Choose folder
            </h3>
            <button
              class="btn-ghost flex items-center gap-2 !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/60 !text-zinc-800 dark:!text-zinc-100 hover:!bg-zinc-200 dark:hover:!bg-zinc-800"
              @click="openFolder = false">
              <span class="material-symbols-outlined text-base">close</span>
              <span class="hidden sm:inline text-xs">Close</span>
            </button>
          </div>

          <div class="p-4 space-y-3 text-xs text-zinc-700 dark:text-zinc-300">
            <div class="flex flex-col md:flex-row md:items-center gap-2">
              <div class="flex-1 flex items-center gap-2">
                <input v-model="groupSearch"
                  class="input w-full text-xs bg-white/80 border-zinc-300 text-zinc-800 dark:bg-zinc-900/80 dark:border-zinc-700 dark:text-zinc-100"
                  placeholder="search" />
                <select v-model="groupSort"
                  class="select w-32 text-xs bg-white/80 border-zinc-300 text-zinc-800 dark:bg-zinc-900/80 dark:border-zinc-700 dark:text-zinc-100">
                  <option value="name">Name</option>
                  <option value="count">Files</option>
                </select>
                <button
                  class="btn-ghost flex items-center gap-2 !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/60 !text-zinc-800 dark:!text-zinc-100 hover:!bg-zinc-200 dark:hover:!bg-zinc-800 disabled:opacity-40"
                  @click="refreshGroups()" :disabled="loadingGroups">
                  <span class="material-symbols-outlined text-base">refresh</span>
                  <span class="hidden sm:inline text-xs">Refresh</span>
                </button>
              </div>
              <div class="flex items-center gap-2">
                <input v-model="newGroupName"
                  class="input w-40 text-xs bg-white/80 border-zinc-300 text-zinc-800 dark:bg-zinc-900/80 dark:border-zinc-700 dark:text-zinc-100"
                  placeholder="new folder name" />
                <button
                  class="btn-ghost flex items-center gap-2 !border-sky-500/40 !bg-sky-500/10 !text-sky-700 hover:!bg-sky-500/20 dark:!bg-sky-500/15 dark:!text-sky-100 dark:hover:!bg-sky-500/25 disabled:opacity-40"
                  @click="createGroup()" :disabled="!newGroupName || creatingGroup">
                  <span class="material-symbols-outlined text-base">add</span>
                  <span class="hidden sm:inline text-xs">Create</span>
                </button>
              </div>
            </div>

            <div class="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
              <div
                class="rounded-2xl border border-zinc-300 dark:border-zinc-800 p-3 bg-zinc-50/80 dark:bg-zinc-900/80">
                <div class="text-xs text-zinc-700 dark:text-zinc-300 mb-2">/ (root)</div>
                <div
                  class="h-16 rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-gradient-to-r from-zinc-200 via-zinc-300 to-zinc-400 dark:from-zinc-800 dark:via-zinc-700 dark:to-zinc-600">
                </div>
                <div class="flex items-center justify-between mt-2">
                  <div class="text-[11px] text-zinc-500 dark:text-zinc-400">No folder</div>
                  <input type="radio" name="folderPick" :checked="pendingSelected === ''"
                    @change="pendingSelected = ''" />
                </div>
              </div>

              <div v-for="g in visibleGroups" :key="g.name"
                class="rounded-2xl border border-zinc-300 dark:border-zinc-800 p-3 bg-zinc-50/80 dark:bg-zinc-900/80">
                <div class="h-16 rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 flex">
                  <div v-for="(c, i) in previewColors(g.name)" :key="i" class="flex-1" :style="{ background: c }"></div>
                </div>

                <div class="mt-2 flex items-center justify-between">
                  <div>
                    <div class="text-xs font-medium truncate text-zinc-800 dark:text-zinc-100">{{ g.name }}</div>
                    <div class="text-[11px] text-zinc-500 dark:text-zinc-400">{{ g.count }} files</div>
                  </div>
                  <input type="radio" name="folderPick" :checked="pendingSelected === g.name"
                    @change="pendingSelected = g.name" />
                </div>

                <div class="mt-2 flex flex-wrap items-center gap-1">
                  <template v-if="renameTarget === g.name">
                    <input v-model="renameValue"
                      class="input text-[11px] bg-white/90 border-zinc-300 text-zinc-800 dark:bg-zinc-950 dark:border-zinc-700 dark:text-zinc-100" />
                    <button
                      class="btn-ghost flex items-center gap-1 text-[11px] !border-sky-500/40 !bg-sky-500/10 !text-sky-700 hover:!bg-sky-500/20 dark:!bg-sky-500/15 dark:!text-sky-100 dark:hover:!bg-sky-500/25"
                      @click="submitRename(g.name)" :disabled="!renameValue || renaming">
                      <span class="material-symbols-outlined text-sm">check</span>
                      <span>Save</span>
                    </button>
                    <button
                      class="btn-ghost flex items-center gap-1 text-[11px] !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/80 !text-zinc-700 dark:!text-zinc-200 hover:!bg-zinc-200 dark:hover:!bg-zinc-800"
                      @click="cancelRename()" :disabled="renaming">
                      <span class="material-symbols-outlined text-sm">close</span>
                      <span>Cancel</span>
                    </button>
                  </template>
                  <template v-else>
                    <button
                      class="btn-ghost flex items-center gap-1 text-[11px] !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/80 !text-zinc-700 dark:!text-zinc-200 hover:!bg-zinc-200 dark:hover:!bg-zinc-800"
                      @click="startRename(g.name)">
                      <span class="material-symbols-outlined text-sm">edit</span>
                      <span>Rename</span>
                    </button>
                    <button
                      class="btn-ghost flex items-center gap-1 text-[11px] !border-red-500/50 dark:!border-red-500/60 !bg-red-500/5 dark:!bg-red-500/10 !text-red-700 dark:!text-red-200 hover:!bg-red-500/10 dark:hover:!bg-red-500/20 disabled:opacity-40"
                      @click="deleteGroup(g.name)" :disabled="deleting === g.name">
                      <span class="material-symbols-outlined text-sm">delete</span>
                      <span>Delete</span>
                    </button>
                  </template>
                </div>
              </div>
            </div>

            <div
              class="flex items-center justify-between pt-3 border-t border-zinc-200 dark:border-zinc-800 text-[11px] text-zinc-600 dark:text-zinc-400">
              <div>Selected: {{ pendingSelected || '/' }}</div>
              <div class="flex items-center gap-2">
                <button
                  class="btn-ghost text-xs !border-zinc-300 dark:!border-zinc-700 !bg-zinc-100 dark:!bg-zinc-900/80 !text-zinc-700 dark:!text-zinc-200 hover:!bg-zinc-200 dark:hover:!bg-zinc-800"
                  @click="openFolder = false">
                  Cancel
                </button>
                <button class="btn-primary flex items-center gap-2" @click="useFolder()">
                  <span class="material-symbols-outlined text-base">check_circle</span>
                  <span class="text-xs">Use folder</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="toast">
      <p v-if="toast"
        class="fixed bottom-4 left-1/2 -translate-x-1/2 px-3 py-2 rounded-full bg-zinc-900/95 text-white text-xs shadow-lg border border-zinc-700/80 dark:bg-black/95">
        {{ toast }}
      </p>
    </transition>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import axios from 'axios'

const PREMADE_PALETTES = (() => {
  const curated = [
    {
      "name": "Lunar Cavern 1",
      "colors": [
        "#487BA8",
        "#8DB1ED",
        "#5B4FEF",
        "#45D6B4",
        "#AC6A9C"
      ]
    },
    {
      "name": "Saffron Circuit 2",
      "colors": [
        "#877369",
        "#D754C1",
        "#0BCF36",
        "#BC266C",
        "#D573A3"
      ]
    },
    {
      "name": "Verdant Valley 3",
      "colors": [
        "#F127FD",
        "#C983CA",
        "#A7A73C",
        "#DFB3ED",
        "#318D79"
      ]
    },
    {
      "name": "Nebula Mirror 4",
      "colors": [
        "#7213B2",
        "#79EF83",
        "#BC1367",
        "#4B792A",
        "#1F7D54"
      ]
    },
    {
      "name": "Aurora Pulse 5",
      "colors": [
        "#7C7F8F",
        "#58E3A4",
        "#356398",
        "#44B1F4",
        "#A5268D"
      ]
    },
    {
      "name": "Quantum Pulse 6",
      "colors": [
        "#A563E7",
        "#E6FA95",
        "#133E94",
        "#5E3E15",
        "#7D5699"
      ]
    },
    {
      "name": "Prismatic Fragment 7",
      "colors": [
        "#DFDE4F",
        "#BE94FE",
        "#33338B",
        "#A85816",
        "#ACB9AF"
      ]
    },
    {
      "name": "Spectrum Valley 8",
      "colors": [
        "#47A8B7",
        "#EE6041",
        "#B00D99",
        "#98BB1B",
        "#34E2B7"
      ]
    },
    {
      "name": "Prismatic Garden 9",
      "colors": [
        "#27F78C",
        "#96F858",
        "#3628EE",
        "#3B3FA3",
        "#2E3167"
      ]
    },
    {
      "name": "Opal Field 10",
      "colors": [
        "#88D9D3",
        "#FD0D19",
        "#744CEB",
        "#418C44",
        "#B414D2"
      ]
    },
    {
      "name": "Crimson Mirror 11",
      "colors": [
        "#D552FC",
        "#C66687",
        "#15EE2E",
        "#85A752",
        "#CDFA06"
      ]
    },
    {
      "name": "Nebula Circuit 12",
      "colors": [
        "#92F1DD",
        "#284463",
        "#AF848A",
        "#5A87A1",
        "#C4B043"
      ]
    },
    {
      "name": "Nebula Realm 13",
      "colors": [
        "#86418D",
        "#32B53C",
        "#675BEF",
        "#859342",
        "#B17D9B"
      ]
    },
    {
      "name": "Opal Circuit 14",
      "colors": [
        "#F14875",
        "#98C293",
        "#4A0A2A",
        "#803DC9",
        "#D74BB8"
      ]
    },
    {
      "name": "Prismatic Flare 15",
      "colors": [
        "#2BEA8A",
        "#98D175",
        "#0F2547",
        "#F68674",
        "#CB988F"
      ]
    },
    {
      "name": "Cerulean Storm 16",
      "colors": [
        "#7F004E",
        "#9B9B5C",
        "#9010A2",
        "#AAF291",
        "#20851F"
      ]
    },
    {
      "name": "Crimson Circuit 17",
      "colors": [
        "#8468B3",
        "#9374E6",
        "#8D79A5",
        "#AE02AF",
        "#46CF0A"
      ]
    },
    {
      "name": "Crimson Realm 18",
      "colors": [
        "#149239",
        "#9B3D1D",
        "#4EC6F7",
        "#9FF4A4",
        "#DE548D"
      ]
    },
    {
      "name": "Radiant Wave 19",
      "colors": [
        "#CCE366",
        "#07A5A7",
        "#2B3E49",
        "#39BA39",
        "#FC31DB"
      ]
    },
    {
      "name": "Lunar Empire 20",
      "colors": [
        "#6BA5F1",
        "#DDD35E",
        "#0CA597",
        "#5DA0C5",
        "#06AA5B"
      ]
    },
    {
      "name": "Nebula Cavern 21",
      "colors": [
        "#384BFF",
        "#7078CD",
        "#84807E",
        "#ED533F",
        "#70540C"
      ]
    },
    {
      "name": "Vivid Mirror 22",
      "colors": [
        "#029C04",
        "#20E477",
        "#0FA1DD",
        "#59FCC1",
        "#DF9243"
      ]
    },
    {
      "name": "Velvet Dream 23",
      "colors": [
        "#2014C7",
        "#2AF7D4",
        "#B69E53",
        "#CF5B2E",
        "#00DA71"
      ]
    },
    {
      "name": "Radiant Cascade 24",
      "colors": [
        "#4BD3DF",
        "#B6F8BF",
        "#01B0F6",
        "#B35118",
        "#864863"
      ]
    },
    {
      "name": "Velvet Cavern 25",
      "colors": [
        "#213692",
        "#8BEC34",
        "#4E924E",
        "#1D1000",
        "#FA0B32"
      ]
    },
    {
      "name": "Prismatic Pulse 26",
      "colors": [
        "#7C4A00",
        "#56BA1A",
        "#1B9238",
        "#369E01",
        "#0DC6C8"
      ]
    },
    {
      "name": "Lunar Valley 27",
      "colors": [
        "#B4D74A",
        "#6FC1C0",
        "#CA7FD2",
        "#40BF76",
        "#581994"
      ]
    },
    {
      "name": "Echo Empire 28",
      "colors": [
        "#8888F9",
        "#133064",
        "#AC28B3",
        "#7F4B3E",
        "#7CDE7E"
      ]
    },
    {
      "name": "Opal Fragment 29",
      "colors": [
        "#3A49D7",
        "#EED826",
        "#2A490D",
        "#88CDE9",
        "#00141D"
      ]
    },
    {
      "name": "Spectrum Flare 30",
      "colors": [
        "#37D0AC",
        "#446F19",
        "#7E7BAF",
        "#4F61BB",
        "#C349AB"
      ]
    },
    {
      "name": "Lunar Garden 31",
      "colors": [
        "#AEA9BF",
        "#06EE82",
        "#9627BC",
        "#23E93C",
        "#F86480"
      ]
    },
    {
      "name": "Velvet Pulse 32",
      "colors": [
        "#196A28",
        "#41BE9A",
        "#4E3FEA",
        "#2BE48F",
        "#8227D3"
      ]
    },
    {
      "name": "Saffron Storm 33",
      "colors": [
        "#E141F0",
        "#44F878",
        "#785CB9",
        "#CE3372",
        "#C6E310"
      ]
    },
    {
      "name": "Radiant Horizon 34",
      "colors": [
        "#BCC759",
        "#2952B4",
        "#2CC633",
        "#3A4187",
        "#AE9887"
      ]
    },
    {
      "name": "Iridescent Garden 35",
      "colors": [
        "#5461CB",
        "#3E85F6",
        "#12AF67",
        "#07765F",
        "#365A54"
      ]
    },
    {
      "name": "Saffron Storm 36",
      "colors": [
        "#AA6C00",
        "#42E515",
        "#1A4DF6",
        "#1633F6",
        "#5638E4"
      ]
    },
    {
      "name": "Opal Realm 37",
      "colors": [
        "#68BD2B",
        "#C97A99",
        "#566F8F",
        "#693A42",
        "#C74325"
      ]
    },
    {
      "name": "Lunar Cavern 38",
      "colors": [
        "#57F20D",
        "#7EF4EF",
        "#318E96",
        "#96941F",
        "#D79663"
      ]
    },
    {
      "name": "Aurora Storm 39",
      "colors": [
        "#F617C7",
        "#62B228",
        "#DD2EFE",
        "#FF9007",
        "#290527"
      ]
    },
    {
      "name": "Vivid Garden 40",
      "colors": [
        "#DFDAB5",
        "#E6D8AF",
        "#609749",
        "#9E8F01",
        "#52D86F"
      ]
    },
    {
      "name": "Cerulean Flare 41",
      "colors": [
        "#122A97",
        "#C3DC83",
        "#53130D",
        "#663125",
        "#8DCAD2"
      ]
    },
    {
      "name": "Opal Mist 42",
      "colors": [
        "#EE22D8",
        "#D86DE1",
        "#DF8497",
        "#2114DA",
        "#6068BF"
      ]
    },
    {
      "name": "Aurora Flare 43",
      "colors": [
        "#0CE507",
        "#1375EA",
        "#DCFF93",
        "#4885B7",
        "#09A9B4"
      ]
    },
    {
      "name": "Molten Fragment 44",
      "colors": [
        "#147C90",
        "#D39470",
        "#B8CCE2",
        "#A8C1BC",
        "#60AA90"
      ]
    },
    {
      "name": "Radiant Pulse 45",
      "colors": [
        "#03A6A7",
        "#D890B8",
        "#973C7A",
        "#01CF18",
        "#E715A8"
      ]
    },
    {
      "name": "Crimson Circuit 46",
      "colors": [
        "#80FE24",
        "#6C78AB",
        "#445D1A",
        "#2200AE",
        "#1BA0B3"
      ]
    },
    {
      "name": "Velvet Garden 47",
      "colors": [
        "#6510CF",
        "#14F973",
        "#C1F429",
        "#590D3E",
        "#1EF101"
      ]
    },
    {
      "name": "Molten Empire 48",
      "colors": [
        "#A03B6B",
        "#AD79E4",
        "#37A4EC",
        "#69E65E",
        "#D6E0E6"
      ]
    },
    {
      "name": "Nebula Storm 49",
      "colors": [
        "#AA743E",
        "#D2603B",
        "#49D91B",
        "#89AE92",
        "#BB9872"
      ]
    },
    {
      "name": "Verdant Cascade 50",
      "colors": [
        "#86C5FB",
        "#971957",
        "#13BC91",
        "#AA2688",
        "#2B801A"
      ]
    },
    {
      "name": "Opal Field 51",
      "colors": [
        "#069E28",
        "#7E6402",
        "#408A00",
        "#3D5BA3",
        "#4829E8"
      ]
    },
    {
      "name": "Crimson Valley 52",
      "colors": [
        "#EFAA91",
        "#FEAF8E",
        "#ECF4B6",
        "#C2441D",
        "#4BAB98"
      ]
    },
    {
      "name": "Crimson Dream 53",
      "colors": [
        "#C33915",
        "#6F1DAC",
        "#7966F4",
        "#01B5C6",
        "#615B18"
      ]
    },
    {
      "name": "Velvet Cascade 54",
      "colors": [
        "#311445",
        "#40E4DA",
        "#C60D29",
        "#D3A4B5",
        "#AC1979"
      ]
    },
    {
      "name": "Quantum Mist 55",
      "colors": [
        "#3824A9",
        "#C8A112",
        "#E69E5A",
        "#726512",
        "#1C58A4"
      ]
    },
    {
      "name": "Velvet Field 56",
      "colors": [
        "#1E96A3",
        "#B60782",
        "#0EE8A3",
        "#75F947",
        "#8F87FE"
      ]
    },
    {
      "name": "Nebula Dream 57",
      "colors": [
        "#CA91EF",
        "#C8B227",
        "#894AAC",
        "#4D3412",
        "#4212B2"
      ]
    },
    {
      "name": "Velvet Fragment 58",
      "colors": [
        "#45A0F0",
        "#2AE598",
        "#1F1F82",
        "#13274A",
        "#481E35"
      ]
    },
    {
      "name": "Obsidian Dream 59",
      "colors": [
        "#DC2329",
        "#53B90D",
        "#41C948",
        "#318A9F",
        "#CA691F"
      ]
    },
    {
      "name": "Obsidian Cavern 60",
      "colors": [
        "#BCF11A",
        "#13758A",
        "#CD3771",
        "#E680DB",
        "#B7C6BA"
      ]
    },
    {
      "name": "Quantum Bloom 61",
      "colors": [
        "#5A19B8",
        "#B15DBB",
        "#81D89A",
        "#1A418E",
        "#FC472B"
      ]
    },
    {
      "name": "Crimson Garden 62",
      "colors": [
        "#B58FBC",
        "#A54156",
        "#6F5C87",
        "#060BB0",
        "#BC7F43"
      ]
    },
    {
      "name": "Crimson Nebula 63",
      "colors": [
        "#BA1846",
        "#D0C0DF",
        "#50200B",
        "#743AD7",
        "#053E0E"
      ]
    },
    {
      "name": "Verdant Cavern 64",
      "colors": [
        "#6EA5C8",
        "#584927",
        "#0CDE86",
        "#86EEA4",
        "#987A46"
      ]
    },
    {
      "name": "Iridescent Fragment 65",
      "colors": [
        "#2EF1B7",
        "#F53A95",
        "#70D551",
        "#7AA4E4",
        "#18DDC7"
      ]
    },
    {
      "name": "Radiant Wave 66",
      "colors": [
        "#AA0FF2",
        "#3CF45B",
        "#8A79C8",
        "#BCF666",
        "#3401EB"
      ]
    },
    {
      "name": "Opal Horizon 67",
      "colors": [
        "#BF67F5",
        "#18F5EB",
        "#10FF33",
        "#6D9DDF",
        "#EB6754"
      ]
    },
    {
      "name": "Spectrum Garden 68",
      "colors": [
        "#5C2E3F",
        "#B499B5",
        "#2714AA",
        "#869EDE",
        "#A13C6D"
      ]
    },
    {
      "name": "Nebula Field 69",
      "colors": [
        "#A6D28E",
        "#E624DE",
        "#EE5106",
        "#5B81F4",
        "#D14882"
      ]
    },
    {
      "name": "Solar Horizon 70",
      "colors": [
        "#E7933C",
        "#E56F07",
        "#58462F",
        "#E74708",
        "#FFAB4A"
      ]
    },
    {
      "name": "Iridescent Realm 71",
      "colors": [
        "#AEBC84",
        "#D476F9",
        "#61514E",
        "#553C04",
        "#263E47"
      ]
    },
    {
      "name": "Velvet Bloom 72",
      "colors": [
        "#D30E76",
        "#3A9871",
        "#01296A",
        "#073935",
        "#1E2235"
      ]
    },
    {
      "name": "Crimson Garden 73",
      "colors": [
        "#EBC969",
        "#275601",
        "#9B0350",
        "#EEABB4",
        "#70955C"
      ]
    },
    {
      "name": "Radiant Bloom 74",
      "colors": [
        "#9BE4B5",
        "#A0A7C3",
        "#5F8DB5",
        "#38CCCD",
        "#62326A"
      ]
    },
    {
      "name": "Verdant Horizon 75",
      "colors": [
        "#6F86FE",
        "#D93A2B",
        "#C48650",
        "#EE9D52",
        "#E85AD9"
      ]
    },
    {
      "name": "Vivid Mirror 76",
      "colors": [
        "#33A820",
        "#659687",
        "#D4519C",
        "#5864EE",
        "#B534A7"
      ]
    },
    {
      "name": "Echo Wave 77",
      "colors": [
        "#9FABC2",
        "#F92F7C",
        "#7823CF",
        "#C61C54",
        "#CCA7E5"
      ]
    },
    {
      "name": "Lunar Fragment 78",
      "colors": [
        "#FD37E6",
        "#A34F35",
        "#558A66",
        "#C7B23F",
        "#45E5ED"
      ]
    },
    {
      "name": "Echo Cascade 79",
      "colors": [
        "#C5DF67",
        "#8FA614",
        "#17D57B",
        "#2C0AFE",
        "#5931F8"
      ]
    },
    {
      "name": "Echo Empire 80",
      "colors": [
        "#5A9A94",
        "#D9FCD2",
        "#28BC36",
        "#2F42CE",
        "#35279C"
      ]
    },
    {
      "name": "Prismatic Horizon 81",
      "colors": [
        "#BEF6F6",
        "#10D8B3",
        "#791CB7",
        "#E0A14C",
        "#F704EF"
      ]
    },
    {
      "name": "Aurora Wave 82",
      "colors": [
        "#F35E20",
        "#0B7E39",
        "#B04DA6",
        "#9E94FF",
        "#CF4632"
      ]
    },
    {
      "name": "Prismatic Storm 83",
      "colors": [
        "#A3B6D1",
        "#BA6E55",
        "#10EFC5",
        "#4C58D8",
        "#955AFE"
      ]
    },
    {
      "name": "Vivid Wave 84",
      "colors": [
        "#DEAFA6",
        "#59B606",
        "#B0BCB6",
        "#ACE68D",
        "#E0AC35"
      ]
    },
    {
      "name": "Solar Mirror 85",
      "colors": [
        "#1A264E",
        "#806755",
        "#BFD2F1",
        "#A7CAF7",
        "#D2C158"
      ]
    },
    {
      "name": "Spectrum Field 86",
      "colors": [
        "#22487C",
        "#4080B8",
        "#3D2BC7",
        "#99D688",
        "#7492E9"
      ]
    },
    {
      "name": "Lunar Flare 87",
      "colors": [
        "#E4BCD2",
        "#D9704E",
        "#2711D0",
        "#6587B9",
        "#FFFB7A"
      ]
    },
    {
      "name": "Nebula Pulse 88",
      "colors": [
        "#1C62EE",
        "#F386E2",
        "#88094E",
        "#2180DF",
        "#B57C28"
      ]
    },
    {
      "name": "Lunar Cascade 89",
      "colors": [
        "#AD5C86",
        "#4A360F",
        "#E4175C",
        "#41B378",
        "#18A383"
      ]
    },
    {
      "name": "Aurora Mirror 90",
      "colors": [
        "#5B572F",
        "#0D6DCA",
        "#D8F456",
        "#783646",
        "#5804A3"
      ]
    },
    {
      "name": "Nebula Dream 91",
      "colors": [
        "#2FAE2F",
        "#DE9F52",
        "#88D3CC",
        "#ABC1A6",
        "#A13437"
      ]
    },
    {
      "name": "Velvet Bloom 92",
      "colors": [
        "#86B935",
        "#DB7F05",
        "#85B552",
        "#1454EA",
        "#C2FD81"
      ]
    },
    {
      "name": "Prismatic Flare 93",
      "colors": [
        "#02BD3A",
        "#64B841",
        "#6334C7",
        "#E5AFEC",
        "#E098DF"
      ]
    },
    {
      "name": "Aurora Dream 94",
      "colors": [
        "#31DD40",
        "#FF4D31",
        "#62769E",
        "#6D320B",
        "#C65C44"
      ]
    },
    {
      "name": "Saffron Valley 95",
      "colors": [
        "#AF3698",
        "#275827",
        "#C014CA",
        "#13DCF0",
        "#4BC993"
      ]
    },
    {
      "name": "Verdant Valley 96",
      "colors": [
        "#E46C55",
        "#12B6B6",
        "#8E4DF9",
        "#481641",
        "#141109"
      ]
    },
    {
      "name": "Nebula Cascade 97",
      "colors": [
        "#8B66EF",
        "#435DD5",
        "#4DDDEC",
        "#ECB61B",
        "#DACB4E"
      ]
    },
    {
      "name": "Vivid Pulse 98",
      "colors": [
        "#CC3125",
        "#9AC39F",
        "#8B681B",
        "#5E41D8",
        "#C95433"
      ]
    },
    {
      "name": "Velvet Storm 99",
      "colors": [
        "#999474",
        "#2A3346",
        "#E0BEB0",
        "#5A8D02",
        "#ECADE0"
      ]
    },
    {
      "name": "Lunar Mirror 100",
      "colors": [
        "#4D16D1",
        "#5705DE",
        "#B39BEC",
        "#808F33",
        "#BA5455"
      ]
    },
    {
      "name": "Verdant Cascade 101",
      "colors": [
        "#C33166",
        "#8A6E59",
        "#CB6577",
        "#52D9FA",
        "#35B358"
      ]
    },
    {
      "name": "Saffron Cascade 102",
      "colors": [
        "#4EB24B",
        "#BFF83A",
        "#BA9BD3",
        "#A86506",
        "#7B800C"
      ]
    },
    {
      "name": "Opal Dream 103",
      "colors": [
        "#B8540B",
        "#A1773A",
        "#1C3B31",
        "#A4B2EE",
        "#5218B6"
      ]
    },
    {
      "name": "Opal Valley 104",
      "colors": [
        "#12002E",
        "#27D6D3",
        "#89F197",
        "#F33123",
        "#4ADEDE"
      ]
    },
    {
      "name": "Radiant Storm 105",
      "colors": [
        "#CC6C68",
        "#7CC6BD",
        "#D94301",
        "#287F32",
        "#C38A82"
      ]
    },
    {
      "name": "Molten Mirror 106",
      "colors": [
        "#0BB922",
        "#378479",
        "#22B01F",
        "#3B10F3",
        "#91ADE7"
      ]
    },
    {
      "name": "Spectrum Storm 107",
      "colors": [
        "#A29808",
        "#AA2E48",
        "#3EE13F",
        "#EEE2B6",
        "#FE3D42"
      ]
    },
    {
      "name": "Velvet Bloom 108",
      "colors": [
        "#445644",
        "#AB77C3",
        "#ADFAC3",
        "#C0D209",
        "#020475"
      ]
    },
    {
      "name": "Obsidian Glow 109",
      "colors": [
        "#5C5323",
        "#42E71E",
        "#290B97",
        "#BDA68B",
        "#DE8810"
      ]
    },
    {
      "name": "Iridescent Wave 110",
      "colors": [
        "#0349F1",
        "#131A1C",
        "#B807CD",
        "#C4F481",
        "#9FC6EB"
      ]
    },
    {
      "name": "Verdant Pulse 111",
      "colors": [
        "#56571C",
        "#C4B3FC",
        "#88C75C",
        "#728C29",
        "#37ADEA"
      ]
    },
    {
      "name": "Obsidian Pulse 112",
      "colors": [
        "#D28EE2",
        "#ACDE8A",
        "#9A6DDF",
        "#A8EEFC",
        "#E40927"
      ]
    },
    {
      "name": "Nebula Mirror 113",
      "colors": [
        "#77D86F",
        "#84AAB2",
        "#7201CE",
        "#72AF0D",
        "#707100"
      ]
    },
    {
      "name": "Verdant Empire 114",
      "colors": [
        "#E46641",
        "#179FA5",
        "#FAB579",
        "#8E4186",
        "#1EEBC4"
      ]
    },
    {
      "name": "Crimson Flare 115",
      "colors": [
        "#9A2576",
        "#BE1BD0",
        "#F2058B",
        "#96235F",
        "#A93B3B"
      ]
    },
    {
      "name": "Lunar Empire 116",
      "colors": [
        "#4E3415",
        "#C6770D",
        "#089914",
        "#83F6A0",
        "#B088F3"
      ]
    },
    {
      "name": "Echo Flare 117",
      "colors": [
        "#BF4159",
        "#66E2F7",
        "#C84315",
        "#DB3B6F",
        "#FA777C"
      ]
    },
    {
      "name": "Cerulean Storm 118",
      "colors": [
        "#2EF610",
        "#5DEC96",
        "#349306",
        "#86D037",
        "#B86D89"
      ]
    },
    {
      "name": "Echo Horizon 119",
      "colors": [
        "#E5675A",
        "#754B2E",
        "#0DE380",
        "#AD3F6C",
        "#B478EB"
      ]
    },
    {
      "name": "Vivid Storm 120",
      "colors": [
        "#B33A44",
        "#DCD75E",
        "#7B65F9",
        "#078491",
        "#27F1BE"
      ]
    },
    {
      "name": "Molten Pulse 121",
      "colors": [
        "#210677",
        "#B77E0B",
        "#802A8C",
        "#0B5BBA",
        "#308AD1"
      ]
    },
    {
      "name": "Prismatic Mist 122",
      "colors": [
        "#5FEE19",
        "#FCC8C8",
        "#8668A8",
        "#2E5B9E",
        "#C87D8F"
      ]
    },
    {
      "name": "Spectrum Cavern 123",
      "colors": [
        "#597CD3",
        "#5C0F29",
        "#9677D7",
        "#429340",
        "#B48789"
      ]
    },
    {
      "name": "Nebula Bloom 124",
      "colors": [
        "#E38B2F",
        "#13B1F5",
        "#DED256",
        "#13B3F9",
        "#0CD8C9"
      ]
    },
    {
      "name": "Saffron Storm 125",
      "colors": [
        "#1301CE",
        "#0EE0F7",
        "#B285FA",
        "#BD1058",
        "#764433"
      ]
    },
    {
      "name": "Quantum Garden 126",
      "colors": [
        "#1793F2",
        "#9C061A",
        "#9F3047",
        "#03DC92",
        "#7041A8"
      ]
    },
    {
      "name": "Quantum Mist 127",
      "colors": [
        "#C8CC59",
        "#851C47",
        "#D6B4D2",
        "#D5D26D",
        "#CE8AFF"
      ]
    },
    {
      "name": "Nebula Flare 128",
      "colors": [
        "#8AFF62",
        "#61B1D1",
        "#FB1801",
        "#064B27",
        "#0E27F9"
      ]
    },
    {
      "name": "Verdant Empire 129",
      "colors": [
        "#838E55",
        "#17D67A",
        "#EB2C0F",
        "#E4DF67",
        "#53C4F8"
      ]
    },
    {
      "name": "Crimson Field 130",
      "colors": [
        "#E7455D",
        "#2E2447",
        "#E1919F",
        "#33E79D",
        "#796EBB"
      ]
    },
    {
      "name": "Vivid Cascade 131",
      "colors": [
        "#F2D2F7",
        "#2B7921",
        "#B08C01",
        "#83E7EA",
        "#CB43FB"
      ]
    },
    {
      "name": "Cerulean Empire 132",
      "colors": [
        "#AB74E0",
        "#945855",
        "#A7A59A",
        "#55A3D9",
        "#F3B2E3"
      ]
    },
    {
      "name": "Nimbus Cavern 133",
      "colors": [
        "#DFA0D0",
        "#0C906D",
        "#66210E",
        "#B4FFE3",
        "#FAAFAD"
      ]
    },
    {
      "name": "Opal Mirror 134",
      "colors": [
        "#E91FD8",
        "#BD0BCD",
        "#DD2C96",
        "#C2ED05",
        "#C1B2DB"
      ]
    },
    {
      "name": "Spectrum Realm 135",
      "colors": [
        "#4BC9B9",
        "#79E018",
        "#52679A",
        "#4A0C66",
        "#F68436"
      ]
    },
    {
      "name": "Cerulean Field 136",
      "colors": [
        "#775AC6",
        "#760292",
        "#977E0A",
        "#AA4C09",
        "#FA16CB"
      ]
    },
    {
      "name": "Prismatic Cavern 137",
      "colors": [
        "#8F4DDA",
        "#26BC1E",
        "#C2A570",
        "#FFB203",
        "#102965"
      ]
    },
    {
      "name": "Opal Horizon 138",
      "colors": [
        "#738564",
        "#1BF780",
        "#F2D4F9",
        "#13CEB5",
        "#4D3F64"
      ]
    },
    {
      "name": "Echo Glow 139",
      "colors": [
        "#7BBB66",
        "#F6902E",
        "#A9591F",
        "#DE6D75",
        "#8CE341"
      ]
    },
    {
      "name": "Obsidian Bloom 140",
      "colors": [
        "#7DD6EF",
        "#1FB839",
        "#7BE6CD",
        "#76ABDE",
        "#D5EBD9"
      ]
    },
    {
      "name": "Crimson Nebula 141",
      "colors": [
        "#DE1B97",
        "#F19231",
        "#F92169",
        "#A52AA8",
        "#8D9D2F"
      ]
    },
    {
      "name": "Crimson Mist 142",
      "colors": [
        "#A7B908",
        "#5F5A37",
        "#EBAF98",
        "#644239",
        "#A8DCAF"
      ]
    },
    {
      "name": "Solar Flare 143",
      "colors": [
        "#965BF7",
        "#081832",
        "#73E76F",
        "#6AA661",
        "#CF3321"
      ]
    },
    {
      "name": "Lunar Dream 144",
      "colors": [
        "#9689EA",
        "#6BA5DB",
        "#87D857",
        "#370079",
        "#2D8D27"
      ]
    },
    {
      "name": "Nimbus Valley 145",
      "colors": [
        "#AD1CE7",
        "#C57EAF",
        "#A395D1",
        "#AEA927",
        "#741136"
      ]
    },
    {
      "name": "Radiant Glow 146",
      "colors": [
        "#6200A3",
        "#09F11F",
        "#DDF6DD",
        "#4F3EF4",
        "#A9824F"
      ]
    },
    {
      "name": "Lunar Storm 147",
      "colors": [
        "#EF283D",
        "#50D9B5",
        "#2C2F60",
        "#3A0115",
        "#05014D"
      ]
    },
    {
      "name": "Molten Pulse 148",
      "colors": [
        "#393A1D",
        "#5ABC13",
        "#385586",
        "#4342D1",
        "#FA3609"
      ]
    },
    {
      "name": "Opal Dream 149",
      "colors": [
        "#354DFB",
        "#4395AA",
        "#A3C565",
        "#59F29B",
        "#7DF3AA"
      ]
    },
    {
      "name": "Molten Dream 150",
      "colors": [
        "#17B547",
        "#8DE318",
        "#6943B5",
        "#CD7D06",
        "#E53335"
      ]
    },
    {
      "name": "Vivid Fragment 151",
      "colors": [
        "#294070",
        "#D1D1AE",
        "#08D68F",
        "#384891",
        "#6C8337"
      ]
    },
    {
      "name": "Cerulean Flare 152",
      "colors": [
        "#FF418E",
        "#F8462C",
        "#2D5903",
        "#4549C3",
        "#A8CE4B"
      ]
    },
    {
      "name": "Nebula Nebula 153",
      "colors": [
        "#27DBE1",
        "#932A4D",
        "#4272BA",
        "#142B2F",
        "#3CCC8D"
      ]
    },
    {
      "name": "Saffron Empire 154",
      "colors": [
        "#4B3BF8",
        "#108FF7",
        "#57609D",
        "#881F98",
        "#AB736F"
      ]
    },
    {
      "name": "Velvet Fragment 155",
      "colors": [
        "#DA7ADB",
        "#19A4FB",
        "#F4E7DC",
        "#8EE183",
        "#FCF0F8"
      ]
    },
    {
      "name": "Saffron Pulse 156",
      "colors": [
        "#184478",
        "#5D28AF",
        "#8C432C",
        "#73D9AF",
        "#B1E9E7"
      ]
    },
    {
      "name": "Verdant Bloom 157",
      "colors": [
        "#BB3AE6",
        "#112693",
        "#9C26CC",
        "#81AA4A",
        "#A4ADA9"
      ]
    },
    {
      "name": "Quantum Empire 158",
      "colors": [
        "#2AB140",
        "#5D0B21",
        "#D55571",
        "#47D4FA",
        "#112036"
      ]
    },
    {
      "name": "Prismatic Horizon 159",
      "colors": [
        "#840D83",
        "#FFB46B",
        "#385B23",
        "#8B1FD8",
        "#8FD30F"
      ]
    },
    {
      "name": "Solar Fragment 160",
      "colors": [
        "#018BF6",
        "#04DF00",
        "#A749DA",
        "#E292D5",
        "#515A0D"
      ]
    },
    {
      "name": "Quantum Pulse 161",
      "colors": [
        "#AD16EE",
        "#9AD5D1",
        "#302EB1",
        "#A2595E",
        "#625CF2"
      ]
    },
    {
      "name": "Spectrum Horizon 162",
      "colors": [
        "#DE979B",
        "#F68446",
        "#3D6A87",
        "#5ED9AD",
        "#CC6142"
      ]
    },
    {
      "name": "Nimbus Fragment 163",
      "colors": [
        "#0FA01E",
        "#EFFE29",
        "#FFF4AE",
        "#B744C1",
        "#58B7B0"
      ]
    },
    {
      "name": "Nimbus Horizon 164",
      "colors": [
        "#ABFD7D",
        "#0B421D",
        "#97D4D3",
        "#5D4D6E",
        "#D588A3"
      ]
    },
    {
      "name": "Solar Cascade 165",
      "colors": [
        "#0DFAB4",
        "#475A78",
        "#32F632",
        "#4FB221",
        "#B1C4F5"
      ]
    },
    {
      "name": "Crimson Mirror 166",
      "colors": [
        "#6DBB14",
        "#E78A28",
        "#E89D6F",
        "#E1DA1C",
        "#48BAE5"
      ]
    },
    {
      "name": "Spectrum Glow 167",
      "colors": [
        "#03E902",
        "#FD4810",
        "#FC5449",
        "#956924",
        "#EE892C"
      ]
    },
    {
      "name": "Nebula Glow 168",
      "colors": [
        "#A8A140",
        "#1C6725",
        "#67B682",
        "#A8838E",
        "#21F125"
      ]
    },
    {
      "name": "Spectrum Nebula 169",
      "colors": [
        "#C117CD",
        "#B6AABC",
        "#DF699A",
        "#9B2EA5",
        "#34FDB0"
      ]
    },
    {
      "name": "Verdant Valley 170",
      "colors": [
        "#64D8D6",
        "#E668D1",
        "#98A099",
        "#C63CFB",
        "#7AED45"
      ]
    },
    {
      "name": "Lunar Cavern 171",
      "colors": [
        "#630076",
        "#C8D8B6",
        "#BF946B",
        "#9EE14F",
        "#2C8AD2"
      ]
    },
    {
      "name": "Prismatic Nebula 172",
      "colors": [
        "#BED977",
        "#464095",
        "#B67B32",
        "#B723CA",
        "#287704"
      ]
    },
    {
      "name": "Molten Flare 173",
      "colors": [
        "#A91018",
        "#45E254",
        "#AF7BD4",
        "#7FDEBC",
        "#B035C7"
      ]
    },
    {
      "name": "Velvet Fragment 174",
      "colors": [
        "#F4B97B",
        "#0916C7",
        "#B5B51F",
        "#6A566E",
        "#876462"
      ]
    },
    {
      "name": "Molten Flare 175",
      "colors": [
        "#24ED52",
        "#96604F",
        "#D62F97",
        "#551CC8",
        "#203188"
      ]
    },
    {
      "name": "Lunar Mist 176",
      "colors": [
        "#F4E643",
        "#B26C83",
        "#E41605",
        "#F0D89C",
        "#7D95F8"
      ]
    },
    {
      "name": "Cerulean Empire 177",
      "colors": [
        "#3CCDFA",
        "#2A24C7",
        "#9809EB",
        "#9C081F",
        "#FBA1CF"
      ]
    },
    {
      "name": "Opal Circuit 178",
      "colors": [
        "#28DC36",
        "#6E47AB",
        "#83FB3B",
        "#997B4E",
        "#C2131C"
      ]
    },
    {
      "name": "Solar Glow 179",
      "colors": [
        "#676A40",
        "#D6E0E9",
        "#725718",
        "#7B6EBF",
        "#52F81E"
      ]
    },
    {
      "name": "Lunar Bloom 180",
      "colors": [
        "#F49945",
        "#C1C87D",
        "#6DE0EC",
        "#A8AE93",
        "#A86BD6"
      ]
    },
    {
      "name": "Solar Circuit 181",
      "colors": [
        "#032DB2",
        "#6874C0",
        "#C7C5B5",
        "#555CC8",
        "#AC9DCE"
      ]
    },
    {
      "name": "Spectrum Flare 182",
      "colors": [
        "#874410",
        "#99109B",
        "#B13E5B",
        "#7CFEA5",
        "#CA8C43"
      ]
    },
    {
      "name": "Saffron Fragment 183",
      "colors": [
        "#4DE39E",
        "#C77BFB",
        "#196E1A",
        "#0780EF",
        "#8D1F26"
      ]
    },
    {
      "name": "Molten Dream 184",
      "colors": [
        "#8BD7B3",
        "#23094F",
        "#CB4358",
        "#B813A5",
        "#886310"
      ]
    },
    {
      "name": "Nimbus Mist 185",
      "colors": [
        "#B99680",
        "#827DF5",
        "#AC2F6A",
        "#8D1ABB",
        "#5CE314"
      ]
    },
    {
      "name": "Iridescent Field 186",
      "colors": [
        "#0AC3F1",
        "#ED53BF",
        "#BD0D24",
        "#4D9CF4",
        "#B9063B"
      ]
    },
    {
      "name": "Aurora Garden 187",
      "colors": [
        "#A414AE",
        "#FBB064",
        "#0FDF75",
        "#57F199",
        "#613D40"
      ]
    },
    {
      "name": "Saffron Valley 188",
      "colors": [
        "#1AA25A",
        "#4A2DCF",
        "#60F98B",
        "#A7F65E",
        "#BF9012"
      ]
    },
    {
      "name": "Velvet Pulse 189",
      "colors": [
        "#579D7A",
        "#895B11",
        "#0071A5",
        "#EFDBC7",
        "#CEB3B2"
      ]
    },
    {
      "name": "Nimbus Nebula 190",
      "colors": [
        "#59E806",
        "#EF0589",
        "#DF76EA",
        "#ACD993",
        "#421A4F"
      ]
    },
    {
      "name": "Crimson Fragment 191",
      "colors": [
        "#3125D1",
        "#B0C17A",
        "#BAC72B",
        "#CA19AD",
        "#D8DF9A"
      ]
    },
    {
      "name": "Cerulean Field 192",
      "colors": [
        "#A8F290",
        "#1E6A68",
        "#0D91A9",
        "#3EB87A",
        "#3A191D"
      ]
    },
    {
      "name": "Opal Wave 193",
      "colors": [
        "#E99682",
        "#7FAEC0",
        "#7442BD",
        "#0B138C",
        "#31A86B"
      ]
    },
    {
      "name": "Saffron Wave 194",
      "colors": [
        "#66F821",
        "#A6496A",
        "#A4B238",
        "#33AE98",
        "#C53287"
      ]
    },
    {
      "name": "Vivid Mirror 195",
      "colors": [
        "#BCAC5F",
        "#6E0847",
        "#0A57D5",
        "#DEA4E4",
        "#C7D80A"
      ]
    },
    {
      "name": "Radiant Mirror 196",
      "colors": [
        "#273BBC",
        "#620E1F",
        "#7590D6",
        "#E701C5",
        "#651FEC"
      ]
    },
    {
      "name": "Crimson Field 197",
      "colors": [
        "#8758CD",
        "#D109BC",
        "#7D3C98",
        "#F4C654",
        "#9E8008"
      ]
    },
    {
      "name": "Echo Realm 198",
      "colors": [
        "#59E2CD",
        "#1FD56C",
        "#6ADA6B",
        "#1F307D",
        "#B10472"
      ]
    },
    {
      "name": "Radiant Mist 199",
      "colors": [
        "#08B21B",
        "#2524DE",
        "#910A6B",
        "#73AC3D",
        "#5D0BAC"
      ]
    },
    {
      "name": "Verdant Empire 200",
      "colors": [
        "#DD5290",
        "#4EDC65",
        "#728F49",
        "#F80DC8",
        "#4C5A93"
      ]
    },
    {
      "name": "Cerulean Dream 201",
      "colors": [
        "#9E573C",
        "#84B0E1",
        "#2442DB",
        "#1F98E8",
        "#EE1000"
      ]
    },
    {
      "name": "Velvet Field 202",
      "colors": [
        "#5AF205",
        "#89AAB0",
        "#984245",
        "#15329B",
        "#EBCF8C"
      ]
    },
    {
      "name": "Nimbus Field 203",
      "colors": [
        "#794026",
        "#592F5A",
        "#8CC6B3",
        "#7676AA",
        "#A025AB"
      ]
    },
    {
      "name": "Verdant Bloom 204",
      "colors": [
        "#4AC3EC",
        "#CEAD37",
        "#CB0E0C",
        "#DAA31A",
        "#EC2DB7"
      ]
    },
    {
      "name": "Saffron Circuit 205",
      "colors": [
        "#604464",
        "#92A40D",
        "#CCD859",
        "#5D49C2",
        "#BA248B"
      ]
    },
    {
      "name": "Velvet Horizon 206",
      "colors": [
        "#7540DE",
        "#21EFF8",
        "#B8D4B9",
        "#80B7FA",
        "#8A1063"
      ]
    },
    {
      "name": "Prismatic Field 207",
      "colors": [
        "#6EFB7E",
        "#0F6E53",
        "#46F407",
        "#7F2A84",
        "#7E9CE6"
      ]
    },
    {
      "name": "Lunar Cavern 208",
      "colors": [
        "#72ED75",
        "#CD81B0",
        "#9999A6",
        "#1C9F08",
        "#9FBB45"
      ]
    },
    {
      "name": "Opal Mirror 209",
      "colors": [
        "#3314A9",
        "#A7F06C",
        "#C8E4BD",
        "#B07C6C",
        "#548631"
      ]
    },
    {
      "name": "Verdant Horizon 210",
      "colors": [
        "#D51B68",
        "#A9F0A3",
        "#163757",
        "#2C3557",
        "#68C75E"
      ]
    },
    {
      "name": "Velvet Mist 211",
      "colors": [
        "#29F770",
        "#91FE2D",
        "#6F0435",
        "#E40609",
        "#5EC4D8"
      ]
    },
    {
      "name": "Solar Garden 212",
      "colors": [
        "#28F8C1",
        "#4EAA82",
        "#C50229",
        "#A90E0E",
        "#DA0B2A"
      ]
    },
    {
      "name": "Molten Garden 213",
      "colors": [
        "#884643",
        "#4D137D",
        "#E29524",
        "#0FC718",
        "#9931D5"
      ]
    },
    {
      "name": "Nebula Circuit 214",
      "colors": [
        "#48CCE0",
        "#B67A96",
        "#C72708",
        "#DAFCB0",
        "#12ACF6"
      ]
    },
    {
      "name": "Iridescent Cavern 215",
      "colors": [
        "#C3CD52",
        "#638BFD",
        "#AD5782",
        "#47DC54",
        "#0C38C1"
      ]
    },
    {
      "name": "Echo Dream 216",
      "colors": [
        "#DC5FE6",
        "#38E162",
        "#82F1CD",
        "#22AE92",
        "#DD3E0A"
      ]
    },
    {
      "name": "Spectrum Circuit 217",
      "colors": [
        "#934533",
        "#8B13AB",
        "#D6FBDE",
        "#B0883E",
        "#A3CE7E"
      ]
    },
    {
      "name": "Lunar Wave 218",
      "colors": [
        "#B791F3",
        "#9B749D",
        "#F48DC2",
        "#0387FD",
        "#228FDE"
      ]
    },
    {
      "name": "Crimson Cavern 219",
      "colors": [
        "#93B407",
        "#FC19E5",
        "#082D1E",
        "#09AD5D",
        "#8BEB49"
      ]
    },
    {
      "name": "Solar Storm 220",
      "colors": [
        "#26705B",
        "#8F4669",
        "#1F3413",
        "#FB7C15",
        "#26D398"
      ]
    },
    {
      "name": "Molten Empire 221",
      "colors": [
        "#539FA5",
        "#AA314D",
        "#40885F",
        "#D170FF",
        "#07F344"
      ]
    },
    {
      "name": "Echo Flare 222",
      "colors": [
        "#65A72A",
        "#3C3D37",
        "#3C7A9A",
        "#66201E",
        "#44108C"
      ]
    },
    {
      "name": "Prismatic Garden 223",
      "colors": [
        "#DBEEE1",
        "#4B5F1B",
        "#F0CB76",
        "#2775A7",
        "#8B577F"
      ]
    },
    {
      "name": "Echo Valley 224",
      "colors": [
        "#745561",
        "#1F35C7",
        "#A6823B",
        "#2EF82F",
        "#D6957C"
      ]
    },
    {
      "name": "Nebula Empire 225",
      "colors": [
        "#0298BA",
        "#EE91AB",
        "#B30098",
        "#755974",
        "#E6B864"
      ]
    },
    {
      "name": "Quantum Cavern 226",
      "colors": [
        "#D117AB",
        "#70BCCE",
        "#E0DECC",
        "#9F46F2",
        "#EFD221"
      ]
    },
    {
      "name": "Cerulean Garden 227",
      "colors": [
        "#427472",
        "#6211F0",
        "#49BE68",
        "#6F9CB0",
        "#E77975"
      ]
    },
    {
      "name": "Molten Glow 228",
      "colors": [
        "#B10EAD",
        "#7D526F",
        "#DCF498",
        "#B77E14",
        "#0B4A49"
      ]
    },
    {
      "name": "Solar Storm 229",
      "colors": [
        "#6788F5",
        "#92B178",
        "#1F6E89",
        "#512B52",
        "#122C11"
      ]
    },
    {
      "name": "Saffron Fragment 230",
      "colors": [
        "#1AA701",
        "#15A533",
        "#13E7C5",
        "#186B45",
        "#CA2FE0"
      ]
    },
    {
      "name": "Echo Nebula 231",
      "colors": [
        "#0009DB",
        "#D4C6D7",
        "#B67ED9",
        "#E5319F",
        "#9E4AB6"
      ]
    },
    {
      "name": "Obsidian Garden 232",
      "colors": [
        "#6BB26A",
        "#7DCD52",
        "#76F93C",
        "#67A32B",
        "#3B1669"
      ]
    },
    {
      "name": "Aurora Storm 233",
      "colors": [
        "#C6B0D0",
        "#1A6FA4",
        "#D7DF02",
        "#472CC3",
        "#9C429B"
      ]
    },
    {
      "name": "Prismatic Glow 234",
      "colors": [
        "#B17EE9",
        "#9A3293",
        "#9FA6CD",
        "#43FC0B",
        "#DFFF3D"
      ]
    },
    {
      "name": "Molten Glow 235",
      "colors": [
        "#661C12",
        "#0B50EA",
        "#3FD58D",
        "#57FF6A",
        "#8A113F"
      ]
    },
    {
      "name": "Aurora Garden 236",
      "colors": [
        "#D30A22",
        "#CA85F1",
        "#E2106D",
        "#A80470",
        "#055510"
      ]
    },
    {
      "name": "Crimson Circuit 237",
      "colors": [
        "#83F9C0",
        "#558DA0",
        "#CF327C",
        "#D48437",
        "#FE7870"
      ]
    },
    {
      "name": "Opal Circuit 238",
      "colors": [
        "#C176AB",
        "#4AE47A",
        "#E79CC5",
        "#050666",
        "#0A4C00"
      ]
    },
    {
      "name": "Cerulean Cavern 239",
      "colors": [
        "#9B1E49",
        "#A31354",
        "#8CB625",
        "#AB3436",
        "#2AB9CD"
      ]
    },
    {
      "name": "Lunar Realm 240",
      "colors": [
        "#BB8164",
        "#AC5133",
        "#D2A982",
        "#C57C94",
        "#44AA23"
      ]
    },
    {
      "name": "Radiant Circuit 241",
      "colors": [
        "#44E4FB",
        "#BBAC17",
        "#734382",
        "#C99606",
        "#232833"
      ]
    },
    {
      "name": "Obsidian Garden 242",
      "colors": [
        "#4ED53F",
        "#012B18",
        "#5BB8D2",
        "#87561B",
        "#0332A2"
      ]
    },
    {
      "name": "Radiant Cascade 243",
      "colors": [
        "#7FC670",
        "#0CC554",
        "#2C454D",
        "#973746",
        "#470BEF"
      ]
    },
    {
      "name": "Aurora Nebula 244",
      "colors": [
        "#41A138",
        "#3617E7",
        "#C7150E",
        "#675322",
        "#F55D19"
      ]
    },
    {
      "name": "Echo Circuit 245",
      "colors": [
        "#99BE19",
        "#563288",
        "#92A886",
        "#0A6525",
        "#32C55B"
      ]
    },
    {
      "name": "Aurora Nebula 246",
      "colors": [
        "#AE57D9",
        "#245CC1",
        "#8C43DD",
        "#DB2C53",
        "#BB4316"
      ]
    },
    {
      "name": "Radiant Storm 247",
      "colors": [
        "#F64C80",
        "#E74C89",
        "#3D9732",
        "#AB1882",
        "#8E15CA"
      ]
    },
    {
      "name": "Molten Dream 248",
      "colors": [
        "#B3AD36",
        "#34CB38",
        "#84D2EF",
        "#18CC23",
        "#BDCBC2"
      ]
    },
    {
      "name": "Saffron Fragment 249",
      "colors": [
        "#85ABCB",
        "#1B94F1",
        "#7750B6",
        "#753C47",
        "#49DDF6"
      ]
    },
    {
      "name": "Obsidian Garden 250",
      "colors": [
        "#A76226",
        "#8EDF89",
        "#A86300",
        "#79469D",
        "#9E6A0D"
      ]
    },
    {
      "name": "Velvet Cavern 251",
      "colors": [
        "#001CFC",
        "#DC2688",
        "#5D9D0B",
        "#A0A881",
        "#F0465E"
      ]
    },
    {
      "name": "Spectrum Field 252",
      "colors": [
        "#858B40",
        "#5D4926",
        "#D2AFD7",
        "#E1621A",
        "#93EA68"
      ]
    },
    {
      "name": "Aurora Realm 253",
      "colors": [
        "#D02751",
        "#E85DAB",
        "#B9B066",
        "#0AB4BF",
        "#58ACBB"
      ]
    },
    {
      "name": "Nebula Nebula 254",
      "colors": [
        "#FE3BB8",
        "#D6245D",
        "#154752",
        "#6F4F53",
        "#5F3D49"
      ]
    },
    {
      "name": "Cerulean Glow 255",
      "colors": [
        "#845B3B",
        "#E43271",
        "#3EAC33",
        "#363024",
        "#D35750"
      ]
    },
    {
      "name": "Molten Bloom 256",
      "colors": [
        "#E1B99D",
        "#8C3A86",
        "#C3C888",
        "#52D7CE",
        "#759CE3"
      ]
    },
    {
      "name": "Nimbus Nebula 257",
      "colors": [
        "#0C7100",
        "#484F03",
        "#55E7C8",
        "#FC8518",
        "#083BB7"
      ]
    },
    {
      "name": "Aurora Pulse 258",
      "colors": [
        "#3B4070",
        "#6F01BA",
        "#CC08AA",
        "#2E0B23",
        "#C4F708"
      ]
    },
    {
      "name": "Prismatic Storm 259",
      "colors": [
        "#45AF39",
        "#6A1A88",
        "#C15F30",
        "#3F5256",
        "#B62294"
      ]
    },
    {
      "name": "Nebula Cascade 260",
      "colors": [
        "#3C5056",
        "#04F0CF",
        "#1C8DB9",
        "#2285C0",
        "#171ACB"
      ]
    },
    {
      "name": "Opal Field 261",
      "colors": [
        "#DF27D3",
        "#D26C8B",
        "#6E5868",
        "#E80533",
        "#8E145D"
      ]
    },
    {
      "name": "Velvet Cascade 262",
      "colors": [
        "#3BC48D",
        "#CABBF2",
        "#E62A26",
        "#FD0C57",
        "#5D87BE"
      ]
    },
    {
      "name": "Saffron Empire 263",
      "colors": [
        "#E8FB7D",
        "#E80074",
        "#91E702",
        "#EC925D",
        "#3E9E60"
      ]
    },
    {
      "name": "Saffron Garden 264",
      "colors": [
        "#ACCA39",
        "#49A720",
        "#A42F98",
        "#B45C2D",
        "#E14620"
      ]
    },
    {
      "name": "Velvet Mirror 265",
      "colors": [
        "#5FD344",
        "#354DAE",
        "#C87FC7",
        "#86F31D",
        "#81F92F"
      ]
    },
    {
      "name": "Nebula Circuit 266",
      "colors": [
        "#B34122",
        "#1387DA",
        "#F9743A",
        "#53F017",
        "#E4A5A7"
      ]
    },
    {
      "name": "Crimson Pulse 267",
      "colors": [
        "#06D29C",
        "#6E1599",
        "#ED933B",
        "#A770FC",
        "#BC21CD"
      ]
    },
    {
      "name": "Crimson Dream 268",
      "colors": [
        "#C5F00D",
        "#027BB3",
        "#FA03C1",
        "#CE728D",
        "#199050"
      ]
    },
    {
      "name": "Crimson Pulse 269",
      "colors": [
        "#990E52",
        "#7678F0",
        "#655F02",
        "#8030DD",
        "#BB8B82"
      ]
    },
    {
      "name": "Iridescent Glow 270",
      "colors": [
        "#FF1365",
        "#2D9B80",
        "#967415",
        "#6988B5",
        "#A4A180"
      ]
    },
    {
      "name": "Nimbus Garden 271",
      "colors": [
        "#52B5A7",
        "#1EC015",
        "#BA41E2",
        "#50EC10",
        "#79A683"
      ]
    },
    {
      "name": "Spectrum Circuit 272",
      "colors": [
        "#8C36FA",
        "#B7402F",
        "#BAF1DC",
        "#E8AD39",
        "#7826E6"
      ]
    },
    {
      "name": "Quantum Cascade 273",
      "colors": [
        "#6FA125",
        "#27B878",
        "#2A3DC3",
        "#F482CF",
        "#D74D1E"
      ]
    },
    {
      "name": "Aurora Field 274",
      "colors": [
        "#92C8BB",
        "#4642B8",
        "#FE1462",
        "#E73D6D",
        "#7EDC41"
      ]
    },
    {
      "name": "Spectrum Cavern 275",
      "colors": [
        "#E9C545",
        "#A7CE3E",
        "#03EB1D",
        "#B8C88B",
        "#F87851"
      ]
    },
    {
      "name": "Solar Nebula 276",
      "colors": [
        "#935737",
        "#34A22F",
        "#D44C3B",
        "#892AC2",
        "#A07595"
      ]
    },
    {
      "name": "Prismatic Mirror 277",
      "colors": [
        "#39C76A",
        "#5FCD65",
        "#542E4B",
        "#AF62E0",
        "#95D925"
      ]
    },
    {
      "name": "Molten Mirror 278",
      "colors": [
        "#13D7EC",
        "#B1A7AB",
        "#C1672C",
        "#1A8CC8",
        "#7D150E"
      ]
    },
    {
      "name": "Iridescent Field 279",
      "colors": [
        "#31036D",
        "#46E671",
        "#C9EA4C",
        "#3B86D4",
        "#88556C"
      ]
    },
    {
      "name": "Lunar Mirror 280",
      "colors": [
        "#C1B362",
        "#1E929D",
        "#C4B031",
        "#D4291B",
        "#FE2214"
      ]
    },
    {
      "name": "Velvet Field 281",
      "colors": [
        "#EDCB1C",
        "#5770F4",
        "#D815D2",
        "#9918CC",
        "#C79BBA"
      ]
    },
    {
      "name": "Iridescent Mirror 282",
      "colors": [
        "#1F6ADA",
        "#F719C3",
        "#1C0D0B",
        "#65D772",
        "#F59BC2"
      ]
    },
    {
      "name": "Crimson Field 283",
      "colors": [
        "#D78676",
        "#2776E1",
        "#D585A7",
        "#8FE68B",
        "#B099DE"
      ]
    },
    {
      "name": "Echo Field 284",
      "colors": [
        "#4C86CC",
        "#791BBC",
        "#408583",
        "#6B37F1",
        "#057477"
      ]
    },
    {
      "name": "Molten Empire 285",
      "colors": [
        "#BC6749",
        "#5FF3DD",
        "#F58B7B",
        "#D9B33B",
        "#B67321"
      ]
    },
    {
      "name": "Verdant Wave 286",
      "colors": [
        "#C31683",
        "#540FB9",
        "#56C219",
        "#DAE076",
        "#D0925D"
      ]
    },
    {
      "name": "Molten Mirror 287",
      "colors": [
        "#1F166D",
        "#6A25D8",
        "#A5F41B",
        "#0B3DBA",
        "#2FD659"
      ]
    },
    {
      "name": "Aurora Field 288",
      "colors": [
        "#42B6D0",
        "#1540CD",
        "#EF69F2",
        "#C7E48A",
        "#4FE2AC"
      ]
    },
    {
      "name": "Vivid Cavern 289",
      "colors": [
        "#0C2F74",
        "#5C02DC",
        "#54DDE2",
        "#265990",
        "#EFDCE8"
      ]
    },
    {
      "name": "Radiant Cavern 290",
      "colors": [
        "#B79640",
        "#E83128",
        "#1CE46C",
        "#A4959A",
        "#C69EF3"
      ]
    },
    {
      "name": "Lunar Fragment 291",
      "colors": [
        "#1A2785",
        "#54B0FD",
        "#39E265",
        "#E29154",
        "#638BE2"
      ]
    },
    {
      "name": "Saffron Mist 292",
      "colors": [
        "#AC9C87",
        "#DEC824",
        "#743D81",
        "#E8E6B6",
        "#A109E3"
      ]
    },
    {
      "name": "Crimson Glow 293",
      "colors": [
        "#2433A0",
        "#5C8467",
        "#F9AF1E",
        "#818E77",
        "#842F68"
      ]
    },
    {
      "name": "Cerulean Circuit 294",
      "colors": [
        "#E06880",
        "#538CCA",
        "#679B4B",
        "#DB655C",
        "#A5EF28"
      ]
    },
    {
      "name": "Obsidian Cascade 295",
      "colors": [
        "#D4D9EF",
        "#5875C1",
        "#E09823",
        "#91AB52",
        "#927C55"
      ]
    },
    {
      "name": "Verdant Pulse 296",
      "colors": [
        "#6BFAB6",
        "#F11161",
        "#81CE7C",
        "#7BD617",
        "#3D92CB"
      ]
    },
    {
      "name": "Cerulean Pulse 297",
      "colors": [
        "#ECDDE3",
        "#9A1711",
        "#383388",
        "#BC0C4E",
        "#C83DE5"
      ]
    },
    {
      "name": "Verdant Storm 298",
      "colors": [
        "#08089F",
        "#D76590",
        "#BAB3DC",
        "#4E8136",
        "#21F0B4"
      ]
    },
    {
      "name": "Saffron Garden 299",
      "colors": [
        "#391723",
        "#902836",
        "#3F1B19",
        "#E8F1DB",
        "#CFD819"
      ]
    },
    {
      "name": "Quantum Cavern 300",
      "colors": [
        "#F82A83",
        "#1F3360",
        "#594DD1",
        "#C6DD68",
        "#3A25B0"
      ]
    },
    {
      "name": "Saffron Empire 301",
      "colors": [
        "#446783",
        "#A97534",
        "#7A81A5",
        "#D78C9D",
        "#426D49"
      ]
    },
    {
      "name": "Opal Cavern 302",
      "colors": [
        "#CB71EC",
        "#B5DEE4",
        "#E4DA5C",
        "#C73EF7",
        "#5ECF76"
      ]
    },
    {
      "name": "Vivid Wave 303",
      "colors": [
        "#A7808B",
        "#24CBB9",
        "#8918D2",
        "#F77B87",
        "#3BD36B"
      ]
    },
    {
      "name": "Iridescent Bloom 304",
      "colors": [
        "#1A9627",
        "#985244",
        "#9442CD",
        "#34ECA8",
        "#0B7B00"
      ]
    },
    {
      "name": "Nimbus Circuit 305",
      "colors": [
        "#92C473",
        "#1F7296",
        "#6ADA4D",
        "#E20A96",
        "#F561BD"
      ]
    },
    {
      "name": "Solar Wave 306",
      "colors": [
        "#34D56C",
        "#E7FFA0",
        "#A4422C",
        "#814972",
        "#B7CDDC"
      ]
    },
    {
      "name": "Verdant Mist 307",
      "colors": [
        "#827028",
        "#1D65BC",
        "#4EF1D8",
        "#92FE20",
        "#FE83AC"
      ]
    },
    {
      "name": "Verdant Dream 308",
      "colors": [
        "#8082B2",
        "#87F6BA",
        "#46E3A9",
        "#1EB20E",
        "#F9E1DC"
      ]
    },
    {
      "name": "Prismatic Cascade 309",
      "colors": [
        "#3092A9",
        "#3085FD",
        "#21C857",
        "#E1C6F8",
        "#869274"
      ]
    },
    {
      "name": "Lunar Valley 310",
      "colors": [
        "#48518E",
        "#F76461",
        "#2F32DF",
        "#5F3561",
        "#F37046"
      ]
    },
    {
      "name": "Saffron Cavern 311",
      "colors": [
        "#CF63E4",
        "#75E1E8",
        "#3408DD",
        "#8E74BF",
        "#970B0D"
      ]
    },
    {
      "name": "Iridescent Empire 312",
      "colors": [
        "#8BCE49",
        "#369F97",
        "#08129B",
        "#C29461",
        "#CD49FA"
      ]
    },
    {
      "name": "Opal Flare 313",
      "colors": [
        "#A40E92",
        "#63E673",
        "#D379B3",
        "#105916",
        "#477BB9"
      ]
    },
    {
      "name": "Verdant Field 314",
      "colors": [
        "#EDC83F",
        "#5AB941",
        "#A8C37C",
        "#1500F2",
        "#1FE4F3"
      ]
    },
    {
      "name": "Spectrum Cascade 315",
      "colors": [
        "#1AB0A3",
        "#6256A2",
        "#535668",
        "#231831",
        "#91247F"
      ]
    },
    {
      "name": "Iridescent Glow 316",
      "colors": [
        "#54C93E",
        "#803A4F",
        "#3945D3",
        "#EB5AD8",
        "#6BF7DD"
      ]
    },
    {
      "name": "Velvet Horizon 317",
      "colors": [
        "#ECFAD6",
        "#4B752E",
        "#EBED40",
        "#CE9F0F",
        "#E91935"
      ]
    },
    {
      "name": "Molten Storm 318",
      "colors": [
        "#D4E3DC",
        "#814102",
        "#DA3C2A",
        "#00A5A4",
        "#E9DC19"
      ]
    },
    {
      "name": "Quantum Pulse 319",
      "colors": [
        "#1AF5C6",
        "#9F1F9C",
        "#D95C1B",
        "#7E6C5F",
        "#F2BFA0"
      ]
    },
    {
      "name": "Echo Cascade 320",
      "colors": [
        "#DC617B",
        "#63DE2F",
        "#E339C0",
        "#F20A5C",
        "#5A190A"
      ]
    },
    {
      "name": "Prismatic Empire 321",
      "colors": [
        "#EB123B",
        "#D72A92",
        "#521C37",
        "#30AB36",
        "#A48E69"
      ]
    },
    {
      "name": "Spectrum Storm 322",
      "colors": [
        "#F96E3C",
        "#C0A433",
        "#CE993E",
        "#3FB911",
        "#525284"
      ]
    },
    {
      "name": "Vivid Nebula 323",
      "colors": [
        "#030ABB",
        "#985BBE",
        "#59B6F9",
        "#4930B9",
        "#3628AD"
      ]
    },
    {
      "name": "Vivid Dream 324",
      "colors": [
        "#F45B4A",
        "#2E892B",
        "#B03C32",
        "#0EAC40",
        "#586EEF"
      ]
    },
    {
      "name": "Spectrum Mist 325",
      "colors": [
        "#BADCBA",
        "#516CE6",
        "#5BF90E",
        "#3C05C1",
        "#8E5473"
      ]
    },
    {
      "name": "Nimbus Dream 326",
      "colors": [
        "#AD4D4C",
        "#BA92F8",
        "#FF5B52",
        "#5CE666",
        "#FABE5E"
      ]
    },
    {
      "name": "Saffron Glow 327",
      "colors": [
        "#9BA4D4",
        "#319E63",
        "#343C3B",
        "#DD4BA9",
        "#1168A2"
      ]
    },
    {
      "name": "Quantum Circuit 328",
      "colors": [
        "#534DF3",
        "#991ED3",
        "#4320B9",
        "#FA3B64",
        "#2C21EB"
      ]
    },
    {
      "name": "Lunar Cavern 329",
      "colors": [
        "#153D82",
        "#57A787",
        "#C5BF8B",
        "#8B8BEE",
        "#38C362"
      ]
    },
    {
      "name": "Opal Field 330",
      "colors": [
        "#4903DC",
        "#93EAF0",
        "#BE947C",
        "#6B98CB",
        "#6ACF0D"
      ]
    },
    {
      "name": "Verdant Mist 331",
      "colors": [
        "#0C29C7",
        "#1F76B2",
        "#E3CAD0",
        "#A70E77",
        "#B1329B"
      ]
    },
    {
      "name": "Aurora Dream 332",
      "colors": [
        "#C7614E",
        "#9EB39D",
        "#0A5616",
        "#3310F4",
        "#08C53D"
      ]
    },
    {
      "name": "Vivid Bloom 333",
      "colors": [
        "#672FD0",
        "#F01F66",
        "#1267EA",
        "#8BA26C",
        "#5637C8"
      ]
    },
    {
      "name": "Velvet Dream 334",
      "colors": [
        "#E15323",
        "#A987E4",
        "#78FB70",
        "#9C5AB6",
        "#958BD9"
      ]
    },
    {
      "name": "Nimbus Empire 335",
      "colors": [
        "#BF8D9A",
        "#E19376",
        "#628763",
        "#41D605",
        "#D57782"
      ]
    },
    {
      "name": "Opal Flare 336",
      "colors": [
        "#97C417",
        "#4415D8",
        "#29AF7D",
        "#46CDA1",
        "#5430DA"
      ]
    },
    {
      "name": "Verdant Garden 337",
      "colors": [
        "#1E6DBF",
        "#6EE576",
        "#437A99",
        "#239A3A",
        "#50FAC8"
      ]
    },
    {
      "name": "Verdant Dream 338",
      "colors": [
        "#217E16",
        "#2256E3",
        "#C2157C",
        "#266909",
        "#AB657B"
      ]
    },
    {
      "name": "Crimson Field 339",
      "colors": [
        "#0A3E49",
        "#8C0168",
        "#FDF617",
        "#FDD681",
        "#ECB610"
      ]
    },
    {
      "name": "Iridescent Wave 340",
      "colors": [
        "#455827",
        "#00B70B",
        "#18E424",
        "#7B84ED",
        "#3B64F6"
      ]
    },
    {
      "name": "Obsidian Garden 341",
      "colors": [
        "#7793A0",
        "#F36FF6",
        "#6B491B",
        "#6ECE5B",
        "#6E6DD2"
      ]
    },
    {
      "name": "Nimbus Field 342",
      "colors": [
        "#C76FDD",
        "#228A1C",
        "#DAB25A",
        "#FF5602",
        "#79BDAF"
      ]
    },
    {
      "name": "Solar Cascade 343",
      "colors": [
        "#47F822",
        "#A4BC4A",
        "#B6232B",
        "#649ACE",
        "#2CDD4A"
      ]
    },
    {
      "name": "Verdant Garden 344",
      "colors": [
        "#140CA5",
        "#AAEFCE",
        "#6E557B",
        "#30743C",
        "#A0CEAA"
      ]
    },
    {
      "name": "Radiant Nebula 345",
      "colors": [
        "#27EC50",
        "#6894F9",
        "#32FC38",
        "#9D98BB",
        "#6F8CFA"
      ]
    },
    {
      "name": "Iridescent Cascade 346",
      "colors": [
        "#2BB352",
        "#B928FF",
        "#C1EA32",
        "#472FF4",
        "#2FC340"
      ]
    },
    {
      "name": "Prismatic Cascade 347",
      "colors": [
        "#3A106C",
        "#D0D6A1",
        "#C875F3",
        "#DFFA99",
        "#7AE250"
      ]
    },
    {
      "name": "Opal Field 348",
      "colors": [
        "#C1FAEF",
        "#965F9C",
        "#585C8B",
        "#F7674E",
        "#43FEDC"
      ]
    },
    {
      "name": "Verdant Cavern 349",
      "colors": [
        "#28B5CD",
        "#D9331A",
        "#661F84",
        "#EEBD76",
        "#4B8257"
      ]
    },
    {
      "name": "Radiant Realm 350",
      "colors": [
        "#505EE3",
        "#EAB09D",
        "#84CCE4",
        "#FE5E85",
        "#626643"
      ]
    },
    {
      "name": "Nebula Flare 351",
      "colors": [
        "#468CFE",
        "#FD7C38",
        "#C14832",
        "#9A0F0B",
        "#EAE5F6"
      ]
    },
    {
      "name": "Lunar Realm 352",
      "colors": [
        "#5F1EEB",
        "#B02322",
        "#0119D8",
        "#20E83F",
        "#4DA0E3"
      ]
    },
    {
      "name": "Vivid Field 353",
      "colors": [
        "#F5D75C",
        "#355748",
        "#97F604",
        "#8307CF",
        "#CA8660"
      ]
    },
    {
      "name": "Nimbus Dream 354",
      "colors": [
        "#EEED5B",
        "#D644D3",
        "#D695E4",
        "#26F2C1",
        "#CE1628"
      ]
    },
    {
      "name": "Nimbus Empire 355",
      "colors": [
        "#25E3B2",
        "#EC26F1",
        "#EB9954",
        "#E1CE02",
        "#44ABFA"
      ]
    },
    {
      "name": "Obsidian Mist 356",
      "colors": [
        "#47933A",
        "#33EBE1",
        "#05406D",
        "#F224D1",
        "#758FAB"
      ]
    },
    {
      "name": "Saffron Dream 357",
      "colors": [
        "#F41EF8",
        "#FC8C95",
        "#B11828",
        "#93C80E",
        "#B4B31A"
      ]
    },
    {
      "name": "Obsidian Empire 358",
      "colors": [
        "#B15AFD",
        "#87497A",
        "#C06250",
        "#30E3A2",
        "#1B8860"
      ]
    },
    {
      "name": "Nimbus Flare 359",
      "colors": [
        "#8E6001",
        "#3E59CE",
        "#11E164",
        "#E13ADC",
        "#EE12D5"
      ]
    },
    {
      "name": "Nimbus Cavern 360",
      "colors": [
        "#2BF774",
        "#DEAB94",
        "#B172EB",
        "#8A6E75",
        "#8E880E"
      ]
    },
    {
      "name": "Echo Bloom 361",
      "colors": [
        "#2B42F4",
        "#26E0CB",
        "#1FEAB3",
        "#29E5EE",
        "#C25692"
      ]
    },
    {
      "name": "Molten Flare 362",
      "colors": [
        "#846B2D",
        "#5A47BD",
        "#53950F",
        "#BC8DDA",
        "#AA5B06"
      ]
    },
    {
      "name": "Radiant Storm 363",
      "colors": [
        "#22DF38",
        "#EDEAF6",
        "#ACE3B9",
        "#09DB8D",
        "#F532A0"
      ]
    },
    {
      "name": "Spectrum Flare 364",
      "colors": [
        "#41A35A",
        "#2C67B3",
        "#6DBFE8",
        "#C75330",
        "#764B0B"
      ]
    },
    {
      "name": "Opal Wave 365",
      "colors": [
        "#96C7EC",
        "#DF7ECE",
        "#2627E3",
        "#B5FD74",
        "#7746B7"
      ]
    },
    {
      "name": "Quantum Mist 366",
      "colors": [
        "#F26096",
        "#D1FE39",
        "#DB7827",
        "#6745B8",
        "#5B2ED5"
      ]
    },
    {
      "name": "Velvet Storm 367",
      "colors": [
        "#C45474",
        "#04E4E9",
        "#7F51B1",
        "#586CE6",
        "#B00944"
      ]
    },
    {
      "name": "Velvet Cascade 368",
      "colors": [
        "#CDB9E1",
        "#68F468",
        "#3E59D2",
        "#690472",
        "#27A879"
      ]
    },
    {
      "name": "Opal Cascade 369",
      "colors": [
        "#92F48E",
        "#AABACE",
        "#CEFFE2",
        "#7726FF",
        "#ADFDE0"
      ]
    },
    {
      "name": "Aurora Cavern 370",
      "colors": [
        "#7228BA",
        "#8DAF5E",
        "#E2C194",
        "#2A93D3",
        "#BE06AE"
      ]
    },
    {
      "name": "Cerulean Cavern 371",
      "colors": [
        "#F02DBD",
        "#5B3C0A",
        "#8F6F41",
        "#CD7290",
        "#CAEA8D"
      ]
    },
    {
      "name": "Velvet Empire 372",
      "colors": [
        "#D7E3BE",
        "#3DDE11",
        "#AB220A",
        "#F424E5",
        "#A3453E"
      ]
    },
    {
      "name": "Crimson Glow 373",
      "colors": [
        "#1E619A",
        "#EA1803",
        "#A53800",
        "#11DAE6",
        "#433B4C"
      ]
    },
    {
      "name": "Nebula Nebula 374",
      "colors": [
        "#F0E1AB",
        "#5BB525",
        "#689B35",
        "#41D9BA",
        "#046EE2"
      ]
    },
    {
      "name": "Lunar Flare 375",
      "colors": [
        "#647C1F",
        "#FFD137",
        "#4A3BB7",
        "#E557E3",
        "#01BBC3"
      ]
    },
    {
      "name": "Lunar Horizon 376",
      "colors": [
        "#A59A56",
        "#5C1D1A",
        "#B3FCB7",
        "#3A4FF4",
        "#BCA8FE"
      ]
    },
    {
      "name": "Obsidian Horizon 377",
      "colors": [
        "#6AC519",
        "#8CC99F",
        "#DB2376",
        "#E67CB4",
        "#62C3F7"
      ]
    },
    {
      "name": "Quantum Cascade 378",
      "colors": [
        "#CCBBAD",
        "#03A9A9",
        "#0722C2",
        "#0E5CC0",
        "#46C2AC"
      ]
    },
    {
      "name": "Obsidian Nebula 379",
      "colors": [
        "#7557C1",
        "#1F1A88",
        "#745410",
        "#1045D7",
        "#E79E2F"
      ]
    },
    {
      "name": "Obsidian Valley 380",
      "colors": [
        "#A2D7FA",
        "#D40D03",
        "#527E07",
        "#06A2DD",
        "#A5FB01"
      ]
    },
    {
      "name": "Prismatic Mirror 381",
      "colors": [
        "#4BA2BD",
        "#E88C9F",
        "#450E6C",
        "#58B402",
        "#034CCD"
      ]
    },
    {
      "name": "Crimson Field 382",
      "colors": [
        "#83F870",
        "#6442D7",
        "#8C134B",
        "#6FE13A",
        "#6EE1F2"
      ]
    },
    {
      "name": "Opal Fragment 383",
      "colors": [
        "#F79EEA",
        "#DBB9B3",
        "#7DDD58",
        "#6582AE",
        "#E2B935"
      ]
    },
    {
      "name": "Prismatic Horizon 384",
      "colors": [
        "#35A115",
        "#73197D",
        "#80BDEB",
        "#E86E44",
        "#53BF7B"
      ]
    },
    {
      "name": "Crimson Mirror 385",
      "colors": [
        "#08B6DD",
        "#312533",
        "#7C103A",
        "#A60209",
        "#77AE21"
      ]
    },
    {
      "name": "Spectrum Field 386",
      "colors": [
        "#BE1AFA",
        "#965D33",
        "#4D11AE",
        "#094843",
        "#3650D4"
      ]
    },
    {
      "name": "Nebula Horizon 387",
      "colors": [
        "#5F0D67",
        "#4B3E97",
        "#F78132",
        "#70ADDA",
        "#8A4875"
      ]
    },
    {
      "name": "Nebula Field 388",
      "colors": [
        "#5CDC4C",
        "#7DB6FF",
        "#7C8CD4",
        "#EF00F8",
        "#7E4796"
      ]
    },
    {
      "name": "Nebula Cavern 389",
      "colors": [
        "#535E97",
        "#E80D82",
        "#E32504",
        "#DBB99E",
        "#6810D8"
      ]
    },
    {
      "name": "Spectrum Empire 390",
      "colors": [
        "#497029",
        "#217C4E",
        "#0FAD6B",
        "#F29648",
        "#207018"
      ]
    },
    {
      "name": "Molten Storm 391",
      "colors": [
        "#50CC6F",
        "#221425",
        "#71B516",
        "#73CA5F",
        "#78C118"
      ]
    },
    {
      "name": "Prismatic Mirror 392",
      "colors": [
        "#8C978C",
        "#204B8A",
        "#D90E2C",
        "#C60423",
        "#486FD4"
      ]
    },
    {
      "name": "Vivid Garden 393",
      "colors": [
        "#0627B6",
        "#4397C4",
        "#8701C7",
        "#037286",
        "#DBD63D"
      ]
    },
    {
      "name": "Lunar Garden 394",
      "colors": [
        "#539ED0",
        "#20FDBD",
        "#EB6E28",
        "#83D7E2",
        "#B44009"
      ]
    },
    {
      "name": "Nebula Empire 395",
      "colors": [
        "#ED3B80",
        "#87F7E5",
        "#205A3A",
        "#F22775",
        "#4E3599"
      ]
    },
    {
      "name": "Vivid Realm 396",
      "colors": [
        "#6F8CFB",
        "#17DD75",
        "#63F49A",
        "#7E6A94",
        "#BDCDCF"
      ]
    },
    {
      "name": "Prismatic Cascade 397",
      "colors": [
        "#7315FA",
        "#0BEF23",
        "#58325B",
        "#A7ADB6",
        "#7B4E4F"
      ]
    },
    {
      "name": "Saffron Fragment 398",
      "colors": [
        "#9EDA94",
        "#FF3E8E",
        "#A11902",
        "#F895F0",
        "#A3A92E"
      ]
    },
    {
      "name": "Solar Circuit 399",
      "colors": [
        "#BA781C",
        "#06A60E",
        "#650119",
        "#354D0E",
        "#7CB916"
      ]
    },
    {
      "name": "Cerulean Garden 400",
      "colors": [
        "#5C2130",
        "#9C4E87",
        "#66C6BB",
        "#D72499",
        "#0026D8"
      ]
    },
    {
      "name": "Echo Dream 401",
      "colors": [
        "#32D822",
        "#A94064",
        "#62BEDF",
        "#116030",
        "#B03184"
      ]
    },
    {
      "name": "Aurora Glow 402",
      "colors": [
        "#650CAF",
        "#92D36B",
        "#C32F01",
        "#5F6DD3",
        "#765182"
      ]
    },
    {
      "name": "Aurora Nebula 403",
      "colors": [
        "#393E51",
        "#07DDC8",
        "#941CE8",
        "#A21D0F",
        "#4DCF6C"
      ]
    },
    {
      "name": "Verdant Circuit 404",
      "colors": [
        "#36F3C0",
        "#519873",
        "#1A7639",
        "#B59FA0",
        "#42C074"
      ]
    },
    {
      "name": "Prismatic Garden 405",
      "colors": [
        "#245F99",
        "#39F4C6",
        "#C1DC01",
        "#BEC0F2",
        "#9A0715"
      ]
    },
    {
      "name": "Spectrum Dream 406",
      "colors": [
        "#8A86EB",
        "#33A83A",
        "#331591",
        "#0C227E",
        "#89D379"
      ]
    },
    {
      "name": "Obsidian Empire 407",
      "colors": [
        "#C31BE5",
        "#F319A5",
        "#CBCDF7",
        "#E72AE5",
        "#FB8B77"
      ]
    },
    {
      "name": "Spectrum Realm 408",
      "colors": [
        "#954115",
        "#56321B",
        "#FDA81B",
        "#8F5C70",
        "#26B87E"
      ]
    },
    {
      "name": "Prismatic Cavern 409",
      "colors": [
        "#A780CA",
        "#4B1AFA",
        "#A75527",
        "#43D2D4",
        "#47DB90"
      ]
    },
    {
      "name": "Prismatic Cavern 410",
      "colors": [
        "#B38B4B",
        "#77BDDA",
        "#4649EF",
        "#C04871",
        "#6ABA9E"
      ]
    },
    {
      "name": "Verdant Nebula 411",
      "colors": [
        "#FFC7A8",
        "#DA2D40",
        "#0EF272",
        "#1606AD",
        "#D0A5D2"
      ]
    },
    {
      "name": "Lunar Flare 412",
      "colors": [
        "#821E68",
        "#87D34E",
        "#FCA4D3",
        "#B1751C",
        "#85760A"
      ]
    },
    {
      "name": "Lunar Valley 413",
      "colors": [
        "#C85FFB",
        "#05D7E2",
        "#29B764",
        "#AAE3C7",
        "#B75D76"
      ]
    },
    {
      "name": "Velvet Cascade 414",
      "colors": [
        "#EB6B5A",
        "#002DF5",
        "#302DAB",
        "#BCBECD",
        "#34605D"
      ]
    },
    {
      "name": "Crimson Flare 415",
      "colors": [
        "#413242",
        "#6424EC",
        "#2D74DF",
        "#A3595A",
        "#DA364B"
      ]
    },
    {
      "name": "Obsidian Empire 416",
      "colors": [
        "#782F69",
        "#C4865F",
        "#2E599A",
        "#3B6972",
        "#5C0CD2"
      ]
    },
    {
      "name": "Verdant Dream 417",
      "colors": [
        "#9C66FA",
        "#B88E54",
        "#0E1399",
        "#7BA5B8",
        "#1D18AD"
      ]
    },
    {
      "name": "Nimbus Mirror 418",
      "colors": [
        "#001FB2",
        "#3E6AAD",
        "#388472",
        "#91F6EC",
        "#0E53D4"
      ]
    },
    {
      "name": "Opal Wave 419",
      "colors": [
        "#9DACBB",
        "#452C10",
        "#3EA1AE",
        "#394ADE",
        "#ABF641"
      ]
    },
    {
      "name": "Quantum Nebula 420",
      "colors": [
        "#DD921A",
        "#233EAF",
        "#F51811",
        "#089E3A",
        "#D70CB0"
      ]
    },
    {
      "name": "Quantum Mist 421",
      "colors": [
        "#DFD6D7",
        "#52C874",
        "#4CAC9D",
        "#3B0B8F",
        "#CD96B4"
      ]
    },
    {
      "name": "Radiant Fragment 422",
      "colors": [
        "#993007",
        "#8D78A1",
        "#82DBE4",
        "#4A4F7F",
        "#0EB45F"
      ]
    },
    {
      "name": "Obsidian Storm 423",
      "colors": [
        "#8ACD01",
        "#020214",
        "#67FA37",
        "#5DAC27",
        "#46D233"
      ]
    },
    {
      "name": "Obsidian Horizon 424",
      "colors": [
        "#274E10",
        "#B461DC",
        "#5D77DC",
        "#D875F0",
        "#1FA638"
      ]
    },
    {
      "name": "Opal Empire 425",
      "colors": [
        "#C5DB35",
        "#0063EC",
        "#6297C4",
        "#149455",
        "#3246BA"
      ]
    },
    {
      "name": "Opal Flare 426",
      "colors": [
        "#051011",
        "#8E8527",
        "#FC7647",
        "#45F01C",
        "#5F21C0"
      ]
    },
    {
      "name": "Iridescent Storm 427",
      "colors": [
        "#3F9C6F",
        "#9266A4",
        "#20499E",
        "#F05BEF",
        "#5D0738"
      ]
    },
    {
      "name": "Prismatic Pulse 428",
      "colors": [
        "#1634C8",
        "#7443FD",
        "#623799",
        "#2FCF1C",
        "#AD62A4"
      ]
    },
    {
      "name": "Aurora Fragment 429",
      "colors": [
        "#7C057B",
        "#F48F24",
        "#B7320D",
        "#4D713C",
        "#E87CB4"
      ]
    },
    {
      "name": "Aurora Flare 430",
      "colors": [
        "#49D5B7",
        "#EDAFDF",
        "#1F1B60",
        "#B466E5",
        "#C574EE"
      ]
    },
    {
      "name": "Spectrum Glow 431",
      "colors": [
        "#5BFB1B",
        "#6E4D9C",
        "#546727",
        "#EAD3F0",
        "#CF135E"
      ]
    },
    {
      "name": "Nimbus Wave 432",
      "colors": [
        "#017A3F",
        "#65F578",
        "#82BDA8",
        "#A58622",
        "#6D0003"
      ]
    },
    {
      "name": "Velvet Field 433",
      "colors": [
        "#76766A",
        "#27DF74",
        "#04EC83",
        "#6AE49B",
        "#BD7D35"
      ]
    },
    {
      "name": "Vivid Storm 434",
      "colors": [
        "#A5C84C",
        "#F5A63F",
        "#B74E32",
        "#712730",
        "#6C81A2"
      ]
    },
    {
      "name": "Lunar Dream 435",
      "colors": [
        "#278A1B",
        "#EB7A58",
        "#FCD80A",
        "#F005B8",
        "#70A812"
      ]
    },
    {
      "name": "Vivid Mirror 436",
      "colors": [
        "#03F178",
        "#107D4E",
        "#A53AED",
        "#031999",
        "#709844"
      ]
    },
    {
      "name": "Nebula Dream 437",
      "colors": [
        "#950D08",
        "#A0B3F1",
        "#D2442C",
        "#F6E7F6",
        "#0C5260"
      ]
    },
    {
      "name": "Radiant Realm 438",
      "colors": [
        "#F3B248",
        "#46F9F6",
        "#DF1AB6",
        "#E4573A",
        "#B94AD3"
      ]
    },
    {
      "name": "Echo Flare 439",
      "colors": [
        "#FFB57A",
        "#7684AB",
        "#3E3427",
        "#C0135E",
        "#F7274D"
      ]
    },
    {
      "name": "Cerulean Valley 440",
      "colors": [
        "#DF785D",
        "#5E04DF",
        "#712D8E",
        "#B816C7",
        "#16E450"
      ]
    },
    {
      "name": "Molten Storm 441",
      "colors": [
        "#D25413",
        "#F10798",
        "#CE2085",
        "#429B5A",
        "#3A8FA7"
      ]
    },
    {
      "name": "Verdant Circuit 442",
      "colors": [
        "#FFE712",
        "#6DAAB1",
        "#3B1D58",
        "#E519C2",
        "#D8A956"
      ]
    },
    {
      "name": "Cerulean Glow 443",
      "colors": [
        "#64D32F",
        "#B90F00",
        "#6C5955",
        "#9464D8",
        "#1CD7C8"
      ]
    },
    {
      "name": "Nimbus Bloom 444",
      "colors": [
        "#9D8D92",
        "#E4616B",
        "#C72DB6",
        "#A34AE8",
        "#1F795C"
      ]
    },
    {
      "name": "Prismatic Cavern 445",
      "colors": [
        "#A9CB24",
        "#70ECAC",
        "#A9461F",
        "#2B099F",
        "#DFEB22"
      ]
    },
    {
      "name": "Velvet Wave 446",
      "colors": [
        "#D4F3C6",
        "#46D239",
        "#B9084F",
        "#45D163",
        "#DBC82C"
      ]
    },
    {
      "name": "Velvet Bloom 447",
      "colors": [
        "#98E12D",
        "#35E56C",
        "#BD9669",
        "#905CC1",
        "#9E9EFD"
      ]
    },
    {
      "name": "Solar Cascade 448",
      "colors": [
        "#8F8562",
        "#CC8463",
        "#32E684",
        "#134FA1",
        "#8A4605"
      ]
    },
    {
      "name": "Aurora Realm 449",
      "colors": [
        "#158433",
        "#2E78A7",
        "#ECE7DA",
        "#C1F281",
        "#13CBDC"
      ]
    },
    {
      "name": "Quantum Empire 450",
      "colors": [
        "#C8DFF7",
        "#B078A7",
        "#F0A5EF",
        "#FBD31F",
        "#A50EB5"
      ]
    },
    {
      "name": "Nebula Circuit 451",
      "colors": [
        "#F5808D",
        "#8C5B1A",
        "#BBB226",
        "#449354",
        "#F92737"
      ]
    },
    {
      "name": "Solar Storm 452",
      "colors": [
        "#DAADE1",
        "#887123",
        "#731140",
        "#B403E4",
        "#CFE159"
      ]
    },
    {
      "name": "Solar Cavern 453",
      "colors": [
        "#171B04",
        "#843655",
        "#EF8CC4",
        "#A2B39A",
        "#E02111"
      ]
    },
    {
      "name": "Aurora Cavern 454",
      "colors": [
        "#87268B",
        "#46C995",
        "#84210D",
        "#BC7F58",
        "#3FE4A5"
      ]
    },
    {
      "name": "Nebula Pulse 455",
      "colors": [
        "#D479D1",
        "#FFE58D",
        "#743B05",
        "#206FE8",
        "#263ABB"
      ]
    },
    {
      "name": "Obsidian Field 456",
      "colors": [
        "#906F02",
        "#EFB33E",
        "#ABE79E",
        "#C5F471",
        "#F483F5"
      ]
    },
    {
      "name": "Opal Glow 457",
      "colors": [
        "#252034",
        "#FF6E01",
        "#D55682",
        "#FE1335",
        "#178A4F"
      ]
    },
    {
      "name": "Molten Fragment 458",
      "colors": [
        "#7B8636",
        "#8A9446",
        "#054D53",
        "#6FEE19",
        "#5F077D"
      ]
    },
    {
      "name": "Radiant Garden 459",
      "colors": [
        "#1A317F",
        "#D93767",
        "#8AB027",
        "#023F64",
        "#85CE0E"
      ]
    },
    {
      "name": "Prismatic Storm 460",
      "colors": [
        "#9DE255",
        "#55D467",
        "#836C86",
        "#3BBE2D",
        "#EBC4D3"
      ]
    },
    {
      "name": "Prismatic Wave 461",
      "colors": [
        "#CC2EBA",
        "#3E8459",
        "#DEA6EA",
        "#277F58",
        "#AC9B77"
      ]
    },
    {
      "name": "Radiant Circuit 462",
      "colors": [
        "#C194F9",
        "#9CE27E",
        "#09AE8D",
        "#217D72",
        "#2DF0AA"
      ]
    },
    {
      "name": "Verdant Glow 463",
      "colors": [
        "#D543C3",
        "#C5A891",
        "#31470C",
        "#217444",
        "#498A03"
      ]
    },
    {
      "name": "Spectrum Flare 464",
      "colors": [
        "#1E8037",
        "#AF28F5",
        "#91F8A1",
        "#A6A6D8",
        "#887FDF"
      ]
    },
    {
      "name": "Velvet Glow 465",
      "colors": [
        "#1B3AE6",
        "#193D71",
        "#45B3CC",
        "#08DFC3",
        "#538AC9"
      ]
    },
    {
      "name": "Iridescent Realm 466",
      "colors": [
        "#9D979B",
        "#7C6C38",
        "#085709",
        "#3BAD01",
        "#76A1CA"
      ]
    },
    {
      "name": "Nebula Glow 467",
      "colors": [
        "#A0B3F0",
        "#731FB7",
        "#24062A",
        "#15CA11",
        "#2939BB"
      ]
    },
    {
      "name": "Aurora Glow 468",
      "colors": [
        "#D65EAB",
        "#80FCB0",
        "#E6FC5C",
        "#D167F8",
        "#26B804"
      ]
    },
    {
      "name": "Cerulean Fragment 469",
      "colors": [
        "#4134AC",
        "#4001BF",
        "#043D3E",
        "#BF5C0C",
        "#254584"
      ]
    },
    {
      "name": "Echo Mirror 470",
      "colors": [
        "#F68305",
        "#D1BDF7",
        "#C85CF1",
        "#A447C4",
        "#74D14C"
      ]
    },
    {
      "name": "Radiant Circuit 471",
      "colors": [
        "#848F82",
        "#CA5704",
        "#A407AB",
        "#080EE1",
        "#2E8677"
      ]
    },
    {
      "name": "Iridescent Flare 472",
      "colors": [
        "#B5C700",
        "#6373C9",
        "#2DCE2A",
        "#36CDC4",
        "#ED6290"
      ]
    },
    {
      "name": "Cerulean Field 473",
      "colors": [
        "#A44915",
        "#0787B3",
        "#F0AA59",
        "#5BF0C5",
        "#606277"
      ]
    },
    {
      "name": "Cerulean Cavern 474",
      "colors": [
        "#4A0194",
        "#EA0960",
        "#07D63C",
        "#AC706B",
        "#0C5EEA"
      ]
    },
    {
      "name": "Quantum Cascade 475",
      "colors": [
        "#D70011",
        "#E6A023",
        "#5387F9",
        "#0F64EB",
        "#7F271A"
      ]
    },
    {
      "name": "Prismatic Cavern 476",
      "colors": [
        "#38335B",
        "#5B2DF0",
        "#EC2654",
        "#031C9D",
        "#3216C4"
      ]
    },
    {
      "name": "Solar Valley 477",
      "colors": [
        "#F10FE4",
        "#B42BC4",
        "#4549BE",
        "#B7E6EA",
        "#831DF5"
      ]
    },
    {
      "name": "Iridescent Realm 478",
      "colors": [
        "#54FBB7",
        "#2B2501",
        "#AC106C",
        "#A378BF",
        "#60D729"
      ]
    },
    {
      "name": "Obsidian Glow 479",
      "colors": [
        "#3D4739",
        "#4D4910",
        "#F2DB9F",
        "#7D362C",
        "#215DBF"
      ]
    },
    {
      "name": "Iridescent Cavern 480",
      "colors": [
        "#F7407C",
        "#2A207B",
        "#7FCCB5",
        "#E87446",
        "#56C3B1"
      ]
    },
    {
      "name": "Verdant Wave 481",
      "colors": [
        "#A54EA5",
        "#ADC43F",
        "#CA13BE",
        "#7AC6D2",
        "#AD35CB"
      ]
    },
    {
      "name": "Iridescent Mirror 482",
      "colors": [
        "#70667E",
        "#0F0F41",
        "#F31AAF",
        "#A7DEA8",
        "#404C2A"
      ]
    },
    {
      "name": "Molten Pulse 483",
      "colors": [
        "#1ED53B",
        "#EA7B9A",
        "#5EEEF5",
        "#AD9754",
        "#F46B9C"
      ]
    },
    {
      "name": "Nebula Empire 484",
      "colors": [
        "#2C9BB5",
        "#6B55BF",
        "#932ED4",
        "#ED4746",
        "#1A4981"
      ]
    },
    {
      "name": "Echo Circuit 485",
      "colors": [
        "#F47373",
        "#5E71C8",
        "#3EA2C1",
        "#7AD9BC",
        "#B24582"
      ]
    },
    {
      "name": "Aurora Dream 486",
      "colors": [
        "#56F74D",
        "#6FBDB4",
        "#E9B619",
        "#E9CE6D",
        "#2430EF"
      ]
    },
    {
      "name": "Quantum Glow 487",
      "colors": [
        "#3E100C",
        "#F7914D",
        "#18F68F",
        "#CB86C8",
        "#4B70AE"
      ]
    },
    {
      "name": "Aurora Empire 488",
      "colors": [
        "#6FB9C9",
        "#ACF58A",
        "#805A8B",
        "#FF1632",
        "#679CAD"
      ]
    },
    {
      "name": "Aurora Glow 489",
      "colors": [
        "#3CB4DB",
        "#50CFA7",
        "#EB762A",
        "#B89FA3",
        "#3DA45E"
      ]
    },
    {
      "name": "Quantum Storm 490",
      "colors": [
        "#CFF587",
        "#267D35",
        "#8A4D51",
        "#A1D6FC",
        "#C93E16"
      ]
    },
    {
      "name": "Radiant Realm 491",
      "colors": [
        "#6DC0B2",
        "#E34B49",
        "#740E5A",
        "#32F4BD",
        "#02E21E"
      ]
    },
    {
      "name": "Quantum Garden 492",
      "colors": [
        "#C4EF25",
        "#C0B9B4",
        "#50AACC",
        "#9A6407",
        "#890E04"
      ]
    },
    {
      "name": "Saffron Realm 493",
      "colors": [
        "#737A8B",
        "#F307AD",
        "#9DC2FD",
        "#46BB32",
        "#27B8C6"
      ]
    },
    {
      "name": "Aurora Realm 494",
      "colors": [
        "#8710D8",
        "#ABA716",
        "#B285F9",
        "#028DC4",
        "#8093BC"
      ]
    },
    {
      "name": "Vivid Horizon 495",
      "colors": [
        "#209480",
        "#463921",
        "#F45C1F",
        "#355209",
        "#4825B5"
      ]
    },
    {
      "name": "Prismatic Cascade 496",
      "colors": [
        "#3059C8",
        "#DF93C7",
        "#A5DF95",
        "#1B3844",
        "#C30A18"
      ]
    },
    {
      "name": "Obsidian Dream 497",
      "colors": [
        "#21D5FB",
        "#4CB086",
        "#2029BF",
        "#382F9C",
        "#9A1BF7"
      ]
    },
    {
      "name": "Cerulean Mirror 498",
      "colors": [
        "#FA2DA8",
        "#88C8D8",
        "#21A59F",
        "#538C09",
        "#8DC8A1"
      ]
    },
    {
      "name": "Vivid Garden 499",
      "colors": [
        "#AF6B17",
        "#51BF90",
        "#637034",
        "#CDBD7A",
        "#5F484E"
      ]
    },
    {
      "name": "Crimson Mirror 500",
      "colors": [
        "#0FEF6C",
        "#ACCA28",
        "#58AFEA",
        "#14F35E",
        "#F9C192"
      ]
    },
    {
      "name": "Lunar Pulse 501",
      "colors": [
        "#D55814",
        "#D1DCCA",
        "#7C0C15",
        "#31CFE4",
        "#CAB712"
      ]
    },
    {
      "name": "Velvet Valley 502",
      "colors": [
        "#0E32BD",
        "#E4F6E5",
        "#D49A16",
        "#456D4C",
        "#08F0C6"
      ]
    },
    {
      "name": "Solar Realm 503",
      "colors": [
        "#D56CCB",
        "#B0941C",
        "#EAB448",
        "#3744BA",
        "#BEE0FA"
      ]
    },
    {
      "name": "Opal Field 504",
      "colors": [
        "#34CA71",
        "#78444C",
        "#CD1419",
        "#D49933",
        "#F825D9"
      ]
    },
    {
      "name": "Velvet Realm 505",
      "colors": [
        "#75D880",
        "#5A4631",
        "#CAD6C9",
        "#4D6BB1",
        "#C02FA6"
      ]
    },
    {
      "name": "Echo Field 506",
      "colors": [
        "#6DE519",
        "#08BFA3",
        "#1DC3BE",
        "#E81780",
        "#C62FDA"
      ]
    },
    {
      "name": "Obsidian Empire 507",
      "colors": [
        "#E7AE84",
        "#6A8C18",
        "#402C79",
        "#DB84CE",
        "#A74445"
      ]
    },
    {
      "name": "Nebula Pulse 508",
      "colors": [
        "#9772F6",
        "#ED4722",
        "#A18156",
        "#03ED25",
        "#65BBD3"
      ]
    },
    {
      "name": "Aurora Flare 509",
      "colors": [
        "#4767B4",
        "#A45286",
        "#D02E2D",
        "#B77432",
        "#44771F"
      ]
    },
    {
      "name": "Quantum Mirror 510",
      "colors": [
        "#D7C3CB",
        "#D2E97C",
        "#D5076D",
        "#8999DE",
        "#0E6ECE"
      ]
    },
    {
      "name": "Radiant Glow 511",
      "colors": [
        "#3ACA60",
        "#F57B2F",
        "#9DC744",
        "#0940B1",
        "#3BDD51"
      ]
    },
    {
      "name": "Solar Realm 512",
      "colors": [
        "#7E935B",
        "#159732",
        "#7BBF80",
        "#3DD243",
        "#D5C5A0"
      ]
    },
    {
      "name": "Cerulean Wave 513",
      "colors": [
        "#95CC81",
        "#82EF33",
        "#9BEF0E",
        "#3E4145",
        "#3E0219"
      ]
    },
    {
      "name": "Nimbus Storm 514",
      "colors": [
        "#15822D",
        "#DE7743",
        "#4D7A67",
        "#A5A857",
        "#E56A4C"
      ]
    },
    {
      "name": "Molten Horizon 515",
      "colors": [
        "#27FC0F",
        "#D5D2CB",
        "#DC9BEC",
        "#F30FC6",
        "#00E8C6"
      ]
    },
    {
      "name": "Nebula Mist 516",
      "colors": [
        "#F58BE1",
        "#651E40",
        "#F0C755",
        "#97CD36",
        "#A154DC"
      ]
    },
    {
      "name": "Lunar Circuit 517",
      "colors": [
        "#8F3339",
        "#FFE4C0",
        "#CB61EC",
        "#B9E8FD",
        "#A41462"
      ]
    },
    {
      "name": "Nebula Garden 518",
      "colors": [
        "#8A5536",
        "#3D5B75",
        "#74C252",
        "#3EE9DF",
        "#961AD4"
      ]
    },
    {
      "name": "Saffron Bloom 519",
      "colors": [
        "#9BAB80",
        "#263411",
        "#4C127F",
        "#16EBD3",
        "#44166F"
      ]
    },
    {
      "name": "Aurora Bloom 520",
      "colors": [
        "#E2ED82",
        "#755F67",
        "#C1D64F",
        "#AC367C",
        "#34FCBC"
      ]
    },
    {
      "name": "Vivid Glow 521",
      "colors": [
        "#D886B2",
        "#89C8CA",
        "#62C4F3",
        "#032EFB",
        "#56019C"
      ]
    },
    {
      "name": "Cerulean Horizon 522",
      "colors": [
        "#4998EE",
        "#55BC23",
        "#4E3A6F",
        "#B65287",
        "#D957DF"
      ]
    },
    {
      "name": "Crimson Dream 523",
      "colors": [
        "#FFBB4D",
        "#DE7EF5",
        "#17E664",
        "#707F00",
        "#E66E18"
      ]
    },
    {
      "name": "Opal Garden 524",
      "colors": [
        "#6940F6",
        "#88DF32",
        "#EC8E5E",
        "#A6087B",
        "#26A4EE"
      ]
    },
    {
      "name": "Vivid Circuit 525",
      "colors": [
        "#AB4161",
        "#131031",
        "#658FC4",
        "#DC512A",
        "#6234BC"
      ]
    },
    {
      "name": "Molten Flare 526",
      "colors": [
        "#752901",
        "#4C56B8",
        "#B84735",
        "#501AB6",
        "#CD6162"
      ]
    },
    {
      "name": "Lunar Dream 527",
      "colors": [
        "#FF572F",
        "#A16450",
        "#F7774D",
        "#D91E02",
        "#D94B20"
      ]
    },
    {
      "name": "Cerulean Garden 528",
      "colors": [
        "#B95A22",
        "#213B1D",
        "#A34362",
        "#CA9D00",
        "#D753D4"
      ]
    },
    {
      "name": "Opal Pulse 529",
      "colors": [
        "#53A432",
        "#5F6DD7",
        "#36FE64",
        "#5E65CC",
        "#3E3EE9"
      ]
    },
    {
      "name": "Prismatic Valley 530",
      "colors": [
        "#DEBDC8",
        "#50072A",
        "#BDA662",
        "#5B38D1",
        "#8E9A6D"
      ]
    },
    {
      "name": "Obsidian Circuit 531",
      "colors": [
        "#D01A99",
        "#FC23AF",
        "#D7BDF8",
        "#210CE6",
        "#27EDD4"
      ]
    },
    {
      "name": "Opal Storm 532",
      "colors": [
        "#386850",
        "#3C2900",
        "#FF4865",
        "#E493E8",
        "#62EF36"
      ]
    },
    {
      "name": "Radiant Wave 533",
      "colors": [
        "#A023B4",
        "#41567F",
        "#D75695",
        "#C68120",
        "#12416A"
      ]
    },
    {
      "name": "Obsidian Flare 534",
      "colors": [
        "#14803E",
        "#A38308",
        "#05E896",
        "#181A84",
        "#1E920C"
      ]
    },
    {
      "name": "Nebula Mirror 535",
      "colors": [
        "#051C97",
        "#74E72E",
        "#0DAC88",
        "#928751",
        "#BC5BC8"
      ]
    },
    {
      "name": "Spectrum Mist 536",
      "colors": [
        "#5EEC29",
        "#1683C2",
        "#0D4BAD",
        "#FD87EB",
        "#88359A"
      ]
    },
    {
      "name": "Echo Storm 537",
      "colors": [
        "#3A5ED1",
        "#535D18",
        "#D4CA48",
        "#C7D9E7",
        "#4E1765"
      ]
    },
    {
      "name": "Aurora Mist 538",
      "colors": [
        "#47419D",
        "#D9CA95",
        "#24894B",
        "#67A1D5",
        "#4258BF"
      ]
    },
    {
      "name": "Solar Glow 539",
      "colors": [
        "#20BA40",
        "#0DC848",
        "#F8B167",
        "#D7B91C",
        "#D6B4B8"
      ]
    },
    {
      "name": "Echo Storm 540",
      "colors": [
        "#CDCD81",
        "#5C3830",
        "#16D90F",
        "#D8F569",
        "#2742C0"
      ]
    },
    {
      "name": "Solar Garden 541",
      "colors": [
        "#228C62",
        "#8B7BC5",
        "#100EE9",
        "#5102A4",
        "#C9917F"
      ]
    },
    {
      "name": "Prismatic Mist 542",
      "colors": [
        "#26AACB",
        "#DC9E0E",
        "#2F15FA",
        "#7F4891",
        "#220250"
      ]
    },
    {
      "name": "Crimson Field 543",
      "colors": [
        "#310660",
        "#390B9F",
        "#1DEFB7",
        "#ED0AFF",
        "#BDEF21"
      ]
    },
    {
      "name": "Aurora Glow 544",
      "colors": [
        "#C3D45E",
        "#399951",
        "#1E79CA",
        "#0D416A",
        "#78E74F"
      ]
    },
    {
      "name": "Spectrum Horizon 545",
      "colors": [
        "#189395",
        "#A9EDB2",
        "#1C1D6F",
        "#64FA33",
        "#BC03E4"
      ]
    },
    {
      "name": "Nebula Wave 546",
      "colors": [
        "#2916DE",
        "#356C47",
        "#9D0872",
        "#09FBDB",
        "#5DEA11"
      ]
    },
    {
      "name": "Lunar Mist 547",
      "colors": [
        "#F73382",
        "#A3C82F",
        "#9CA8B0",
        "#CE93F6",
        "#526206"
      ]
    },
    {
      "name": "Iridescent Realm 548",
      "colors": [
        "#60DE43",
        "#3FAABD",
        "#E49054",
        "#871F30",
        "#DC2C8B"
      ]
    },
    {
      "name": "Vivid Garden 549",
      "colors": [
        "#2E5E32",
        "#47D1FB",
        "#7D4B3B",
        "#122AFA",
        "#C543E4"
      ]
    },
    {
      "name": "Vivid Mist 550",
      "colors": [
        "#588D93",
        "#1B0547",
        "#FC70F3",
        "#C04DA0",
        "#C148AF"
      ]
    },
    {
      "name": "Saffron Pulse 551",
      "colors": [
        "#0F49DE",
        "#AE64A5",
        "#3D5700",
        "#A7A270",
        "#9ACCBA"
      ]
    },
    {
      "name": "Cerulean Mist 552",
      "colors": [
        "#8B37F2",
        "#297EF3",
        "#0A4F35",
        "#8CC097",
        "#9F504A"
      ]
    },
    {
      "name": "Velvet Wave 553",
      "colors": [
        "#EC763C",
        "#D33BB6",
        "#ADA028",
        "#D38852",
        "#3E65D8"
      ]
    },
    {
      "name": "Verdant Mist 554",
      "colors": [
        "#01AA92",
        "#756AF9",
        "#55F0AC",
        "#D2DC7C",
        "#6EB31E"
      ]
    },
    {
      "name": "Nimbus Wave 555",
      "colors": [
        "#F9F52C",
        "#F25FE6",
        "#B1717D",
        "#0F1146",
        "#E39EE6"
      ]
    },
    {
      "name": "Aurora Garden 556",
      "colors": [
        "#EBBF95",
        "#2DC567",
        "#A57BAE",
        "#A9B846",
        "#1CEA4A"
      ]
    },
    {
      "name": "Nimbus Fragment 557",
      "colors": [
        "#720DE0",
        "#C426C0",
        "#DA8CA9",
        "#872D1F",
        "#867D5F"
      ]
    },
    {
      "name": "Solar Horizon 558",
      "colors": [
        "#EF5DAD",
        "#31D7CB",
        "#2D3A2D",
        "#C5B509",
        "#87B087"
      ]
    },
    {
      "name": "Crimson Wave 559",
      "colors": [
        "#3DE895",
        "#3ADF6E",
        "#09370A",
        "#7C9ABE",
        "#D95E58"
      ]
    },
    {
      "name": "Verdant Valley 560",
      "colors": [
        "#061CCD",
        "#B00E9F",
        "#12D963",
        "#DB797D",
        "#24094A"
      ]
    },
    {
      "name": "Saffron Realm 561",
      "colors": [
        "#AB8338",
        "#A59648",
        "#B7EBA0",
        "#B74555",
        "#3C291B"
      ]
    },
    {
      "name": "Prismatic Wave 562",
      "colors": [
        "#B8EEFD",
        "#EE9C81",
        "#73F703",
        "#7394BB",
        "#4B2900"
      ]
    },
    {
      "name": "Opal Storm 563",
      "colors": [
        "#723AC0",
        "#7E960E",
        "#D11709",
        "#DA9C11",
        "#074B31"
      ]
    },
    {
      "name": "Solar Pulse 564",
      "colors": [
        "#31F29C",
        "#E84F11",
        "#08F222",
        "#33CFDF",
        "#548EF2"
      ]
    },
    {
      "name": "Quantum Nebula 565",
      "colors": [
        "#BD41F9",
        "#27EBE7",
        "#6A9121",
        "#AE7F41",
        "#E86C69"
      ]
    },
    {
      "name": "Crimson Bloom 566",
      "colors": [
        "#8AC049",
        "#C4948C",
        "#465152",
        "#CB5DB8",
        "#FE81D0"
      ]
    },
    {
      "name": "Lunar Garden 567",
      "colors": [
        "#F45BA2",
        "#DD8A6B",
        "#2AE8D5",
        "#8B6E88",
        "#407CD6"
      ]
    },
    {
      "name": "Velvet Horizon 568",
      "colors": [
        "#0A0F1A",
        "#69BD33",
        "#10254E",
        "#BEAD72",
        "#8D8A93"
      ]
    },
    {
      "name": "Quantum Realm 569",
      "colors": [
        "#A57FC6",
        "#85469E",
        "#BC7CA7",
        "#1E5ED8",
        "#21B916"
      ]
    },
    {
      "name": "Saffron Circuit 570",
      "colors": [
        "#0B9536",
        "#071AF5",
        "#47789C",
        "#E84DE1",
        "#BE0379"
      ]
    },
    {
      "name": "Molten Nebula 571",
      "colors": [
        "#BE7D6A",
        "#16950D",
        "#5EC71E",
        "#A69F97",
        "#2C076D"
      ]
    },
    {
      "name": "Iridescent Empire 572",
      "colors": [
        "#517E03",
        "#2D444D",
        "#B62C22",
        "#54F9B5",
        "#CDE68D"
      ]
    },
    {
      "name": "Quantum Glow 573",
      "colors": [
        "#B66D70",
        "#E9E1F1",
        "#8A0ACF",
        "#17D4D8",
        "#59C0A9"
      ]
    },
    {
      "name": "Iridescent Empire 574",
      "colors": [
        "#3E9B89",
        "#6FD34A",
        "#22E382",
        "#33A4A6",
        "#80D09E"
      ]
    },
    {
      "name": "Radiant Glow 575",
      "colors": [
        "#7A8194",
        "#C8B13F",
        "#CE5626",
        "#4B17DC",
        "#5EE43E"
      ]
    },
    {
      "name": "Saffron Circuit 576",
      "colors": [
        "#271B62",
        "#71BD4A",
        "#6D546F",
        "#7DDD40",
        "#41CBFA"
      ]
    },
    {
      "name": "Prismatic Pulse 577",
      "colors": [
        "#DB0952",
        "#5C4606",
        "#8D2675",
        "#7BC531",
        "#357547"
      ]
    },
    {
      "name": "Saffron Empire 578",
      "colors": [
        "#8B72DD",
        "#06586B",
        "#8D1D0B",
        "#518E1C",
        "#7AE146"
      ]
    },
    {
      "name": "Velvet Storm 579",
      "colors": [
        "#6841E1",
        "#590E18",
        "#18AC83",
        "#756AD7",
        "#1E2D81"
      ]
    },
    {
      "name": "Cerulean Cavern 580",
      "colors": [
        "#670EE7",
        "#2B7528",
        "#3456A7",
        "#6ED665",
        "#1D9D24"
      ]
    },
    {
      "name": "Solar Nebula 581",
      "colors": [
        "#C5DDF8",
        "#A1902D",
        "#93D915",
        "#839D65",
        "#E40357"
      ]
    },
    {
      "name": "Echo Fragment 582",
      "colors": [
        "#5CB4E2",
        "#B23B4E",
        "#9715EC",
        "#F4C1A0",
        "#7C1E4C"
      ]
    },
    {
      "name": "Prismatic Valley 583",
      "colors": [
        "#860396",
        "#B455FD",
        "#86571D",
        "#F05729",
        "#42CAD8"
      ]
    },
    {
      "name": "Opal Storm 584",
      "colors": [
        "#619C18",
        "#CB02FD",
        "#B9C036",
        "#3EF7EE",
        "#36D39E"
      ]
    },
    {
      "name": "Verdant Realm 585",
      "colors": [
        "#DE35C9",
        "#1FE14B",
        "#89E7D2",
        "#1BF258",
        "#503A78"
      ]
    },
    {
      "name": "Solar Wave 586",
      "colors": [
        "#66944E",
        "#26EC7A",
        "#E83D55",
        "#A1080D",
        "#49B0EC"
      ]
    },
    {
      "name": "Quantum Valley 587",
      "colors": [
        "#8AE7C8",
        "#6BAB86",
        "#2C016A",
        "#63FC0E",
        "#374641"
      ]
    },
    {
      "name": "Radiant Pulse 588",
      "colors": [
        "#2C0861",
        "#6C7F4F",
        "#7D22B6",
        "#BC04BD",
        "#0785DF"
      ]
    },
    {
      "name": "Saffron Mist 589",
      "colors": [
        "#978B0C",
        "#5D735B",
        "#782A10",
        "#FDA328",
        "#F43CD9"
      ]
    },
    {
      "name": "Echo Wave 590",
      "colors": [
        "#879EB8",
        "#7CA7FB",
        "#01EF74",
        "#5CD7CA",
        "#10C0D6"
      ]
    },
    {
      "name": "Lunar Field 591",
      "colors": [
        "#DCD596",
        "#27B4B0",
        "#3B697E",
        "#F4822B",
        "#3BD9B2"
      ]
    },
    {
      "name": "Vivid Horizon 592",
      "colors": [
        "#C60C9D",
        "#A51B7F",
        "#E49080",
        "#69FCA3",
        "#E0CF8B"
      ]
    },
    {
      "name": "Velvet Mirror 593",
      "colors": [
        "#8A54AF",
        "#1EA3F3",
        "#017695",
        "#5D2B39",
        "#040CC9"
      ]
    },
    {
      "name": "Opal Field 594",
      "colors": [
        "#3F4A30",
        "#05669D",
        "#1DCC81",
        "#4F57FD",
        "#FF7504"
      ]
    },
    {
      "name": "Aurora Glow 595",
      "colors": [
        "#FF6AA3",
        "#7F5F1C",
        "#A4AB56",
        "#866D4F",
        "#3FDA34"
      ]
    },
    {
      "name": "Obsidian Fragment 596",
      "colors": [
        "#8EE7B8",
        "#15F278",
        "#FC2DC9",
        "#C795C5",
        "#A6F7DD"
      ]
    },
    {
      "name": "Iridescent Bloom 597",
      "colors": [
        "#F7C4E1",
        "#08E8D4",
        "#5E8F6B",
        "#007D1C",
        "#4526E1"
      ]
    },
    {
      "name": "Opal Mirror 598",
      "colors": [
        "#23BB5E",
        "#7CDD85",
        "#3C65AD",
        "#57519B",
        "#810075"
      ]
    },
    {
      "name": "Iridescent Valley 599",
      "colors": [
        "#35202A",
        "#38067C",
        "#0580C7",
        "#900BFD",
        "#6EC3BF"
      ]
    },
    {
      "name": "Iridescent Fragment 600",
      "colors": [
        "#60E838",
        "#486AEB",
        "#ED6396",
        "#BE0F5C",
        "#521174"
      ]
    },
    {
      "name": "Molten Mirror 601",
      "colors": [
        "#C0F9CF",
        "#3882D7",
        "#018F3B",
        "#5BBC36",
        "#B753D6"
      ]
    },
    {
      "name": "Solar Nebula 602",
      "colors": [
        "#151DC1",
        "#07AFFF",
        "#9C23B2",
        "#9D73B7",
        "#4F6A56"
      ]
    },
    {
      "name": "Iridescent Field 603",
      "colors": [
        "#770B28",
        "#16BB23",
        "#8CBE31",
        "#A539FE",
        "#3ED18C"
      ]
    },
    {
      "name": "Cerulean Wave 604",
      "colors": [
        "#762567",
        "#16CAF4",
        "#6406B9",
        "#200D34",
        "#0AC0A9"
      ]
    },
    {
      "name": "Prismatic Nebula 605",
      "colors": [
        "#4D2083",
        "#0DBB07",
        "#F2D087",
        "#CE1131",
        "#34EE3D"
      ]
    },
    {
      "name": "Iridescent Glow 606",
      "colors": [
        "#A9E6DE",
        "#E912BA",
        "#D8F0BE",
        "#DC1105",
        "#8C7A07"
      ]
    },
    {
      "name": "Aurora Storm 607",
      "colors": [
        "#0A0294",
        "#444A0D",
        "#24719B",
        "#EA7F6A",
        "#FBCE4B"
      ]
    },
    {
      "name": "Lunar Glow 608",
      "colors": [
        "#A69846",
        "#A1BF29",
        "#11A7C6",
        "#5AD01A",
        "#32D8B9"
      ]
    },
    {
      "name": "Prismatic Nebula 609",
      "colors": [
        "#5CDB92",
        "#6D6D50",
        "#5525FB",
        "#83C1D8",
        "#B83235"
      ]
    },
    {
      "name": "Quantum Mist 610",
      "colors": [
        "#5057C7",
        "#C9FFF1",
        "#E42DBC",
        "#BD3D8A",
        "#EEF2E3"
      ]
    },
    {
      "name": "Echo Nebula 611",
      "colors": [
        "#5BEF2A",
        "#374468",
        "#CB133A",
        "#F24591",
        "#B5F268"
      ]
    },
    {
      "name": "Spectrum Mist 612",
      "colors": [
        "#8C7623",
        "#9C43BF",
        "#6985C9",
        "#B1994C",
        "#A5AE37"
      ]
    },
    {
      "name": "Molten Circuit 613",
      "colors": [
        "#F06DDD",
        "#7EA5FB",
        "#EF27AA",
        "#550360",
        "#72B7CC"
      ]
    },
    {
      "name": "Nebula Circuit 614",
      "colors": [
        "#7D5A86",
        "#1758A2",
        "#3AEA67",
        "#736837",
        "#54C909"
      ]
    },
    {
      "name": "Nimbus Mirror 615",
      "colors": [
        "#D38109",
        "#38CDDF",
        "#627F0B",
        "#CA51F0",
        "#61E33B"
      ]
    },
    {
      "name": "Lunar Realm 616",
      "colors": [
        "#08D5D5",
        "#D2D6C5",
        "#6BAE8F",
        "#6F5282",
        "#C816DC"
      ]
    },
    {
      "name": "Nimbus Empire 617",
      "colors": [
        "#19270D",
        "#6B1398",
        "#B04697",
        "#51C1C6",
        "#321CA0"
      ]
    },
    {
      "name": "Echo Dream 618",
      "colors": [
        "#E64733",
        "#89E2E7",
        "#DDB7C1",
        "#9478DC",
        "#E3F67A"
      ]
    },
    {
      "name": "Vivid Realm 619",
      "colors": [
        "#00DD8B",
        "#302C40",
        "#1DD66B",
        "#0B4A38",
        "#140B3F"
      ]
    },
    {
      "name": "Aurora Circuit 620",
      "colors": [
        "#6EBF91",
        "#3410F3",
        "#53F59E",
        "#564F3B",
        "#2B8E45"
      ]
    },
    {
      "name": "Quantum Mirror 621",
      "colors": [
        "#EAD917",
        "#31B850",
        "#243B55",
        "#E92321",
        "#A83D55"
      ]
    },
    {
      "name": "Opal Fragment 622",
      "colors": [
        "#600D65",
        "#CD4699",
        "#ED7569",
        "#7D58CB",
        "#E6B0A1"
      ]
    },
    {
      "name": "Cerulean Nebula 623",
      "colors": [
        "#EA97DB",
        "#E36D6B",
        "#F6ED01",
        "#820AD6",
        "#8CCB52"
      ]
    },
    {
      "name": "Nebula Garden 624",
      "colors": [
        "#D52591",
        "#2FD764",
        "#14FC57",
        "#81633A",
        "#D565B4"
      ]
    },
    {
      "name": "Lunar Empire 625",
      "colors": [
        "#7F65E3",
        "#C8776F",
        "#2629B5",
        "#F1D343",
        "#294CC6"
      ]
    },
    {
      "name": "Echo Realm 626",
      "colors": [
        "#D30C0B",
        "#FAF266",
        "#7CE3C8",
        "#55E1A7",
        "#30DC11"
      ]
    },
    {
      "name": "Crimson Bloom 627",
      "colors": [
        "#7B99EC",
        "#E08D48",
        "#DDDF8C",
        "#176B9E",
        "#8586AB"
      ]
    },
    {
      "name": "Cerulean Valley 628",
      "colors": [
        "#65EF4A",
        "#A31A45",
        "#584378",
        "#74F56E",
        "#AEDA82"
      ]
    },
    {
      "name": "Molten Mirror 629",
      "colors": [
        "#F1765D",
        "#552432",
        "#46B691",
        "#113C7C",
        "#66BB9B"
      ]
    },
    {
      "name": "Iridescent Valley 630",
      "colors": [
        "#4BA387",
        "#112B47",
        "#22F1CF",
        "#B092B6",
        "#39871C"
      ]
    },
    {
      "name": "Iridescent Valley 631",
      "colors": [
        "#573F3D",
        "#8C0241",
        "#3C99BA",
        "#D8D5E4",
        "#93269D"
      ]
    },
    {
      "name": "Radiant Fragment 632",
      "colors": [
        "#06C17A",
        "#ED1176",
        "#951513",
        "#7A8D6D",
        "#5E03A1"
      ]
    },
    {
      "name": "Aurora Mist 633",
      "colors": [
        "#697E95",
        "#F9EDAE",
        "#446312",
        "#BEE631",
        "#95B224"
      ]
    },
    {
      "name": "Echo Bloom 634",
      "colors": [
        "#BE16E4",
        "#33AA80",
        "#690F19",
        "#8BCF8D",
        "#4FB1D6"
      ]
    },
    {
      "name": "Prismatic Glow 635",
      "colors": [
        "#7A9392",
        "#CE4F5D",
        "#A0AA79",
        "#936269",
        "#664BC6"
      ]
    },
    {
      "name": "Vivid Wave 636",
      "colors": [
        "#9A61CE",
        "#D705E0",
        "#5931B2",
        "#A59D6D",
        "#BCB980"
      ]
    },
    {
      "name": "Prismatic Bloom 637",
      "colors": [
        "#2F5A8D",
        "#CD348F",
        "#BE800B",
        "#CEDD64",
        "#02FBA5"
      ]
    },
    {
      "name": "Spectrum Circuit 638",
      "colors": [
        "#D165E4",
        "#E5341C",
        "#56A178",
        "#A814DF",
        "#2DF59D"
      ]
    },
    {
      "name": "Aurora Horizon 639",
      "colors": [
        "#050A98",
        "#6BDF24",
        "#5C8302",
        "#F4D7C9",
        "#82101F"
      ]
    },
    {
      "name": "Cerulean Dream 640",
      "colors": [
        "#883691",
        "#E5F50C",
        "#3BD0BD",
        "#DC5521",
        "#D4E3A7"
      ]
    },
    {
      "name": "Obsidian Realm 641",
      "colors": [
        "#455E96",
        "#0DB415",
        "#9A6ABF",
        "#F276F5",
        "#42F236"
      ]
    },
    {
      "name": "Vivid Pulse 642",
      "colors": [
        "#75F1DC",
        "#78E977",
        "#6DF89A",
        "#ABD2B0",
        "#4F10AD"
      ]
    },
    {
      "name": "Iridescent Realm 643",
      "colors": [
        "#06A65C",
        "#2E9325",
        "#5DBE46",
        "#110E55",
        "#E0B098"
      ]
    },
    {
      "name": "Verdant Pulse 644",
      "colors": [
        "#D36DD8",
        "#18D0B4",
        "#C1A8BB",
        "#D31C4D",
        "#651133"
      ]
    },
    {
      "name": "Saffron Field 645",
      "colors": [
        "#0BF25C",
        "#24B1B4",
        "#F9DFA0",
        "#4C2898",
        "#EF0F70"
      ]
    },
    {
      "name": "Verdant Fragment 646",
      "colors": [
        "#343996",
        "#050DE8",
        "#62CB13",
        "#F677FB",
        "#A63E33"
      ]
    },
    {
      "name": "Echo Pulse 647",
      "colors": [
        "#8F5E14",
        "#B87CBE",
        "#65B6AE",
        "#7A6694",
        "#C422BE"
      ]
    },
    {
      "name": "Nimbus Fragment 648",
      "colors": [
        "#4E225E",
        "#1130F2",
        "#AA2FF4",
        "#05499D",
        "#A515EA"
      ]
    },
    {
      "name": "Cerulean Wave 649",
      "colors": [
        "#641987",
        "#704913",
        "#8BD42E",
        "#DEFD15",
        "#FAA2AA"
      ]
    },
    {
      "name": "Obsidian Flare 650",
      "colors": [
        "#4C08BC",
        "#96B9C7",
        "#C95B5E",
        "#482022",
        "#8240B5"
      ]
    },
    {
      "name": "Nebula Empire 651",
      "colors": [
        "#968B63",
        "#9DA6EC",
        "#E085D1",
        "#57FB44",
        "#085778"
      ]
    },
    {
      "name": "Cerulean Glow 652",
      "colors": [
        "#64F09B",
        "#07BB20",
        "#FAFFB7",
        "#3D7748",
        "#CE7DB2"
      ]
    },
    {
      "name": "Spectrum Horizon 653",
      "colors": [
        "#B89A77",
        "#908594",
        "#C3F64C",
        "#C4B0B9",
        "#68487C"
      ]
    },
    {
      "name": "Prismatic Storm 654",
      "colors": [
        "#B6A42A",
        "#07BEA3",
        "#EA3754",
        "#1A78BD",
        "#275C97"
      ]
    },
    {
      "name": "Spectrum Valley 655",
      "colors": [
        "#8C2877",
        "#803483",
        "#A204C6",
        "#635B53",
        "#A96663"
      ]
    },
    {
      "name": "Nimbus Dream 656",
      "colors": [
        "#612996",
        "#FA69B9",
        "#77F745",
        "#897E13",
        "#B7191C"
      ]
    },
    {
      "name": "Nebula Glow 657",
      "colors": [
        "#939A58",
        "#60A4A9",
        "#3AC816",
        "#EB2123",
        "#8A3314"
      ]
    },
    {
      "name": "Lunar Realm 658",
      "colors": [
        "#A77C0F",
        "#4EC07F",
        "#22FC2C",
        "#9A7A22",
        "#7FE005"
      ]
    },
    {
      "name": "Lunar Circuit 659",
      "colors": [
        "#2A0B93",
        "#6A787D",
        "#D86A5E",
        "#E9BDD2",
        "#318C84"
      ]
    },
    {
      "name": "Iridescent Flare 660",
      "colors": [
        "#0300A6",
        "#B83071",
        "#FD7250",
        "#44F389",
        "#B00879"
      ]
    },
    {
      "name": "Echo Glow 661",
      "colors": [
        "#D52F81",
        "#8C9EDA",
        "#52FBD5",
        "#1467B7",
        "#D18524"
      ]
    },
    {
      "name": "Solar Mist 662",
      "colors": [
        "#2EBB98",
        "#8D4BE4",
        "#B5AED1",
        "#62AB69",
        "#6FE507"
      ]
    },
    {
      "name": "Opal Wave 663",
      "colors": [
        "#B370F0",
        "#782E2E",
        "#CAF4FA",
        "#1BDDDF",
        "#441F7E"
      ]
    },
    {
      "name": "Molten Garden 664",
      "colors": [
        "#55034C",
        "#EB0F0C",
        "#9B3D0F",
        "#282E76",
        "#09A1C0"
      ]
    },
    {
      "name": "Solar Mirror 665",
      "colors": [
        "#65C5C3",
        "#A659F0",
        "#C837E8",
        "#15CD5C",
        "#DC2A4E"
      ]
    },
    {
      "name": "Nebula Empire 666",
      "colors": [
        "#1AF86A",
        "#49B49A",
        "#499000",
        "#103F3C",
        "#B097D2"
      ]
    },
    {
      "name": "Solar Empire 667",
      "colors": [
        "#4757D1",
        "#7977E5",
        "#A4FB87",
        "#0D5EFE",
        "#5465EC"
      ]
    },
    {
      "name": "Opal Valley 668",
      "colors": [
        "#016BDA",
        "#CB2A35",
        "#D34796",
        "#8708C4",
        "#4F916D"
      ]
    },
    {
      "name": "Prismatic Valley 669",
      "colors": [
        "#913455",
        "#990CA2",
        "#09443D",
        "#2ECCC6",
        "#D5BF70"
      ]
    },
    {
      "name": "Vivid Mist 670",
      "colors": [
        "#2599A5",
        "#00CD76",
        "#09F0AF",
        "#DF46D1",
        "#BF8A7A"
      ]
    },
    {
      "name": "Saffron Mist 671",
      "colors": [
        "#DB029A",
        "#57B443",
        "#813C69",
        "#94EA9F",
        "#B191F6"
      ]
    },
    {
      "name": "Verdant Nebula 672",
      "colors": [
        "#13E882",
        "#AE1F20",
        "#12BBA5",
        "#1B720A",
        "#B49833"
      ]
    },
    {
      "name": "Saffron Cavern 673",
      "colors": [
        "#9FD44A",
        "#78A718",
        "#CF1A4C",
        "#D60EB7",
        "#64E004"
      ]
    },
    {
      "name": "Echo Glow 674",
      "colors": [
        "#069C12",
        "#AB58BC",
        "#3EF10E",
        "#DDD452",
        "#D77559"
      ]
    },
    {
      "name": "Velvet Cascade 675",
      "colors": [
        "#D62889",
        "#4AB62D",
        "#E0E40F",
        "#2DD963",
        "#E53D0E"
      ]
    },
    {
      "name": "Solar Empire 676",
      "colors": [
        "#6C3A9A",
        "#EA73EF",
        "#6F1E20",
        "#54457A",
        "#A0EA73"
      ]
    },
    {
      "name": "Opal Garden 677",
      "colors": [
        "#B91743",
        "#AD6F6E",
        "#ECD3C6",
        "#0FD2AB",
        "#FAF0D1"
      ]
    },
    {
      "name": "Radiant Realm 678",
      "colors": [
        "#9CEEB0",
        "#CD5254",
        "#A59BE3",
        "#09AC27",
        "#A130FB"
      ]
    },
    {
      "name": "Crimson Pulse 679",
      "colors": [
        "#F81267",
        "#F3141F",
        "#BE827F",
        "#B29D52",
        "#E8C774"
      ]
    },
    {
      "name": "Obsidian Bloom 680",
      "colors": [
        "#A08BCC",
        "#BDACE0",
        "#79546E",
        "#286249",
        "#5983B0"
      ]
    },
    {
      "name": "Obsidian Wave 681",
      "colors": [
        "#E47AAB",
        "#C5C11C",
        "#295E09",
        "#897C5A",
        "#66B166"
      ]
    },
    {
      "name": "Spectrum Wave 682",
      "colors": [
        "#CF7A6E",
        "#20F1BA",
        "#B0E3A6",
        "#C731A1",
        "#A5AD5F"
      ]
    },
    {
      "name": "Prismatic Wave 683",
      "colors": [
        "#5F4154",
        "#94F930",
        "#7B5313",
        "#277722",
        "#EBE7EA"
      ]
    },
    {
      "name": "Opal Cavern 684",
      "colors": [
        "#7916BE",
        "#C5408F",
        "#3520A9",
        "#4A9356",
        "#A5A9C1"
      ]
    },
    {
      "name": "Vivid Storm 685",
      "colors": [
        "#DF7B3E",
        "#FC4938",
        "#805CE0",
        "#9CF610",
        "#E124CF"
      ]
    },
    {
      "name": "Echo Circuit 686",
      "colors": [
        "#C505A2",
        "#9DAB06",
        "#51D76C",
        "#AC9499",
        "#3920EE"
      ]
    },
    {
      "name": "Radiant Horizon 687",
      "colors": [
        "#EA3124",
        "#8A22D8",
        "#AE699B",
        "#941DFA",
        "#03CE48"
      ]
    },
    {
      "name": "Saffron Bloom 688",
      "colors": [
        "#BF9943",
        "#D99725",
        "#A4D260",
        "#266852",
        "#38C565"
      ]
    },
    {
      "name": "Aurora Fragment 689",
      "colors": [
        "#5D4B7D",
        "#449EAF",
        "#A6C089",
        "#15443B",
        "#774107"
      ]
    },
    {
      "name": "Aurora Bloom 690",
      "colors": [
        "#AB1AB6",
        "#E41473",
        "#05BD64",
        "#3DED25",
        "#75909E"
      ]
    },
    {
      "name": "Velvet Empire 691",
      "colors": [
        "#19B9B4",
        "#1AF45A",
        "#7B3B54",
        "#66B2F4",
        "#5AAD7E"
      ]
    },
    {
      "name": "Iridescent Garden 692",
      "colors": [
        "#84489F",
        "#7F5B91",
        "#FDD9C5",
        "#48DC67",
        "#04B3D1"
      ]
    },
    {
      "name": "Iridescent Cascade 693",
      "colors": [
        "#E47003",
        "#A39B40",
        "#DB71C8",
        "#986EEE",
        "#E34021"
      ]
    },
    {
      "name": "Verdant Realm 694",
      "colors": [
        "#F3B95C",
        "#BF47FC",
        "#6FCB46",
        "#1D8A3D",
        "#ECDD1D"
      ]
    },
    {
      "name": "Spectrum Mirror 695",
      "colors": [
        "#0ECE97",
        "#7C60FC",
        "#2759D6",
        "#325736",
        "#299911"
      ]
    },
    {
      "name": "Crimson Circuit 696",
      "colors": [
        "#39A473",
        "#13EE0F",
        "#6CB49D",
        "#6A42F8",
        "#5C07B6"
      ]
    },
    {
      "name": "Aurora Field 697",
      "colors": [
        "#E030F2",
        "#FE439D",
        "#20A288",
        "#803CAE",
        "#EA7FB7"
      ]
    },
    {
      "name": "Iridescent Horizon 698",
      "colors": [
        "#44E159",
        "#8C73B2",
        "#A849B8",
        "#44D927",
        "#6AE05E"
      ]
    },
    {
      "name": "Prismatic Nebula 699",
      "colors": [
        "#55E823",
        "#9E162F",
        "#D342AF",
        "#D1C3F5",
        "#C71BBC"
      ]
    },
    {
      "name": "Verdant Cavern 700",
      "colors": [
        "#EA3DA2",
        "#D78FD4",
        "#24DDAA",
        "#9E95D7",
        "#93FB40"
      ]
    },
    {
      "name": "Molten Mirror 701",
      "colors": [
        "#74EA77",
        "#AD40DC",
        "#FE513B",
        "#B11013",
        "#A2A055"
      ]
    },
    {
      "name": "Nimbus Flare 702",
      "colors": [
        "#556A20",
        "#1346D4",
        "#705CF3",
        "#8709AD",
        "#4B6A79"
      ]
    },
    {
      "name": "Vivid Pulse 703",
      "colors": [
        "#FE469C",
        "#625203",
        "#6E6A11",
        "#332C69",
        "#75ED65"
      ]
    },
    {
      "name": "Vivid Glow 704",
      "colors": [
        "#BC2474",
        "#AFB4A2",
        "#99C6A8",
        "#60DC01",
        "#B46CCD"
      ]
    },
    {
      "name": "Vivid Flare 705",
      "colors": [
        "#70F73F",
        "#981AD0",
        "#44E32A",
        "#D1F6AF",
        "#682028"
      ]
    },
    {
      "name": "Saffron Cascade 706",
      "colors": [
        "#6D665C",
        "#FD826C",
        "#58FE02",
        "#2912CF",
        "#05119B"
      ]
    },
    {
      "name": "Radiant Wave 707",
      "colors": [
        "#826072",
        "#52DCE4",
        "#CA0E26",
        "#57C629",
        "#C1C3F5"
      ]
    },
    {
      "name": "Molten Fragment 708",
      "colors": [
        "#D9882F",
        "#D79795",
        "#1EB347",
        "#9CB1E9",
        "#DABA99"
      ]
    },
    {
      "name": "Radiant Flare 709",
      "colors": [
        "#C743C1",
        "#9D568A",
        "#0A6ADF",
        "#EF9A84",
        "#DCDCB0"
      ]
    },
    {
      "name": "Vivid Dream 710",
      "colors": [
        "#0038F4",
        "#3B6339",
        "#9CA336",
        "#BECBD0",
        "#EC64FF"
      ]
    },
    {
      "name": "Obsidian Field 711",
      "colors": [
        "#8FB9CE",
        "#B0BE0A",
        "#77C7C8",
        "#59A211",
        "#03872C"
      ]
    },
    {
      "name": "Echo Cascade 712",
      "colors": [
        "#BAECA8",
        "#063514",
        "#59D127",
        "#AC0B54",
        "#2A698E"
      ]
    },
    {
      "name": "Solar Dream 713",
      "colors": [
        "#B78BEA",
        "#2B4D01",
        "#5D0F3A",
        "#F33CCA",
        "#B15A07"
      ]
    },
    {
      "name": "Iridescent Dream 714",
      "colors": [
        "#456199",
        "#E195CC",
        "#C3FD1A",
        "#87EB24",
        "#5E43AB"
      ]
    },
    {
      "name": "Crimson Garden 715",
      "colors": [
        "#29A5DF",
        "#031233",
        "#1DFADD",
        "#3D4467",
        "#3248BC"
      ]
    },
    {
      "name": "Nimbus Field 716",
      "colors": [
        "#1A4CA9",
        "#B459B6",
        "#E7C81F",
        "#D440DD",
        "#3B5998"
      ]
    },
    {
      "name": "Lunar Circuit 717",
      "colors": [
        "#DD03D2",
        "#D44583",
        "#090069",
        "#63BC61",
        "#6CD913"
      ]
    },
    {
      "name": "Crimson Flare 718",
      "colors": [
        "#DD116E",
        "#788553",
        "#4889BE",
        "#F224B0",
        "#98493C"
      ]
    },
    {
      "name": "Spectrum Dream 719",
      "colors": [
        "#6508CF",
        "#723159",
        "#CB050F",
        "#55313C",
        "#E75DEA"
      ]
    },
    {
      "name": "Spectrum Storm 720",
      "colors": [
        "#D23B3D",
        "#7D1248",
        "#6A4D65",
        "#6C0726",
        "#C49188"
      ]
    },
    {
      "name": "Cerulean Mirror 721",
      "colors": [
        "#2D7EC7",
        "#164A6F",
        "#57EF67",
        "#022AE6",
        "#DC1CDC"
      ]
    },
    {
      "name": "Crimson Mirror 722",
      "colors": [
        "#7A22A1",
        "#2140FF",
        "#C9B32F",
        "#6B49FF",
        "#E580E5"
      ]
    },
    {
      "name": "Iridescent Empire 723",
      "colors": [
        "#C17644",
        "#F10BA4",
        "#07C95A",
        "#3531D8",
        "#D373CB"
      ]
    },
    {
      "name": "Radiant Circuit 724",
      "colors": [
        "#409356",
        "#0E6B69",
        "#0B9B24",
        "#450CAB",
        "#4B1CA3"
      ]
    },
    {
      "name": "Vivid Storm 725",
      "colors": [
        "#06F24E",
        "#B92B07",
        "#60905A",
        "#EC0573",
        "#981920"
      ]
    },
    {
      "name": "Verdant Pulse 726",
      "colors": [
        "#6C2709",
        "#F974AA",
        "#F7028F",
        "#8DB1D3",
        "#9EE073"
      ]
    },
    {
      "name": "Cerulean Mirror 727",
      "colors": [
        "#1B6EB4",
        "#712DDB",
        "#0DE8F9",
        "#E4D22C",
        "#0BF477"
      ]
    },
    {
      "name": "Velvet Field 728",
      "colors": [
        "#13072E",
        "#A5067E",
        "#B62099",
        "#FE5B7F",
        "#700BD7"
      ]
    },
    {
      "name": "Cerulean Wave 729",
      "colors": [
        "#17B276",
        "#A0B768",
        "#A40C4F",
        "#A1B346",
        "#3B4579"
      ]
    },
    {
      "name": "Velvet Circuit 730",
      "colors": [
        "#F38D01",
        "#169475",
        "#425F91",
        "#698697",
        "#EFDEAB"
      ]
    },
    {
      "name": "Saffron Garden 731",
      "colors": [
        "#47BEAC",
        "#439B36",
        "#52611A",
        "#12158F",
        "#4F9007"
      ]
    },
    {
      "name": "Vivid Valley 732",
      "colors": [
        "#38A190",
        "#779B61",
        "#E3E0EB",
        "#AA0434",
        "#3A1C3F"
      ]
    },
    {
      "name": "Molten Glow 733",
      "colors": [
        "#29D71D",
        "#6B73A3",
        "#CB14EB",
        "#B7A4D4",
        "#C612EA"
      ]
    },
    {
      "name": "Opal Horizon 734",
      "colors": [
        "#6EF76F",
        "#99E761",
        "#5A60A1",
        "#7A0C3C",
        "#8B5175"
      ]
    },
    {
      "name": "Aurora Circuit 735",
      "colors": [
        "#AC1992",
        "#A48320",
        "#8E2C85",
        "#2A950F",
        "#18D6DC"
      ]
    },
    {
      "name": "Saffron Nebula 736",
      "colors": [
        "#5F9EF2",
        "#92E3CE",
        "#991617",
        "#D0659E",
        "#E95A1C"
      ]
    },
    {
      "name": "Vivid Flare 737",
      "colors": [
        "#D030F9",
        "#91A1AC",
        "#3613DC",
        "#02796E",
        "#E0773D"
      ]
    },
    {
      "name": "Verdant Fragment 738",
      "colors": [
        "#7861C4",
        "#572054",
        "#C0E67A",
        "#1A7AD3",
        "#4E626D"
      ]
    },
    {
      "name": "Vivid Mirror 739",
      "colors": [
        "#8C1404",
        "#3D2AE7",
        "#67CB11",
        "#A41003",
        "#82DD2A"
      ]
    },
    {
      "name": "Verdant Storm 740",
      "colors": [
        "#B3823C",
        "#0AE7A7",
        "#DBDA29",
        "#2B18CA",
        "#12F60F"
      ]
    },
    {
      "name": "Radiant Nebula 741",
      "colors": [
        "#A0AAA9",
        "#B8CAB7",
        "#BD69AB",
        "#63CF38",
        "#309381"
      ]
    },
    {
      "name": "Quantum Bloom 742",
      "colors": [
        "#17F1BE",
        "#B438E8",
        "#E49452",
        "#A4F690",
        "#249D21"
      ]
    },
    {
      "name": "Aurora Bloom 743",
      "colors": [
        "#A37C89",
        "#7F5DC4",
        "#F5AF26",
        "#97F591",
        "#95284E"
      ]
    },
    {
      "name": "Vivid Realm 744",
      "colors": [
        "#57CCB6",
        "#B1DB75",
        "#1B78A3",
        "#1E5974",
        "#06ED06"
      ]
    },
    {
      "name": "Nimbus Garden 745",
      "colors": [
        "#187669",
        "#4DC609",
        "#B6E5D6",
        "#21F387",
        "#CF35A3"
      ]
    },
    {
      "name": "Vivid Flare 746",
      "colors": [
        "#92E248",
        "#DD34D4",
        "#2C8374",
        "#8C5FA8",
        "#4B267C"
      ]
    },
    {
      "name": "Aurora Pulse 747",
      "colors": [
        "#CA09D9",
        "#3B838D",
        "#BED5B7",
        "#ABD3D8",
        "#923EE7"
      ]
    },
    {
      "name": "Aurora Fragment 748",
      "colors": [
        "#F0B6D0",
        "#C3754E",
        "#1965B0",
        "#919C2C",
        "#45EDA5"
      ]
    },
    {
      "name": "Nebula Valley 749",
      "colors": [
        "#FD1C1C",
        "#63CA0E",
        "#BEEBAE",
        "#E9B068",
        "#68F0B0"
      ]
    },
    {
      "name": "Vivid Pulse 750",
      "colors": [
        "#0097E2",
        "#50E775",
        "#E1ACAD",
        "#5C2E99",
        "#DDEA3A"
      ]
    },
    {
      "name": "Vivid Pulse 751",
      "colors": [
        "#394407",
        "#F23A58",
        "#4610CB",
        "#D70DCC",
        "#DFB6AF"
      ]
    },
    {
      "name": "Vivid Glow 752",
      "colors": [
        "#673E95",
        "#46B696",
        "#6008BE",
        "#81F210",
        "#A6ED91"
      ]
    },
    {
      "name": "Spectrum Flare 753",
      "colors": [
        "#57FE8E",
        "#0EF44B",
        "#79A622",
        "#500B4A",
        "#901453"
      ]
    },
    {
      "name": "Echo Realm 754",
      "colors": [
        "#08FF80",
        "#B02F6B",
        "#65458D",
        "#0588AC",
        "#992C2D"
      ]
    },
    {
      "name": "Opal Mirror 755",
      "colors": [
        "#DDA534",
        "#170384",
        "#72960D",
        "#6B03C0",
        "#D32618"
      ]
    },
    {
      "name": "Lunar Wave 756",
      "colors": [
        "#CC3D76",
        "#50A1D8",
        "#795CEA",
        "#6C8EE2",
        "#C109CE"
      ]
    },
    {
      "name": "Lunar Empire 757",
      "colors": [
        "#FB45ED",
        "#F13F04",
        "#E0511E",
        "#34B6B2",
        "#84DAB8"
      ]
    },
    {
      "name": "Spectrum Realm 758",
      "colors": [
        "#041A4F",
        "#5B2B66",
        "#C2361F",
        "#7E9B38",
        "#1C4620"
      ]
    },
    {
      "name": "Obsidian Circuit 759",
      "colors": [
        "#9E8003",
        "#4CBCC0",
        "#8B3BF5",
        "#C15A97",
        "#B2071E"
      ]
    },
    {
      "name": "Molten Nebula 760",
      "colors": [
        "#FAA9B8",
        "#3F8BEC",
        "#027511",
        "#875455",
        "#3E8E91"
      ]
    },
    {
      "name": "Crimson Valley 761",
      "colors": [
        "#275C2C",
        "#14FF23",
        "#B7B4CE",
        "#DBA82B",
        "#FCF50D"
      ]
    },
    {
      "name": "Crimson Circuit 762",
      "colors": [
        "#EFDC7B",
        "#670B64",
        "#E918C5",
        "#83578B",
        "#57C5FA"
      ]
    },
    {
      "name": "Nebula Field 763",
      "colors": [
        "#98C828",
        "#8BDD4C",
        "#DDAAFE",
        "#090B4B",
        "#B9A0EA"
      ]
    },
    {
      "name": "Crimson Pulse 764",
      "colors": [
        "#075459",
        "#9DE2B9",
        "#5571EC",
        "#2795C0",
        "#00E32A"
      ]
    },
    {
      "name": "Iridescent Valley 765",
      "colors": [
        "#436160",
        "#3C3ED6",
        "#1620D5",
        "#1F7685",
        "#FFF46D"
      ]
    },
    {
      "name": "Aurora Mist 766",
      "colors": [
        "#E603CB",
        "#789139",
        "#77EF0D",
        "#7DB17D",
        "#788266"
      ]
    },
    {
      "name": "Spectrum Wave 767",
      "colors": [
        "#11A86E",
        "#34BD29",
        "#734C29",
        "#0418E9",
        "#758081"
      ]
    },
    {
      "name": "Radiant Wave 768",
      "colors": [
        "#4F7CFC",
        "#F5336E",
        "#E81D17",
        "#07AC25",
        "#A9BC83"
      ]
    },
    {
      "name": "Nebula Realm 769",
      "colors": [
        "#89BD88",
        "#340A5B",
        "#387ABD",
        "#76800B",
        "#35690D"
      ]
    },
    {
      "name": "Molten Dream 770",
      "colors": [
        "#F1E107",
        "#EBADD1",
        "#2B7678",
        "#8C5C7F",
        "#073BBC"
      ]
    },
    {
      "name": "Velvet Storm 771",
      "colors": [
        "#3A55A6",
        "#46AD7B",
        "#69EC1D",
        "#01A201",
        "#5AE31E"
      ]
    },
    {
      "name": "Vivid Mist 772",
      "colors": [
        "#6E6E18",
        "#F93890",
        "#4EB639",
        "#25DAD3",
        "#4B9A43"
      ]
    },
    {
      "name": "Nimbus Pulse 773",
      "colors": [
        "#26F449",
        "#48140D",
        "#B344FD",
        "#F9618D",
        "#2186BA"
      ]
    },
    {
      "name": "Molten Fragment 774",
      "colors": [
        "#F3BCC6",
        "#29B16B",
        "#D8C3CC",
        "#3B6840",
        "#061BEB"
      ]
    },
    {
      "name": "Crimson Wave 775",
      "colors": [
        "#8D07B8",
        "#9FB812",
        "#1BC886",
        "#BF40E0",
        "#91C983"
      ]
    },
    {
      "name": "Molten Bloom 776",
      "colors": [
        "#86E276",
        "#14766C",
        "#AE8F10",
        "#753847",
        "#C1D054"
      ]
    },
    {
      "name": "Radiant Storm 777",
      "colors": [
        "#AD2E9A",
        "#2DFE17",
        "#24F60F",
        "#8127E4",
        "#133AD2"
      ]
    },
    {
      "name": "Saffron Nebula 778",
      "colors": [
        "#DDDE7F",
        "#0C3F9A",
        "#606D1A",
        "#770B27",
        "#A05BA0"
      ]
    },
    {
      "name": "Obsidian Circuit 779",
      "colors": [
        "#20AE92",
        "#EA3BFB",
        "#1D1957",
        "#82EFEF",
        "#49CFA7"
      ]
    },
    {
      "name": "Vivid Cascade 780",
      "colors": [
        "#C3033F",
        "#C5CE1D",
        "#3A9134",
        "#098355",
        "#D37589"
      ]
    },
    {
      "name": "Velvet Mist 781",
      "colors": [
        "#33493E",
        "#1A91FD",
        "#3BA5A8",
        "#9A35E6",
        "#C797A8"
      ]
    },
    {
      "name": "Cerulean Cascade 782",
      "colors": [
        "#B3C9A7",
        "#347A8A",
        "#112A81",
        "#671541",
        "#A0D524"
      ]
    },
    {
      "name": "Echo Cavern 783",
      "colors": [
        "#B1EBB4",
        "#48683D",
        "#F21265",
        "#3A388E",
        "#F5FA52"
      ]
    },
    {
      "name": "Nebula Empire 784",
      "colors": [
        "#9A77CB",
        "#81BE0E",
        "#E09E2E",
        "#EF6DD2",
        "#069339"
      ]
    },
    {
      "name": "Spectrum Glow 785",
      "colors": [
        "#F3DB22",
        "#C177DD",
        "#A9FB34",
        "#913826",
        "#65B70C"
      ]
    },
    {
      "name": "Crimson Fragment 786",
      "colors": [
        "#FD1524",
        "#0429FB",
        "#15455E",
        "#DBA94A",
        "#5DAE47"
      ]
    },
    {
      "name": "Quantum Storm 787",
      "colors": [
        "#9861A1",
        "#C792C7",
        "#01D541",
        "#91A4DF",
        "#8FDA04"
      ]
    },
    {
      "name": "Obsidian Valley 788",
      "colors": [
        "#808444",
        "#EFE90C",
        "#A18095",
        "#851FFB",
        "#0E6AE6"
      ]
    },
    {
      "name": "Iridescent Realm 789",
      "colors": [
        "#1671E7",
        "#608A34",
        "#8FF3EA",
        "#3FC6B1",
        "#F7FE48"
      ]
    },
    {
      "name": "Nebula Dream 790",
      "colors": [
        "#3F8F98",
        "#BCE363",
        "#6AED1C",
        "#FF8502",
        "#D814FA"
      ]
    },
    {
      "name": "Solar Cavern 791",
      "colors": [
        "#23C368",
        "#D1C1E7",
        "#4A7243",
        "#DA326B",
        "#E32CC3"
      ]
    },
    {
      "name": "Nimbus Horizon 792",
      "colors": [
        "#D4212A",
        "#A2FED7",
        "#5D0980",
        "#01E448",
        "#D460BB"
      ]
    },
    {
      "name": "Vivid Realm 793",
      "colors": [
        "#E3B168",
        "#844054",
        "#3A394C",
        "#D8EAC3",
        "#0CA1AD"
      ]
    },
    {
      "name": "Vivid Mist 794",
      "colors": [
        "#927D51",
        "#C8DB72",
        "#7C0B37",
        "#9C9519",
        "#93CA44"
      ]
    },
    {
      "name": "Obsidian Nebula 795",
      "colors": [
        "#9E7AFC",
        "#E6351A",
        "#E964BA",
        "#07C0D0",
        "#6AC0C2"
      ]
    },
    {
      "name": "Verdant Storm 796",
      "colors": [
        "#02DAD1",
        "#1D3959",
        "#F90A20",
        "#9F5D81",
        "#35C00F"
      ]
    },
    {
      "name": "Cerulean Empire 797",
      "colors": [
        "#850C91",
        "#7D68E6",
        "#9ACE2F",
        "#065373",
        "#92AD59"
      ]
    },
    {
      "name": "Nebula Circuit 798",
      "colors": [
        "#AAC4C8",
        "#55CB2E",
        "#5AE2C0",
        "#6A8642",
        "#CCCE0D"
      ]
    },
    {
      "name": "Echo Empire 799",
      "colors": [
        "#D057E3",
        "#A6DC14",
        "#99BDB0",
        "#3C5517",
        "#F78C9C"
      ]
    },
    {
      "name": "Nimbus Garden 800",
      "colors": [
        "#9CE3EE",
        "#782540",
        "#BA102A",
        "#41FC36",
        "#3FF517"
      ]
    },
    {
      "name": "Velvet Valley 801",
      "colors": [
        "#9169A8",
        "#25F96D",
        "#DD38C1",
        "#30B942",
        "#F598E2"
      ]
    },
    {
      "name": "Radiant Valley 802",
      "colors": [
        "#D9741A",
        "#68733F",
        "#2CB3B6",
        "#2ABF06",
        "#0CAE68"
      ]
    },
    {
      "name": "Obsidian Pulse 803",
      "colors": [
        "#DD6C5A",
        "#FBD109",
        "#FDF1D0",
        "#20BAC6",
        "#C81D40"
      ]
    },
    {
      "name": "Aurora Empire 804",
      "colors": [
        "#E560B1",
        "#737CAE",
        "#37DB39",
        "#86B719",
        "#108728"
      ]
    },
    {
      "name": "Nimbus Mirror 805",
      "colors": [
        "#4B5F61",
        "#CCB36F",
        "#1D2525",
        "#57CF1D",
        "#653105"
      ]
    },
    {
      "name": "Spectrum Wave 806",
      "colors": [
        "#9C5091",
        "#50BAB1",
        "#6C3E1C",
        "#FF47DE",
        "#8BE5B6"
      ]
    },
    {
      "name": "Saffron Wave 807",
      "colors": [
        "#01AC74",
        "#866A80",
        "#D0BB29",
        "#12CCDD",
        "#FE62B0"
      ]
    },
    {
      "name": "Saffron Fragment 808",
      "colors": [
        "#1741A7",
        "#548DE1",
        "#21A950",
        "#2EC9E5",
        "#3544AD"
      ]
    },
    {
      "name": "Nimbus Mirror 809",
      "colors": [
        "#888126",
        "#B881B8",
        "#3F392A",
        "#BB8708",
        "#604B8B"
      ]
    },
    {
      "name": "Opal Fragment 810",
      "colors": [
        "#05F3E5",
        "#A712B7",
        "#89859D",
        "#9FA096",
        "#ACE46F"
      ]
    },
    {
      "name": "Nimbus Storm 811",
      "colors": [
        "#098479",
        "#8CF64E",
        "#932498",
        "#5D3F89",
        "#DFBB17"
      ]
    },
    {
      "name": "Saffron Field 812",
      "colors": [
        "#7B0747",
        "#D88DB2",
        "#CEE1BF",
        "#81368B",
        "#BBEAAF"
      ]
    },
    {
      "name": "Lunar Glow 813",
      "colors": [
        "#5D4279",
        "#E4B340",
        "#97ED5F",
        "#2578BC",
        "#1989DB"
      ]
    },
    {
      "name": "Quantum Storm 814",
      "colors": [
        "#6AF40D",
        "#7C2AF0",
        "#1EA0D8",
        "#785F5C",
        "#4446B5"
      ]
    },
    {
      "name": "Nebula Valley 815",
      "colors": [
        "#7C2EBE",
        "#0B2BD2",
        "#38BD1D",
        "#3FC1F0",
        "#C74B23"
      ]
    },
    {
      "name": "Cerulean Dream 816",
      "colors": [
        "#D1A547",
        "#E9EBDE",
        "#FC5E2C",
        "#AF30AD",
        "#F690D4"
      ]
    },
    {
      "name": "Echo Valley 817",
      "colors": [
        "#847856",
        "#3AEA6D",
        "#8066A6",
        "#ED86BB",
        "#E6AFD4"
      ]
    },
    {
      "name": "Molten Glow 818",
      "colors": [
        "#6CBAC4",
        "#51DAFC",
        "#9E9140",
        "#39584F",
        "#2CDDF7"
      ]
    },
    {
      "name": "Echo Fragment 819",
      "colors": [
        "#F4AFBE",
        "#B65A47",
        "#F7E0FE",
        "#C5511C",
        "#3CDBE4"
      ]
    },
    {
      "name": "Radiant Field 820",
      "colors": [
        "#344EE3",
        "#D46EB3",
        "#A83AE2",
        "#52EB3E",
        "#E1511F"
      ]
    },
    {
      "name": "Spectrum Flare 821",
      "colors": [
        "#855EDC",
        "#75DE31",
        "#5294AF",
        "#8F94B3",
        "#6A794A"
      ]
    },
    {
      "name": "Nimbus Empire 822",
      "colors": [
        "#BC1DFE",
        "#6C3D14",
        "#6FAEF3",
        "#FC472F",
        "#092A97"
      ]
    },
    {
      "name": "Echo Garden 823",
      "colors": [
        "#5A5D59",
        "#CE1B20",
        "#E792A0",
        "#76F3D6",
        "#5B5DC7"
      ]
    },
    {
      "name": "Nebula Circuit 824",
      "colors": [
        "#5E0477",
        "#4E0C0B",
        "#D0ED84",
        "#3F489B",
        "#44F1AD"
      ]
    },
    {
      "name": "Crimson Nebula 825",
      "colors": [
        "#D0A5D5",
        "#36E990",
        "#44BC74",
        "#48B270",
        "#0343A7"
      ]
    },
    {
      "name": "Obsidian Horizon 826",
      "colors": [
        "#C5AEB1",
        "#054679",
        "#ECFBD5",
        "#4ADAA6",
        "#32E804"
      ]
    },
    {
      "name": "Verdant Glow 827",
      "colors": [
        "#F602AD",
        "#EA4D36",
        "#E3B06D",
        "#57854B",
        "#409038"
      ]
    },
    {
      "name": "Quantum Cavern 828",
      "colors": [
        "#417029",
        "#617103",
        "#9380FA",
        "#F11E88",
        "#9B945F"
      ]
    },
    {
      "name": "Nimbus Garden 829",
      "colors": [
        "#244A7D",
        "#101CC7",
        "#D4C03C",
        "#E361D2",
        "#75215A"
      ]
    },
    {
      "name": "Velvet Circuit 830",
      "colors": [
        "#51D5D9",
        "#A8F2E7",
        "#FEBC9A",
        "#C803A6",
        "#00647D"
      ]
    },
    {
      "name": "Nebula Dream 831",
      "colors": [
        "#F9BC9B",
        "#7C3258",
        "#FDF687",
        "#6AC65C",
        "#516D1A"
      ]
    },
    {
      "name": "Radiant Field 832",
      "colors": [
        "#347208",
        "#C1DF31",
        "#8EB484",
        "#566A66",
        "#39BA96"
      ]
    },
    {
      "name": "Spectrum Cascade 833",
      "colors": [
        "#EC36AD",
        "#37999D",
        "#2E8FDB",
        "#F9262E",
        "#EAA31F"
      ]
    },
    {
      "name": "Prismatic Empire 834",
      "colors": [
        "#1EC4B3",
        "#8D0898",
        "#E5A17A",
        "#3C4348",
        "#E437EE"
      ]
    },
    {
      "name": "Obsidian Garden 835",
      "colors": [
        "#2B1A01",
        "#6402D1",
        "#2358AB",
        "#B73EF3",
        "#2F80D0"
      ]
    },
    {
      "name": "Spectrum Glow 836",
      "colors": [
        "#5DDA6F",
        "#D1E2B1",
        "#94BD3D",
        "#675C37",
        "#39E729"
      ]
    },
    {
      "name": "Cerulean Glow 837",
      "colors": [
        "#AF3801",
        "#1AE547",
        "#F59523",
        "#13ED91",
        "#FE981C"
      ]
    },
    {
      "name": "Nimbus Cavern 838",
      "colors": [
        "#81FD99",
        "#78B801",
        "#55B487",
        "#1DE78E",
        "#63E693"
      ]
    },
    {
      "name": "Crimson Cavern 839",
      "colors": [
        "#6903CE",
        "#42279A",
        "#6F6663",
        "#00F331",
        "#C4A497"
      ]
    },
    {
      "name": "Crimson Cavern 840",
      "colors": [
        "#9B94C6",
        "#279252",
        "#857D67",
        "#3101D3",
        "#F87A58"
      ]
    },
    {
      "name": "Crimson Nebula 841",
      "colors": [
        "#A84E3A",
        "#8C0C94",
        "#AB124D",
        "#738FD8",
        "#9208D8"
      ]
    },
    {
      "name": "Obsidian Empire 842",
      "colors": [
        "#B2F927",
        "#DCB24D",
        "#00EAEF",
        "#A08A02",
        "#1B0E7D"
      ]
    },
    {
      "name": "Obsidian Garden 843",
      "colors": [
        "#C24816",
        "#9DF321",
        "#F11F78",
        "#497010",
        "#66D081"
      ]
    },
    {
      "name": "Nebula Pulse 844",
      "colors": [
        "#E1E4C2",
        "#9DB19E",
        "#6F82E0",
        "#1EA4F5",
        "#4AB413"
      ]
    },
    {
      "name": "Echo Horizon 845",
      "colors": [
        "#835957",
        "#EBE7F9",
        "#5B529E",
        "#18B2DE",
        "#168F79"
      ]
    },
    {
      "name": "Opal Garden 846",
      "colors": [
        "#9DEA1C",
        "#FC0E6A",
        "#08738E",
        "#25384B",
        "#34213C"
      ]
    },
    {
      "name": "Iridescent Flare 847",
      "colors": [
        "#7920AE",
        "#A50CE7",
        "#6B15AE",
        "#C7A75D",
        "#AD147A"
      ]
    },
    {
      "name": "Spectrum Flare 848",
      "colors": [
        "#1A63BD",
        "#3E583D",
        "#99DF43",
        "#9C6CCA",
        "#71D182"
      ]
    },
    {
      "name": "Velvet Circuit 849",
      "colors": [
        "#548E65",
        "#46936C",
        "#FC44AA",
        "#5C5C6D",
        "#4AB742"
      ]
    },
    {
      "name": "Vivid Cavern 850",
      "colors": [
        "#13937D",
        "#2230C7",
        "#3E004C",
        "#8F1CD2",
        "#ADC3B4"
      ]
    },
    {
      "name": "Opal Dream 851",
      "colors": [
        "#F8DDB6",
        "#044B3E",
        "#946E3F",
        "#F25EBB",
        "#3E904F"
      ]
    },
    {
      "name": "Verdant Wave 852",
      "colors": [
        "#7DB3D1",
        "#33ADB9",
        "#3F80E7",
        "#5D63E9",
        "#4A5CE7"
      ]
    },
    {
      "name": "Verdant Pulse 853",
      "colors": [
        "#B0DFBB",
        "#D476E6",
        "#958F51",
        "#D1D934",
        "#9FA49A"
      ]
    },
    {
      "name": "Prismatic Realm 854",
      "colors": [
        "#8C04B8",
        "#2EE91A",
        "#BC4376",
        "#D51F8D",
        "#8299FF"
      ]
    },
    {
      "name": "Prismatic Mist 855",
      "colors": [
        "#FB20A3",
        "#712982",
        "#4A83B0",
        "#005E96",
        "#FB9C97"
      ]
    },
    {
      "name": "Nimbus Valley 856",
      "colors": [
        "#52EA2B",
        "#FA64B8",
        "#9B4940",
        "#8A5ADB",
        "#592AFE"
      ]
    },
    {
      "name": "Spectrum Wave 857",
      "colors": [
        "#72D238",
        "#D654F6",
        "#DF5B1F",
        "#E725F8",
        "#79D618"
      ]
    },
    {
      "name": "Quantum Cascade 858",
      "colors": [
        "#CB616C",
        "#F3E9B2",
        "#CBC2EB",
        "#6BD995",
        "#A0F85D"
      ]
    },
    {
      "name": "Echo Flare 859",
      "colors": [
        "#C25FD3",
        "#1B997A",
        "#CA570C",
        "#300928",
        "#3ECB87"
      ]
    },
    {
      "name": "Verdant Pulse 860",
      "colors": [
        "#7529FC",
        "#E0FC79",
        "#EAF0DC",
        "#BDF9E0",
        "#D768CC"
      ]
    },
    {
      "name": "Verdant Dream 861",
      "colors": [
        "#F9AECF",
        "#C431CB",
        "#0F2EE1",
        "#61E144",
        "#F079B7"
      ]
    },
    {
      "name": "Cerulean Glow 862",
      "colors": [
        "#C9AEF9",
        "#261AD4",
        "#F1614C",
        "#169824",
        "#AE9C16"
      ]
    },
    {
      "name": "Velvet Fragment 863",
      "colors": [
        "#63D375",
        "#7A2FAD",
        "#612743",
        "#00F756",
        "#73CA27"
      ]
    },
    {
      "name": "Iridescent Cavern 864",
      "colors": [
        "#8B82BE",
        "#6BB7AE",
        "#B97633",
        "#818935",
        "#497754"
      ]
    },
    {
      "name": "Radiant Mist 865",
      "colors": [
        "#724A46",
        "#4300D0",
        "#FCE98F",
        "#1C3F36",
        "#9179CC"
      ]
    },
    {
      "name": "Lunar Field 866",
      "colors": [
        "#86382C",
        "#6A00FE",
        "#B7918D",
        "#712F94",
        "#C0D0C7"
      ]
    },
    {
      "name": "Cerulean Glow 867",
      "colors": [
        "#49E438",
        "#21BDE2",
        "#F0BA63",
        "#0B1AA8",
        "#41160A"
      ]
    },
    {
      "name": "Spectrum Glow 868",
      "colors": [
        "#531815",
        "#247D64",
        "#49DB19",
        "#320882",
        "#073118"
      ]
    },
    {
      "name": "Aurora Glow 869",
      "colors": [
        "#0709D0",
        "#0B3D3E",
        "#73CED2",
        "#19A4A5",
        "#21D569"
      ]
    },
    {
      "name": "Molten Pulse 870",
      "colors": [
        "#EB696A",
        "#AC0593",
        "#AD5B81",
        "#1E8663",
        "#44DD40"
      ]
    },
    {
      "name": "Quantum Realm 871",
      "colors": [
        "#4515FF",
        "#669D56",
        "#316674",
        "#E02345",
        "#A300D0"
      ]
    },
    {
      "name": "Opal Fragment 872",
      "colors": [
        "#843587",
        "#867A8D",
        "#1B6744",
        "#40499F",
        "#EC620A"
      ]
    },
    {
      "name": "Prismatic Flare 873",
      "colors": [
        "#E6F339",
        "#182090",
        "#F34EFF",
        "#B54C8B",
        "#9D8491"
      ]
    },
    {
      "name": "Opal Cavern 874",
      "colors": [
        "#A68D76",
        "#65BAF6",
        "#FBB24C",
        "#7C2F4C",
        "#1549D5"
      ]
    },
    {
      "name": "Obsidian Mirror 875",
      "colors": [
        "#3B2088",
        "#CBF98C",
        "#B16D9B",
        "#D2D99A",
        "#894BEB"
      ]
    },
    {
      "name": "Opal Storm 876",
      "colors": [
        "#42A6BC",
        "#5D2CEC",
        "#187F79",
        "#40D173",
        "#665E58"
      ]
    },
    {
      "name": "Iridescent Bloom 877",
      "colors": [
        "#A5EC8C",
        "#FA6364",
        "#DCAB64",
        "#D93B73",
        "#E81478"
      ]
    },
    {
      "name": "Velvet Field 878",
      "colors": [
        "#D51B91",
        "#6B669C",
        "#4653B7",
        "#6B6468",
        "#9CF1B1"
      ]
    },
    {
      "name": "Velvet Valley 879",
      "colors": [
        "#606CBC",
        "#76F449",
        "#B2CDA2",
        "#AE34C0",
        "#379517"
      ]
    },
    {
      "name": "Iridescent Dream 880",
      "colors": [
        "#59EE9C",
        "#FC7728",
        "#EFE7CD",
        "#A20F4E",
        "#EE3E75"
      ]
    },
    {
      "name": "Quantum Nebula 881",
      "colors": [
        "#A7BE70",
        "#4E5711",
        "#3160BF",
        "#A8DF7A",
        "#7D1750"
      ]
    },
    {
      "name": "Echo Horizon 882",
      "colors": [
        "#DD2AFF",
        "#5476C5",
        "#B150AC",
        "#EFEC66",
        "#C005F1"
      ]
    },
    {
      "name": "Solar Garden 883",
      "colors": [
        "#D0CA96",
        "#EF6C21",
        "#5912A5",
        "#D45285",
        "#B46CB2"
      ]
    },
    {
      "name": "Radiant Flare 884",
      "colors": [
        "#A0D1A5",
        "#D75B83",
        "#64A1D2",
        "#D14862",
        "#E656D9"
      ]
    },
    {
      "name": "Saffron Circuit 885",
      "colors": [
        "#4826F6",
        "#56C941",
        "#2DDA2A",
        "#C70ADD",
        "#514489"
      ]
    },
    {
      "name": "Opal Flare 886",
      "colors": [
        "#EF2093",
        "#045D18",
        "#BD70FB",
        "#59FCDA",
        "#37565B"
      ]
    },
    {
      "name": "Quantum Field 887",
      "colors": [
        "#A50419",
        "#CFF28D",
        "#6D9F08",
        "#59792E",
        "#E2D5E0"
      ]
    },
    {
      "name": "Iridescent Fragment 888",
      "colors": [
        "#DB8911",
        "#5759F7",
        "#178913",
        "#7FDD50",
        "#4CEE6C"
      ]
    },
    {
      "name": "Radiant Garden 889",
      "colors": [
        "#5CC28B",
        "#43B7E3",
        "#A7B2C7",
        "#94B751",
        "#FA09A2"
      ]
    },
    {
      "name": "Crimson Dream 890",
      "colors": [
        "#3EBC44",
        "#717B7E",
        "#A9EAC5",
        "#5E608C",
        "#D52FF5"
      ]
    },
    {
      "name": "Opal Cascade 891",
      "colors": [
        "#A37441",
        "#4645AD",
        "#100F3C",
        "#8B3DB9",
        "#413879"
      ]
    },
    {
      "name": "Spectrum Valley 892",
      "colors": [
        "#A7C5FD",
        "#A56BDD",
        "#D4FB0B",
        "#6B1D2B",
        "#E55487"
      ]
    },
    {
      "name": "Prismatic Wave 893",
      "colors": [
        "#ADACF7",
        "#BE2CEA",
        "#014903",
        "#311EDA",
        "#3CE5C5"
      ]
    },
    {
      "name": "Opal Horizon 894",
      "colors": [
        "#A913B4",
        "#666A7B",
        "#B96E25",
        "#D82DC1",
        "#AA15C0"
      ]
    },
    {
      "name": "Velvet Field 895",
      "colors": [
        "#D7383C",
        "#1EE94E",
        "#2D73BA",
        "#3A9E31",
        "#04BABE"
      ]
    },
    {
      "name": "Cerulean Bloom 896",
      "colors": [
        "#62B756",
        "#A3CBFE",
        "#DAC751",
        "#1C3077",
        "#2A699B"
      ]
    },
    {
      "name": "Aurora Mist 897",
      "colors": [
        "#2929FB",
        "#4E72E1",
        "#0BC526",
        "#CEA561",
        "#3A29DC"
      ]
    },
    {
      "name": "Verdant Horizon 898",
      "colors": [
        "#3D125B",
        "#A6DD2F",
        "#53FDCD",
        "#6A1C20",
        "#49A3DB"
      ]
    },
    {
      "name": "Nebula Fragment 899",
      "colors": [
        "#2AA1C2",
        "#D46385",
        "#2109DB",
        "#E1983B",
        "#C50253"
      ]
    },
    {
      "name": "Quantum Horizon 900",
      "colors": [
        "#8C07B7",
        "#1DB671",
        "#94BF45",
        "#8CEF88",
        "#26B683"
      ]
    },
    {
      "name": "Crimson Dream 901",
      "colors": [
        "#21322B",
        "#FD19D8",
        "#F3CB97",
        "#CED271",
        "#CBFD90"
      ]
    },
    {
      "name": "Prismatic Garden 902",
      "colors": [
        "#E4D797",
        "#56E03F",
        "#276286",
        "#7D93F2",
        "#0AA00A"
      ]
    },
    {
      "name": "Verdant Mirror 903",
      "colors": [
        "#D395CF",
        "#ADD614",
        "#3113AE",
        "#2809A6",
        "#76EFF1"
      ]
    },
    {
      "name": "Spectrum Fragment 904",
      "colors": [
        "#D300B6",
        "#7048D3",
        "#667F53",
        "#84196B",
        "#C1644B"
      ]
    },
    {
      "name": "Radiant Storm 905",
      "colors": [
        "#178C89",
        "#AD2579",
        "#0EF3DE",
        "#2A64E7",
        "#D914B2"
      ]
    },
    {
      "name": "Nebula Glow 906",
      "colors": [
        "#A35275",
        "#5E8396",
        "#DB6AAB",
        "#87E005",
        "#30A98A"
      ]
    },
    {
      "name": "Quantum Flare 907",
      "colors": [
        "#8266FD",
        "#3E1D38",
        "#F5BC26",
        "#B700D0",
        "#2A0CCB"
      ]
    },
    {
      "name": "Vivid Pulse 908",
      "colors": [
        "#3D4B78",
        "#5446CD",
        "#D32761",
        "#026EBC",
        "#3BA432"
      ]
    },
    {
      "name": "Iridescent Field 909",
      "colors": [
        "#7AC5BB",
        "#3927A2",
        "#7D1127",
        "#BA7327",
        "#C7833E"
      ]
    },
    {
      "name": "Verdant Mist 910",
      "colors": [
        "#87D11F",
        "#15774A",
        "#96884F",
        "#33F57E",
        "#117980"
      ]
    },
    {
      "name": "Velvet Mirror 911",
      "colors": [
        "#153C19",
        "#C9641F",
        "#9F1748",
        "#06DA04",
        "#D0D27E"
      ]
    },
    {
      "name": "Lunar Storm 912",
      "colors": [
        "#9179D1",
        "#382B6C",
        "#3343FC",
        "#D8818F",
        "#CADC81"
      ]
    },
    {
      "name": "Nebula Glow 913",
      "colors": [
        "#D8902B",
        "#115FD9",
        "#EAEC44",
        "#A82A3B",
        "#178605"
      ]
    },
    {
      "name": "Echo Storm 914",
      "colors": [
        "#9B6C7E",
        "#5CC0B4",
        "#110CA5",
        "#00CB63",
        "#F2780A"
      ]
    },
    {
      "name": "Radiant Storm 915",
      "colors": [
        "#7DE4BB",
        "#77472D",
        "#33E5D4",
        "#D622CE",
        "#376554"
      ]
    },
    {
      "name": "Spectrum Bloom 916",
      "colors": [
        "#FBBD5E",
        "#6D1547",
        "#7501AC",
        "#24A21F",
        "#224672"
      ]
    },
    {
      "name": "Quantum Empire 917",
      "colors": [
        "#00E5D7",
        "#CC1F44",
        "#9B559B",
        "#E41CA0",
        "#0B3E40"
      ]
    },
    {
      "name": "Lunar Mist 918",
      "colors": [
        "#91BD7E",
        "#2CE8BC",
        "#B3CECE",
        "#F5CABA",
        "#BD0BF1"
      ]
    },
    {
      "name": "Verdant Empire 919",
      "colors": [
        "#B7F4BB",
        "#5094EE",
        "#04B08E",
        "#1DDBB5",
        "#FE3EC8"
      ]
    },
    {
      "name": "Iridescent Wave 920",
      "colors": [
        "#9A8533",
        "#03F9F1",
        "#0718AB",
        "#C3EF19",
        "#3CF6BF"
      ]
    },
    {
      "name": "Vivid Valley 921",
      "colors": [
        "#58FA9F",
        "#4B8F0E",
        "#716815",
        "#0E5C14",
        "#5C6D12"
      ]
    },
    {
      "name": "Verdant Valley 922",
      "colors": [
        "#2A08A6",
        "#4BEF31",
        "#BF9F52",
        "#0E5D49",
        "#D1EE2C"
      ]
    },
    {
      "name": "Opal Dream 923",
      "colors": [
        "#4C8E92",
        "#349759",
        "#509BAE",
        "#8E3AFD",
        "#7AE049"
      ]
    },
    {
      "name": "Saffron Cavern 924",
      "colors": [
        "#27C4F7",
        "#355E1F",
        "#66658D",
        "#031EDC",
        "#7BDDE3"
      ]
    },
    {
      "name": "Opal Fragment 925",
      "colors": [
        "#6A3C89",
        "#0BBA44",
        "#3831FF",
        "#14B368",
        "#AFCCC2"
      ]
    },
    {
      "name": "Prismatic Pulse 926",
      "colors": [
        "#A09BD5",
        "#60B2A8",
        "#F5A45C",
        "#703947",
        "#C6B4D0"
      ]
    },
    {
      "name": "Spectrum Glow 927",
      "colors": [
        "#A763CB",
        "#EF563A",
        "#978C7B",
        "#6AA02F",
        "#3BEBFD"
      ]
    },
    {
      "name": "Vivid Realm 928",
      "colors": [
        "#ED94DD",
        "#C68FAB",
        "#1EF666",
        "#10EBCD",
        "#E8BBFC"
      ]
    },
    {
      "name": "Prismatic Mirror 929",
      "colors": [
        "#417CBE",
        "#76DA4C",
        "#45E08D",
        "#DDE0D8",
        "#91F959"
      ]
    },
    {
      "name": "Lunar Field 930",
      "colors": [
        "#0FFD0E",
        "#C11D76",
        "#7D13CB",
        "#161F1A",
        "#66214D"
      ]
    },
    {
      "name": "Nebula Cascade 931",
      "colors": [
        "#B47E90",
        "#041890",
        "#ACFFCF",
        "#EF7F5C",
        "#76FA79"
      ]
    },
    {
      "name": "Radiant Fragment 932",
      "colors": [
        "#EB8E97",
        "#427487",
        "#3CF63D",
        "#F5F0A6",
        "#1083B8"
      ]
    },
    {
      "name": "Opal Garden 933",
      "colors": [
        "#79D470",
        "#8F8CD1",
        "#FFD068",
        "#25FB12",
        "#9120A8"
      ]
    },
    {
      "name": "Quantum Fragment 934",
      "colors": [
        "#7B3170",
        "#565467",
        "#30B9A4",
        "#05862F",
        "#DC812B"
      ]
    },
    {
      "name": "Nimbus Field 935",
      "colors": [
        "#0575A7",
        "#36B04C",
        "#BD9938",
        "#809FF4",
        "#B2B4B6"
      ]
    },
    {
      "name": "Saffron Fragment 936",
      "colors": [
        "#BFE2C9",
        "#60C7A9",
        "#87C1B0",
        "#5F2E1B",
        "#13E6C8"
      ]
    },
    {
      "name": "Obsidian Nebula 937",
      "colors": [
        "#C513B2",
        "#B6D61C",
        "#540000",
        "#68B2E9",
        "#011462"
      ]
    },
    {
      "name": "Echo Circuit 938",
      "colors": [
        "#C512BF",
        "#0BF7B9",
        "#BE5481",
        "#6F3361",
        "#057F87"
      ]
    },
    {
      "name": "Echo Storm 939",
      "colors": [
        "#38ED15",
        "#D4F106",
        "#1723BC",
        "#2A6EE8",
        "#BA2B4F"
      ]
    },
    {
      "name": "Quantum Mirror 940",
      "colors": [
        "#CA80BD",
        "#E7D888",
        "#DB762B",
        "#34A27F",
        "#D94174"
      ]
    },
    {
      "name": "Saffron Empire 941",
      "colors": [
        "#3BE8A6",
        "#4AAA69",
        "#96F8D0",
        "#C0DE7D",
        "#608A87"
      ]
    },
    {
      "name": "Echo Glow 942",
      "colors": [
        "#7932F5",
        "#93B1ED",
        "#6A3BE7",
        "#D4834E",
        "#706BC0"
      ]
    },
    {
      "name": "Opal Flare 943",
      "colors": [
        "#E8B9DB",
        "#890663",
        "#5046B8",
        "#2B520E",
        "#05BC94"
      ]
    },
    {
      "name": "Saffron Storm 944",
      "colors": [
        "#7FFE5A",
        "#697B4F",
        "#C5ABFE",
        "#FCE79A",
        "#CF0602"
      ]
    },
    {
      "name": "Nebula Valley 945",
      "colors": [
        "#5873F8",
        "#549B47",
        "#A28B69",
        "#200D23",
        "#C2396C"
      ]
    },
    {
      "name": "Molten Valley 946",
      "colors": [
        "#3A29DA",
        "#8F1B27",
        "#74F562",
        "#D1BB18",
        "#499714"
      ]
    },
    {
      "name": "Prismatic Field 947",
      "colors": [
        "#0C1C62",
        "#D050D6",
        "#23960D",
        "#41B86E",
        "#A707A8"
      ]
    },
    {
      "name": "Iridescent Nebula 948",
      "colors": [
        "#D8EBC1",
        "#E02EDB",
        "#8D1EA6",
        "#D0656F",
        "#8C8871"
      ]
    },
    {
      "name": "Cerulean Circuit 949",
      "colors": [
        "#EFB86F",
        "#8F4455",
        "#DB2F28",
        "#20459C",
        "#270094"
      ]
    },
    {
      "name": "Nimbus Mist 950",
      "colors": [
        "#A0F368",
        "#62EA8E",
        "#443F4D",
        "#C1F551",
        "#D90A5E"
      ]
    },
    {
      "name": "Aurora Pulse 951",
      "colors": [
        "#0AECC7",
        "#5C0171",
        "#B76615",
        "#84346D",
        "#87BB15"
      ]
    },
    {
      "name": "Lunar Valley 952",
      "colors": [
        "#660414",
        "#84E4F9",
        "#52A0BF",
        "#C43FC2",
        "#BBC4E6"
      ]
    },
    {
      "name": "Prismatic Wave 953",
      "colors": [
        "#C6BEB7",
        "#F0F3A7",
        "#E90C7D",
        "#419D14",
        "#9BC9A8"
      ]
    },
    {
      "name": "Saffron Garden 954",
      "colors": [
        "#E41986",
        "#DA90A7",
        "#D19F17",
        "#C872F8",
        "#6BC8E1"
      ]
    },
    {
      "name": "Solar Flare 955",
      "colors": [
        "#820E0F",
        "#FD86FC",
        "#A9E721",
        "#0355B4",
        "#FBC57C"
      ]
    },
    {
      "name": "Crimson Horizon 956",
      "colors": [
        "#A0FDC2",
        "#550416",
        "#3B6392",
        "#143B25",
        "#71EBC9"
      ]
    },
    {
      "name": "Prismatic Storm 957",
      "colors": [
        "#27858A",
        "#20B3C9",
        "#4FBEEA",
        "#F2E3C5",
        "#52AE37"
      ]
    },
    {
      "name": "Molten Nebula 958",
      "colors": [
        "#BB55CF",
        "#4AE43E",
        "#0327A7",
        "#5F9AFD",
        "#2F7C30"
      ]
    },
    {
      "name": "Lunar Bloom 959",
      "colors": [
        "#599900",
        "#F45465",
        "#AF718F",
        "#2C4E5E",
        "#E9024E"
      ]
    },
    {
      "name": "Lunar Pulse 960",
      "colors": [
        "#5D98DE",
        "#733003",
        "#DA9143",
        "#EE15F6",
        "#3A9248"
      ]
    },
    {
      "name": "Nimbus Fragment 961",
      "colors": [
        "#117F25",
        "#D95ADA",
        "#BAB9EC",
        "#5DC5B4",
        "#E8DADB"
      ]
    },
    {
      "name": "Verdant Fragment 962",
      "colors": [
        "#E5636D",
        "#7A0872",
        "#0936BB",
        "#36BA27",
        "#33B304"
      ]
    },
    {
      "name": "Quantum Mirror 963",
      "colors": [
        "#0CE7A7",
        "#6AA0F5",
        "#2AECD6",
        "#E4F052",
        "#1B9D2E"
      ]
    },
    {
      "name": "Obsidian Valley 964",
      "colors": [
        "#7961D7",
        "#56CE77",
        "#B81578",
        "#F844E0",
        "#97DB7A"
      ]
    },
    {
      "name": "Vivid Flare 965",
      "colors": [
        "#F116D2",
        "#DFF736",
        "#F5B442",
        "#C77FA8",
        "#279DBB"
      ]
    },
    {
      "name": "Obsidian Wave 966",
      "colors": [
        "#25C5FB",
        "#24A003",
        "#9AEDDC",
        "#64DA91",
        "#3709FE"
      ]
    },
    {
      "name": "Solar Mirror 967",
      "colors": [
        "#0B0A47",
        "#527892",
        "#80C3E0",
        "#254746",
        "#BAFE3C"
      ]
    },
    {
      "name": "Opal Storm 968",
      "colors": [
        "#585BC3",
        "#43AF18",
        "#C72219",
        "#6BDE70",
        "#D14167"
      ]
    },
    {
      "name": "Saffron Nebula 969",
      "colors": [
        "#F53213",
        "#316686",
        "#AC0D6B",
        "#ABA480",
        "#66E9EA"
      ]
    },
    {
      "name": "Nimbus Glow 970",
      "colors": [
        "#2B0B9D",
        "#0614DD",
        "#EA104F",
        "#2074A7",
        "#ED8C55"
      ]
    },
    {
      "name": "Echo Pulse 971",
      "colors": [
        "#88BC1C",
        "#BC1657",
        "#4DAC6F",
        "#8691C1",
        "#AAA2D0"
      ]
    },
    {
      "name": "Vivid Empire 972",
      "colors": [
        "#0705CB",
        "#C06C96",
        "#2AF4DB",
        "#770895",
        "#5F4FD9"
      ]
    },
    {
      "name": "Prismatic Mist 973",
      "colors": [
        "#4FC316",
        "#131AD5",
        "#31EFE8",
        "#663638",
        "#24C637"
      ]
    },
    {
      "name": "Nebula Mirror 974",
      "colors": [
        "#941383",
        "#E707B4",
        "#526771",
        "#C3229D",
        "#706E4F"
      ]
    },
    {
      "name": "Obsidian Glow 975",
      "colors": [
        "#18EC10",
        "#057861",
        "#8C4041",
        "#0715D7",
        "#E2BA52"
      ]
    },
    {
      "name": "Quantum Cascade 976",
      "colors": [
        "#D81A99",
        "#9542C5",
        "#4A0FCA",
        "#F66311",
        "#587BC3"
      ]
    },
    {
      "name": "Echo Empire 977",
      "colors": [
        "#B8E46F",
        "#630FC1",
        "#AF4FC6",
        "#27E781",
        "#AB2F21"
      ]
    },
    {
      "name": "Radiant Mist 978",
      "colors": [
        "#D4F385",
        "#2EA8D7",
        "#34CF2E",
        "#1F209E",
        "#D054A4"
      ]
    },
    {
      "name": "Radiant Horizon 979",
      "colors": [
        "#139288",
        "#2E64D2",
        "#C06218",
        "#144775",
        "#1C1712"
      ]
    },
    {
      "name": "Solar Valley 980",
      "colors": [
        "#8AFD8E",
        "#758DA1",
        "#971620",
        "#EA23D4",
        "#1DB196"
      ]
    },
    {
      "name": "Iridescent Mirror 981",
      "colors": [
        "#586B19",
        "#71BC2D",
        "#944173",
        "#DC3038",
        "#22BE1B"
      ]
    },
    {
      "name": "Echo Valley 982",
      "colors": [
        "#781A37",
        "#DCB4BB",
        "#C239F5",
        "#D40ADA",
        "#21CA36"
      ]
    },
    {
      "name": "Crimson Cavern 983",
      "colors": [
        "#6CA299",
        "#277C36",
        "#3A70FC",
        "#4E8590",
        "#F76601"
      ]
    },
    {
      "name": "Saffron Dream 984",
      "colors": [
        "#D6CA14",
        "#C861AB",
        "#084B78",
        "#4B016D",
        "#132E5D"
      ]
    },
    {
      "name": "Obsidian Fragment 985",
      "colors": [
        "#C63055",
        "#BBB377",
        "#274EF1",
        "#4A2548",
        "#E8191F"
      ]
    },
    {
      "name": "Cerulean Realm 986",
      "colors": [
        "#0A7E80",
        "#2C1055",
        "#737DDE",
        "#837368",
        "#C54A57"
      ]
    },
    {
      "name": "Saffron Storm 987",
      "colors": [
        "#6BDEFE",
        "#B5AF78",
        "#9F279B",
        "#C2A8A7",
        "#9B22DE"
      ]
    },
    {
      "name": "Spectrum Circuit 988",
      "colors": [
        "#6E67FF",
        "#15627D",
        "#1B1C51",
        "#C46C23",
        "#5E443D"
      ]
    },
    {
      "name": "Vivid Fragment 989",
      "colors": [
        "#5D7555",
        "#3389BC",
        "#535C56",
        "#B71C4A",
        "#45A1F2"
      ]
    },
    {
      "name": "Solar Empire 990",
      "colors": [
        "#3EB74A",
        "#CF9654",
        "#FB80FF",
        "#D3CF08",
        "#BCF2D2"
      ]
    },
    {
      "name": "Crimson Valley 991",
      "colors": [
        "#71D66F",
        "#EB5227",
        "#8A4DF6",
        "#4A8B27",
        "#E58E24"
      ]
    },
    {
      "name": "Solar Bloom 992",
      "colors": [
        "#D295F1",
        "#D6924B",
        "#8FDE17",
        "#3CFB83",
        "#17BF2B"
      ]
    },
    {
      "name": "Crimson Dream 993",
      "colors": [
        "#581A8D",
        "#1B772A",
        "#65B157",
        "#849253",
        "#BEAF7E"
      ]
    },
    {
      "name": "Velvet Bloom 994",
      "colors": [
        "#CD8A04",
        "#FE5060",
        "#79AC60",
        "#A9347C",
        "#EB3C2D"
      ]
    },
    {
      "name": "Nebula Cascade 995",
      "colors": [
        "#760CF7",
        "#DFF4F1",
        "#7B297A",
        "#8A3DA8",
        "#BE777F"
      ]
    },
    {
      "name": "Quantum Circuit 996",
      "colors": [
        "#837FFA",
        "#20FC70",
        "#7D2430",
        "#F23A57",
        "#48AA68"
      ]
    },
    {
      "name": "Cerulean Cavern 997",
      "colors": [
        "#2E742C",
        "#55B158",
        "#F31766",
        "#9E28D6",
        "#08DB36"
      ]
    },
    {
      "name": "Nimbus Realm 998",
      "colors": [
        "#8CEE0B",
        "#F70FF2",
        "#7037E5",
        "#28C1E6",
        "#C7D93A"
      ]
    },
    {
      "name": "Obsidian Mirror 999",
      "colors": [
        "#8D2561",
        "#8A4784",
        "#C5103C",
        "#00E0E3",
        "#5A22FA"
      ]
    },
    {
      "name": "Solar Circuit 1000",
      "colors": [
        "#2462A2",
        "#721D7F",
        "#272C29",
        "#5738F8",
        "#85E8B7"
      ]
    }
  ]

  function mulberry32(a) {
    return function () {
      let t = (a += 0x6D2B79F5)
      t = Math.imul(t ^ (t >>> 15), t | 1)
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
  }

  function clamp360(h) {
    return ((h % 360) + 360) % 360
  }

  function hslToHex(h, s, l) {
    s /= 100; l /= 100
    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l - c / 2
    let r1 = 0, g1 = 0, b1 = 0
    if (h < 60) { r1 = c; g1 = x; b1 = 0 }
    else if (h < 120) { r1 = x; g1 = c; b1 = 0 }
    else if (h < 180) { r1 = 0; g1 = c; b1 = x }
    else if (h < 240) { r1 = 0; g1 = x; b1 = c }
    else if (h < 300) { r1 = x; g1 = 0; b1 = c }
    else { r1 = c; g1 = 0; b1 = x }
    const r = Math.round((r1 + m) * 255)
    const g = Math.round((g1 + m) * 255)
    const b = Math.round((b1 + m) * 255)
    const toHex = v => v.toString(16).padStart(2, '0').toUpperCase()
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
  }

  function randRange(rng, min, max) {
    return min + (max - min) * rng()
  }

  function pick(rng, arr) {
    return arr[Math.floor(rng() * arr.length)]
  }

  function fromOffsets(rng, baseHue, offsets, sMin, sMax, lMin, lMax) {
    return offsets.map(off => {
      const h = clamp360(baseHue + off)
      const s = randRange(rng, sMin, sMax)
      const l = randRange(rng, lMin, lMax)
      return hslToHex(h, s, l)
    })
  }

  function genPalette(rng, scheme, baseHue) {
    if (scheme === 'Analogous') return fromOffsets(rng, baseHue, [-30, -15, 0, 15, 30], 55, 80, 40, 65)
    if (scheme === 'Triadic') return fromOffsets(rng, baseHue, [0, 120, 240, 20, -20], 60, 85, 45, 65)
    if (scheme === 'Complementary') return fromOffsets(rng, baseHue, [0, 180, 30, 210, -30], 55, 80, 40, 65)
    if (scheme === 'Tetradic') return fromOffsets(rng, baseHue, [0, 90, 180, 270, 45], 55, 80, 45, 65)
    if (scheme === 'Monochrome') {
      const sVals = [15, 25, 35, 45, 55].map(v => Math.max(8, Math.min(95, v + randRange(rng, -3, 3))))
      const lVals = [20, 35, 50, 65, 80].map(v => Math.max(8, Math.min(92, v + randRange(rng, -4, 4))))
      return lVals.map((l, i) => hslToHex(baseHue, sVals[i], l))
    }
    if (scheme === 'Pastel') return fromOffsets(rng, baseHue, [-12, -6, 0, 6, 12], 25, 40, 80, 92)
    if (scheme === 'Vibrant') return fromOffsets(rng, baseHue, [-24, 0, 24, 160, 200], 85, 100, 45, 60)
    if (scheme === 'Earth') {
      const earthHue = Math.floor(randRange(rng, 10, 50))
      return fromOffsets(rng, earthHue, [-30, -10, 0, 10, 30], 25, 45, 30, 60)
    }
    if (scheme === 'Neon') return fromOffsets(rng, baseHue, [0, 72, 144, 216, 288], 95, 100, 50, 60)
    return fromOffsets(rng, baseHue, [-30, -15, 0, 15, 30], 55, 80, 40, 65)
  }

  function generatePalettes(targetCount, seed = 1337) {
    const rng = mulberry32(seed)
    const schemes = ['Analogous', 'Triadic', 'Complementary', 'Tetradic', 'Monochrome', 'Pastel', 'Vibrant', 'Earth', 'Neon']
    const out = []
    const seen = new Set()
    let idx = 0
    while (out.length < targetCount) {
      const scheme = pick(rng, schemes)
      const baseHue = Math.floor(randRange(rng, 0, 360))
      const colors = genPalette(rng, scheme, baseHue)
      const sig = colors.join(',')
      if (seen.has(sig)) continue
      seen.add(sig)
      idx += 1
      out.push({ name: `${scheme} ${String(idx).padStart(3, '0')}`, colors })
    }
    return out
  }

  const TARGET = 1000
  const needed = Math.max(0, TARGET - curated.length)
  const generated = generatePalettes(needed, 2025)
  return curated.concat(generated)
})();

const mode = ref('monochrome')
const colorCount = ref(5)
const seedText = ref('')
const activePalette = ref({ id: uid(), colors: [] })
const batch = ref([])
const openImport = ref(false)
const importKind = ref('hex')
const importText = ref('')
const openExport = ref(false)
const openSave = ref(false)
const exportFormat = ref('css')
const baseName = ref('brand')
const pngCanvas = ref(null)
const toast = ref('')
const groups = ref([])
const loadingGroups = ref(false)
const creatingGroup = ref(false)
const uploading = ref(false)
const selectedGroup = ref('')
const newGroupName = ref('')
const saveFileName = ref('brand-palette')
const openFolder = ref(false)
const groupSearch = ref('')
const groupSort = ref('name')
const pendingSelected = ref('')
const renameTarget = ref('')
const renameValue = ref('')
const renaming = ref(false)
const deleting = ref('')
const openTutorial = ref(false)
const viewMode = ref('generator')
const showGeneratorSettings = ref(false)
const editingColorIndex = ref(null)
const appHeight = ref('100vh')

// se você já tem isso vindo de fora, pode trocar aqui

function uid() { return Math.random().toString(36).slice(2, 9) }
function showToast(m) { toast.value = m; setTimeout(() => toast.value = '', 1400) }
function normalizeGroupName(raw) { return (raw || '').trim().replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').slice(0, 180) }
function randSeedFromText(t) { let s = 0; for (let i = 0; i < t.length; i++) s = (s * 31 + t.charCodeAt(i)) >>> 0; return () => (s = (s * 1664525 + 1013904223) >>> 0, (s / 0xFFFFFFFF)) }
function rng() { return seedText.value ? randSeedFromText(seedText.value) : Math.random }
function randomHsl(r) { const R = r || Math.random; const h = Math.floor(R() * 360); const s = 40 + Math.floor(R() * 50); const l = 35 + Math.floor(R() * 30); return { h, s, l } }

function hslToHex(h, s, l) { s /= 100; l /= 100; const c = (1 - Math.abs(2 * l - 1)) * s; const x = c * (1 - Math.abs((h / 60) % 2 - 1)); const m = l - c / 2; let r = 0, g = 0, b = 0; if (0 <= h && h < 60) { r = c; g = x; b = 0 } else if (60 <= h && h < 120) { r = x; g = c; b = 0 } else if (120 <= h && h < 180) { r = 0; g = c; b = x } else if (180 <= h && h < 240) { r = 0; g = x; b = c } else if (240 <= h && h < 300) { r = x; g = 0; b = c } else { r = c; g = 0; b = x } const toHex = v => ('0' + Math.round((v + m) * 255).toString(16)).slice(-2); return '#' + toHex(r) + toHex(g) + toHex(b) }
function hexToHsl(hex) { const h = toHex(hex).slice(1); const r = parseInt(h.slice(0, 2), 16) / 255; const g = parseInt(h.slice(2, 4), 16) / 255; const b = parseInt(h.slice(4, 6), 16) / 255; const max = Math.max(r, g, b), min = Math.min(r, g, b); let hue = 0, s, l = (max + min) / 2; if (max === min) { s = 0 } else { const d = max - min; s = l > 0.5 ? d / (2 - max - min) : d / (max + min); switch (max) { case r: hue = (g - b) / d + (g < b ? 6 : 0); break; case g: hue = (b - r) / d + 2; break; case b: hue = (r - g) / d + 4; break } hue *= 60 } return { h: Math.round(hue), s: Math.round(s * 100), l: Math.round(l * 100) } }
function toHex(v) { if (!v) return ''; let s = String(v).trim(); if (/^#?[0-9a-f]{3}$/i.test(s)) { s = s.replace(/^#/, '').toLowerCase(); const [r, g, b] = s; return '#' + (r + r + g + g + b + b).toUpperCase() } if (/^#?[0-g-f]{6}$/i.test(s)) { s = s.replace(/^#/, '').toUpperCase(); return '#' + s } return '' }
function bestTextOn(bg) { const w = contrastRatio(bg, '#FFFFFF'), b = contrastRatio(bg, '#000000'); return b >= w ? '#000' : '#fff' }
function contrastRatio(c1, c2) { const L1 = relLum(c1), L2 = relLum(c2); const [a, b] = L1 >= L2 ? [L1, L2] : [L2, L1]; return (a + 0.05) / (b + 0.05) }
function relLum(hex) { const { r, g, b } = hexToRgb(hex); const t = v => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4) }; const R = t(r), G = t(g), B = t(b); return 0.2126 * R + 0.7152 * G + 0.0722 * B }
function hexToRgb(hex) { const h = toHex(hex).slice(1); return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) } }
function makeColor(hsl) { const hex = hslToHex(hsl.h, hsl.s, hsl.l); return { id: uid(), hex, locked: false, ...hsl } }
function spreadAngles(n, offset = 0) { const arr = []; for (let i = 0; i < n; i++) arr.push((Math.round((360 / n) * i + offset) + 360) % 360); return arr }

function generatePalette(count, r) {
  const R = r || rng()
  if (mode.value === 'random') {
    const base = randomHsl(R)
    const angles = spreadAngles(count, Math.floor(R() * 360))
    return angles.map((ang, i) => {
      const s = Math.min(100, Math.max(35, base.s + (i % 2 ? 10 : -10)))
      const l = Math.min(85, Math.max(25, base.l + (i % 2 ? -8 : 8)))
      return makeColor({ h: ang, s, l })
    })
  }
  const base = randomHsl(R)
  if (mode.value === 'complementary') { const hs = [base.h, (base.h + 180) % 360]; return fillToCount(hs, base, count).map(h => makeColor({ h, s: base.s, l: base.l })) }
  if (mode.value === 'analogous') { const step = 20 + Math.floor(R() * 15); const hs = [base.h - 2 * step, base.h - step, base.h, base.h + step, base.h + 2 * step].map(h => ((h % 360) + 360) % 360); return takeN(hs, count).map(h => makeColor({ h, s: base.s, l: base.l })) }
  if (mode.value === 'triad') { const hs = [base.h, (base.h + 120) % 360, (base.h + 240) % 360]; return fillToCount(hs, base, count).map(h => makeColor({ h, s: base.s, l: base.l })) }
  if (mode.value === 'tetrad') { const hs = [base.h, (base.h + 90) % 360, (base.h + 180) % 360, (base.h + 270) % 360]; return fillToCount(hs, base, count).map(h => makeColor({ h, s: base.s, l: base.l })) }
  if (mode.value === 'monochrome') { const hues = Array(count).fill(base.h); return hues.map((h, i) => makeColor({ h, s: 25 + i * (60 / (count - 1)), l: 25 + i * (50 / (count - 1)) })) }
  return []
}

function fillToCount(hs, base, count) { const out = [...hs]; let i = 1; while (out.length < count) { out.push((base.h + i * 30) % 360); if (out.length < count) out.push((base.h - i * 30 + 360) % 360); i++ } return takeN(out, count) }
function takeN(arr, n) { return arr.slice(0, n) }

function randomizeActive() {
  const R = rng()
  const newColors = generatePalette(colorCount.value, R)
  const finalColors = Array.from({ length: colorCount.value }, (_, i) => {
    const oldColor = activePalette.value.colors[i]
    if (oldColor && oldColor.locked) return oldColor
    return newColors[i] || generatePalette(1, R)[0]
  })
  activePalette.value = { id: uid(), colors: finalColors }
}

function removeColorFromActive(i) { if (activePalette.value.colors.length <= 3) return; activePalette.value.colors.splice(i, 1) }
function normalizeActive(i) { const c = activePalette.value.colors[i]; const hx = toHex(c.hex) || '#000000'; const hsl = hexToHsl(hx); activePalette.value.colors[i] = { ...c, hex: hx, ...hsl } }
function applyHsl(i) { const c = activePalette.value.colors[i]; c.hex = hslToHex(c.h, c.s, c.l) }
function setActive(p) { activePalette.value = { id: uid(), colors: p.colors.map(c => { const hsl = hexToHsl(c.hex); return { id: uid(), hex: c.hex, locked: false, ...hsl } }) } }
function copy(text) { navigator.clipboard.writeText(String(text).toUpperCase()); showToast('Copied') }
function copyHexList(colorsArr) { const t = colorsArr.map(c => c.hex.toUpperCase()).join(' '); navigator.clipboard.writeText(t); showToast('HEX copied') }
function parseCoolors(url) { try { const u = new URL(url); const parts = u.pathname.split('/').filter(Boolean); const last = parts[parts.length - 1] || ''; const hexes = last.split('-').map(h => ('#' + h).slice(0, 7)); return hexes.map(toHex).filter(Boolean) } catch { return [] } }

function parseImport() {
  let palettes = []
  if (importKind.value === 'hex') {
    const lines = importText.value.split(/\n+/).map(l => l.trim()).filter(Boolean)
    if (lines.length <= 1) {
      const list = importText.value.split(/[\s,;]+/g).map(toHex).filter(Boolean)
      if (list.length >= 3) palettes.push(list)
    } else {
      for (const ln of lines) {
        const list = ln.split(/[\s,;]+/g).map(toHex).filter(Boolean)
        if (list.length >= 3) palettes.push(list)
      }
    }
  } else if (importKind.value === 'json') {
    try {
      const j = JSON.parse(importText.value)
      if (Array.isArray(j)) {
        for (const item of j) {
          if (Array.isArray(item)) {
            const list = item.map(toHex).filter(Boolean)
            if (list.length >= 3) palettes.push(list)
          } else if (typeof item === 'string') {
            const h = toHex(item)
            if (h) palettes.push([h])
          }
        }
      }
    } catch {
      //
    }
  } else if (importKind.value === 'coolors') {
    const list = parseCoolors(importText.value)
    if (list.length >= 3) palettes.push(list)
  }
  if (!palettes.length) { showToast('Nothing to import'); return [] }
  showToast(`${palettes.length} palette(s) ready`); return palettes
}

function previewImport() { parseImport() }
function applyImport() { const pals = parseImport(); if (!pals.length) return; const toAdd = pals.map(arr => ({ id: uid(), colors: arr.map(h => ({ id: uid(), hex: h })) })); batch.value = [...toAdd, ...batch.value].slice(0, 200); openImport.value = false }

const exportText = computed(() => {
  const arr = activePalette.value.colors.map(c => c.hex.toUpperCase())
  if (exportFormat.value === 'css') { const vars = arr.map((h, i) => `  --${baseName.value}-${i + 1}: ${h};`).join('\n'); return `:root{\n${vars}\n}` }
  if (exportFormat.value === 'scss') { const map = arr.map((h, i) => `  ${i + 1}: ${h}`).join(',\n'); return `$${baseName.value}-colors: (\n${map}\n);` }
  if (exportFormat.value === 'tailwind') { const obj = arr.map((h, i) => `        "${i + 1}": "${h}"`).join(',\n'); return `export default {\n  theme: {\n    extend: {\n      colors: {\n        ${baseName.value}: {\n${obj}\n        }\n      }\n    }\n  }\n}` }
  if (exportFormat.value === 'json') { return JSON.stringify({ name: baseName.value, colors: arr }, null, 2) }
  if (exportFormat.value === 'svg') {
    const w = 1200, h = 300, n = arr.length, cw = w / n
    const rects = arr.map((h, i) => `<rect x="${i * cw}" y="0" width="${cw}" height="300" fill="${h}"/>`).join('\n')
    const labels = arr.map((h, i) => `<text x="${i * cw + cw / 2}" y="170" text-anchor="middle" font-family="ui-monospace,monospace" font-size="18" fill="${bestTextOn(h) === '#000' ? '#000000' : '#FFFFFF'}">${h}</text>`).join('\n')
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">\n${rects}\n${labels}\n</svg>`
  }
  return ''
})

function copyExport() { if (exportFormat.value === 'png') return; navigator.clipboard.writeText(exportText.value).then(() => showToast('Copied')) }
function downloadExport() {
  if (exportFormat.value === 'png') {
    const link = document.createElement('a'); link.download = `${baseName.value}.png`; link.href = pngCanvas.value.toDataURL('image/png'); link.click(); return
  }
  const ext = exportFormat.value === 'css' ? 'css' : exportFormat.value === 'scss' ? 'scss' : exportFormat.value === 'tailwind' ? 'js' : exportFormat.value === 'json' ? 'json' : exportFormat.value === 'svg' ? 'svg' : 'txt'
  const blob = new Blob([exportText.value], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a'); a.href = url; a.download = `${baseName.value}.${ext}`; a.click()
  URL.revokeObjectURL(url)
}

function drawPng() {
  const cvs = pngCanvas.value; if (!cvs) return
  const w = 1400, h = 400
  cvs.width = w; cvs.height = h
  const ctx = cvs.getContext('2d')
  ctx.clearRect(0, 0, w, h)
  const arr = activePalette.value.colors
  const n = Math.max(arr.length, 1)
  const cw = w / n
  arr.forEach((c, i) => {
    ctx.fillStyle = c.hex
    ctx.fillRect(i * cw, 0, cw, h)
    ctx.fillStyle = bestTextOn(c.hex) === '#000' ? '#000' : '#fff'
    ctx.font = 'bold 28px ui-monospace, monospace'
    ctx.textAlign = 'center'
    ctx.fillText(c.hex.toUpperCase(), i * cw + cw / 2, h / 2 + 10)
  })
}

watch(openExport, async v => { if (v && exportFormat.value === 'png') { await nextTick(); drawPng() } })
watch(exportFormat, v => { if (v === 'png') nextTick(drawPng) })
watch(activePalette, () => { if (exportFormat.value === 'png') nextTick(drawPng) }, { deep: true })
watch(openSave, async v => { if (v) await refreshGroups() })
watch(openFolder, async v => { if (v) { pendingSelected.value = selectedGroup.value; await refreshGroups() } })

async function refreshGroups() { try { loadingGroups.value = true; const { data } = await axios.get('/images/groups'); groups.value = Array.isArray(data?.groups) ? data.groups : [] } finally { loadingGroups.value = false } }
async function createGroup() { const name = normalizeGroupName(newGroupName.value); if (!name) { showToast('Invalid name'); return } try { creatingGroup.value = true; await axios.post('/images/groups', { name }, { headers: { 'Content-Type': 'application/json' } }); selectedGroup.value = name; newGroupName.value = ''; await refreshGroups(); showToast('Folder created') } catch (e) { const msg = e?.response?.data?.message || e?.message || 'Error'; showToast(String(msg)) } finally { creatingGroup.value = false } }
function canvasToBlob(cvs) { return new Promise(resolve => { cvs.toBlob(b => resolve(b), 'image/png', 0.92) }) }
async function savePngToServer() {
  const w = 1400, h = 400
  const tmp = document.createElement('canvas')
  tmp.width = w; tmp.height = h
  const ctx = tmp.getContext('2d')
  const arr = activePalette.value.colors
  const cw = w / Math.max(arr.length, 1)
  arr.forEach((c, i) => {
    ctx.fillStyle = c.hex
    ctx.fillRect(i * cw, 0, cw, h)
    ctx.fillStyle = bestTextOn(c.hex) === '#000' ? '#000' : '#fff'
    ctx.font = 'bold 28px ui-monospace, monospace'
    ctx.textAlign = 'center'
    ctx.fillText(c.hex.toUpperCase(), i * cw + cw / 2, h / 2 + 10)
  })
  const blob = await canvasToBlob(tmp); if (!blob) return
  const stem = (saveFileName.value || baseName.value || 'palette').replace(/[^\w.-]+/g, '-').replace(/-{2,}/g, '-').replace(/^-|-$/g, '')
  const filename = `${stem || 'palette'}.png`
  const fd = new FormData()
  fd.append('files', new File([blob], filename, { type: 'image/png' }))
  try {
    uploading.value = true
    const group = normalizeGroupName(selectedGroup.value)
    if (group && !groups.value.some(g => g.name === group)) {
      await axios.post('/images/groups', { name: group }, { headers: { 'Content-Type': 'application/json' } })
      await refreshGroups()
      selectedGroup.value = group
    }
    const q = group ? `?group=${encodeURIComponent(group)}` : ''
    const { data } = await axios.post(`/images/upload${q}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    if (data?.ok) { showToast('Saved'); openSave.value = false }
  } finally {
    uploading.value = false
  }
}

const visibleGroups = computed(() => { let arr = [...groups.value]; if (groupSearch.value) arr = arr.filter(g => g.name.toLowerCase().includes(groupSearch.value.toLowerCase())); if (groupSort.value === 'name') arr.sort((a, b) => a.name.localeCompare(b.name)); else if (groupSort.value === 'count') arr.sort((a, b) => b.count - a.count); return arr })
function previewColors(seed) { const R = randSeedFromText(String(seed || 'root')); const arr = []; for (let i = 0; i < 5; i++) { const h = Math.floor(R() * 360); const s = 45 + Math.floor(R() * 40); const l = 35 + Math.floor(R() * 40); arr.push(hslToHex(h, s, l)) } return arr }
function startRename(name) { renameTarget.value = name; renameValue.value = name }
function cancelRename() { renameTarget.value = ''; renameValue.value = '' }
async function submitRename(oldName) { const newName = normalizeGroupName(renameValue.value); if (!newName) { showToast('Invalid name'); return } if (newName === oldName) { cancelRename(); return } try { renaming.value = true; await axios.put(`/images/groups/${encodeURIComponent(oldName)}`, { name: newName }, { headers: { 'Content-Type': 'application/json' } }); await refreshGroups(); if (selectedGroup.value === oldName) selectedGroup.value = newName; if (pendingSelected.value === oldName) pendingSelected.value = newName; showToast('Renamed') } catch (e) { const msg = e?.response?.data?.message || e?.message || 'Error'; showToast(String(msg)) } finally { renaming.value = false; cancelRename() } }
async function deleteGroup(name) { try { deleting.value = name; await axios.delete(`/images/groups/${encodeURIComponent(name)}`); await refreshGroups(); if (selectedGroup.value === name) selectedGroup.value = ''; if (pendingSelected.value === name) pendingSelected.value = ''; showToast('Deleted') } catch (e) { const msg = e?.response?.data?.message || e?.message || 'Error'; showToast(String(msg)) } finally { deleting.value = '' } }
function useFolder() { selectedGroup.value = pendingSelected.value || ''; openFolder.value = false }

function handleKeydown(event) {
  if (event.code === 'Space' && event.target.tagName !== 'INPUT' && event.target.tagName !== 'TEXTAREA') {
    event.preventDefault()
    randomizeActive()
  }
}

function anyModalOpen() {
  return openImport.value
    || openExport.value
    || openSave.value
    || openFolder.value
    || openTutorial.value
    || showGeneratorSettings.value
    || editingColorIndex.value !== null
}

function isInteractiveEl(el) {
  return !!el.closest('button, [role="button"], input, select, textarea, a, video, .btn, .chip, .action-btn, .select, .input, .range')
}

function handleTapToGenerate(e) {
  if (window.innerWidth >= 768) return
  if (viewMode.value !== 'generator') return
  if (anyModalOpen()) return
  const el = e.target
  if (isInteractiveEl(el)) return
  randomizeActive()
}

function getBtnColor(hex) {
  return bestTextOn(hex) === '#000'
    ? 'bg-white/70 text-zinc-900 border border-zinc-900/10'
    : 'bg-black/40 text-white border border-white/20'
}

const updateHeight = () => {
  appHeight.value = `${window.innerHeight}px`
}

watch(colorCount, () => {
  randomizeActive()
})

onMounted(async () => {
  const ready = PREMADE_PALETTES.map(p => ({ id: uid(), name: p.name, colors: p.colors.map(h => ({ id: uid(), hex: toHex(h) })) }))
  batch.value = ready
  randomizeActive()
  await refreshGroups()
  updateHeight()
  window.addEventListener('resize', updateHeight)
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('pointerup', handleTapToGenerate, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('resize', updateHeight)
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('pointerup', handleTapToGenerate)
})
</script>

<style scoped>
.input {
  @apply w-full rounded-xl border border-black/10 dark:border-zinc-700 bg-white/80 dark:bg-zinc-900/70 px-3 py-2 outline-none text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 text-sm;
}

.select {
  @apply rounded-xl border border-black/10 dark:border-zinc-700 bg-white/90 dark:bg-zinc-900/70 px-3 py-2 text-sm outline-none text-zinc-800 dark:text-zinc-100;
}

.range {
  @apply w-full accent-sky-500;
}

.btn-ghost {
  @apply inline-flex items-center justify-center gap-1.5 rounded-2xl px-3 py-2 text-sm border border-zinc-200/80 dark:border-zinc-700 text-zinc-800 dark:text-zinc-100 bg-white/60 dark:bg-zinc-900/70 hover:bg-zinc-100/80 dark:hover:bg-zinc-800/80 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.97] focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-400/70;
}

.btn {
  @apply inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95;
}

.btn-icon {
  @apply p-2 h-9 w-9 aspect-square;
}

.chip {
  @apply inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-full border border-zinc-200/80 dark:border-zinc-700 bg-white/80 dark:bg-zinc-900/80 hover:bg-white dark:hover:bg-zinc-800 disabled:opacity-60;
}

.code {
  @apply w-full h-64 rounded-2xl border border-black/10 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 p-3 font-mono text-xs text-zinc-800 dark:text-zinc-100;
}

.tiny {
  @apply text-[10px] text-zinc-400 w-3;
}

.tabular-nums {
  font-variant-numeric: tabular-nums;
}

.action-btn {
  @apply h-10 w-10 rounded-2xl flex items-center justify-center border text-sm transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:hover:scale-100;
}

.list-colors-move,
.list-colors-enter-active,
.list-colors-leave-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-colors-enter-from,
.list-colors-leave-to {
  opacity: 0;
  transform: scale(0.5);
}

.list-colors-leave-active {
  position: absolute;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.25s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(18px);
  opacity: 0;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}
</style>
